/**
 * useGlobalTools Hook
 *
 * React hook that provides the GlobalTools framework integration.
 * Manages tool runtimes, shared context, and message handling.
 *
 * Usage:
 * ```typescript
 * const tools = useGlobalTools();
 *
 * // In message handler setup
 * const handler = tools.createMessageHandler();
 *
 * // Check if user is authorized
 * if (tools.isAuthorized) {
 *   // Enable advanced features
 * }
 * ```
 */

import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { withOncallNotice } from '../../../config/oncallNotice';
import { deliveryKey, deliveryLedger } from '../../messageDelivery';
import { userTurnAnchorId } from '../../../types/taskAnchor';
import { useAuth } from '../../../context/AuthContext';
import { useAITransactionRecorder } from '../../../context/AITransactionContext';

import { GlobalToolRuntime, createToolRuntime } from '../runtime';
import { shouldBreakOutOfAwaitingInput } from './awaitingInputBreakOut';
import { canRuntimeCaptureSend } from './runtimeCaptureScope';
import { shouldPreserveInFlightRunOnVisibilityRestore } from './visibilityInactivityGuard';
import { decideOnDoneFallback } from './onDoneFallbackDecision';
import {
  SEND_FAILURE_RETRY_SOURCE_TAG,
  sendFailureBubbleText,
} from '../../../components/GlobalChatSystem/sendFailureRetry';
import { handleOrphanResearchDeliverableDone } from './handleOrphanResearchDeliverableDone';
import { buildMessageTokenUsage } from './buildMessageTokenUsage';
import { resolveRequestedWorkflowHint } from './requestedWorkflowHint';
import { createMessagingHelpers } from '../messaging';
import { matchToolTrigger, getAllRegisteredTools, findToolByButtonId } from '../registry';
import { BUTTON_IDS as BROWSER_USE_BUTTON_IDS } from '../tools/browserUse';
import { actionDecidedDetail, emitActionDecided } from '../tools/actionDecidedEvent';
// The jump rail's id prefix, imported rather than restated so the handler
// and the renderer can never disagree about what a jump button is called.
import { STAGED_SEND_JUMP_BUTTON_ID } from '../tools/authorizeCardHtml';
import { postSubmitAnswer } from '../tools/browserAnswerClient';
// V1 in-chat one-click connect card (PLAN-storage-folder-permissions §7.3,
// 2026-08-11): connect-card buttons are handled inline (like the browser-tile
// buttons) via launchConnectFlow — see the handler in handleButtonClick.
// offerConnectForQuery renders the orchestrator-decided connect offer
// (CONNECT_OFFER:: marker / typed signal). The 2026-08-12 direct-connect
// pre-route that also lived here was removed 2026-08-24 (master rule,
// Primacy Audit D1) — the primary decides; connect cards arrive server-side.
import {
  CONNECT_APP_BUTTON_IDS,
  connectAppOfferKey,
  launchConnectFlow,
  offerConnectForQuery,
} from '../tools/connectApp';
import {
  DESKTOP_RUN_CARD_KIND,
  DESKTOP_RUN_CARD_SOURCE_TAG,
  buildDesktopRunCardHtml,
} from '../tools/desktopRunCardHtml';
// C3b email journey: which-inbox handshake + journey routing for the chat
// connect card (the host component renders the panel's own modal).
import { requestEmailIntent } from '../../composio/emailIntentChannel';
import { isEmailJourneySlug } from '../../composio/providerJourneyMeta';
import { isAgenticWorkflowsAuthorized, extractLatestWorkflowOutput, resolveTranslationIntentWithHistory } from '../../agenticWorkflows';
// Denial copy names the permission by its catalog LABEL, not the raw key.
import { PERMISSION_CATALOG_UI } from '../../../constants/permissionCatalog';
import { composePermissionRefusal, pickRefusalLanguage } from '../../../constants/permissionRefusalCopy.generated';
// 2026-06 reliability overhaul — same per-turn language detector the
// deep-research language mirror uses, so failed-task notices can pick
// errorInfo.userMessageEs for Spanish-language turns.
import { detectInputLanguage } from '../../agenticWorkflows/languageMirror';
import { INITIAL_DR_SESSION, INITIAL_VIDEO_SCRIPT_SESSION } from '../../agenticWorkflows/types';
// BUG-DR-CLARIFYING-CARD-MISSING (2026-05-20) — re-uses the existing
// formatDeepResearchQualificationHtml helper (shared with the legacy widget
// and global-tools paths) to render the Stage-1 clarifying card on the
// task-queue path. Same chrome / Skip-button id / sourceTag as the other
// surfaces so midClarifyingFreeTextPreRoute handles the next-turn reply.
import { formatDeepResearchQualificationHtml, PREST0_PRIMARY_CTA_BG } from '../../agenticWorkflows/helpers';
import { BUTTON_IDS as GLOBAL_VIDEO_SCRIPT_BUTTON_IDS } from '../tools/videoScript';
// BUG-DR-CLARIFYING-CARD-JSX-MISSING (2026-05-20) — defensive fallback so the
// Stage-1 clarifying card always renders even if `currentStage.payload` is
// missing (Firestore arrayUnion edge case, stale frontend cache predating the
// payload contract, future regression of the HTTP serializer, etc.). The
// parser synthesises a ClarifyingQuestionsStagePayload from the always-present
// renderClarifyingCardMarkdown body shipped as `result.finalResponse`.
//
// NOTE (BUG-DR-CLARIFYING-DUP-PROSE, 2026-05-20): renderClarifyingCardMarkdown
// no longer emits the verbose findings/questions body — it emits a one-line
// acknowledgement string. parseClarifyingMarkdownToPayload will return null
// for those new bodies, so this fallback effectively only catches legacy
// in-flight chats (whose finalResponse still carries the PR #194 long body)
// and otherwise the structured `currentStage.payload` branch is the sole
// renderer. We keep the parser import wired up so the legacy compatibility
// path stays alive while in-flight task records drain.
import { parseClarifyingMarkdownToPayload } from '../../prest0nSession/parseClarifyingMarkdown';
import { getFromLocalStorage } from '../../../components/GlobalChatSystem/utils';
import { GLOBAL_CHAT_HISTORY_KEY, type SerializedAttachment } from '../../../components/GlobalChatSystem/types';
import { toSerializedAttachment, type RawAttachmentRecord } from '../../../components/GlobalChatSystem/attachmentRecords';
import { restoreWorkflowCtaButton } from '../../../components/GlobalChatSystem/workflowCtaLifecycle';
import {
  createStreamReveal,
  prefersReducedMotion,
  type StreamRevealHandle,
} from '../../../components/GlobalChatSystem/streamReveal';
import { logOrchestratorTurnError } from '../orchestratorDecisionLog';
import {
  ORCHESTRATOR_AUTHORITATIVE_ROUTING,
  buildRoutingTriggerHint,
  buildPendingQuestionHint,
  buildTranslateIntentHint,
} from '../featureFlags';
import { markdownToHtml } from '../../../utils/markdownProcessor';
import {
  shouldUsePrest0nRuntimeForUser,
  resolveAccountIdFromAuth,
  fernandoTrace,
  isFernandoStrictMode,
  FERN_FE_CODES,
} from '../../prest0nRuntime';
import type { SessionInfo, TurnStreamCallbacks, TaskErrorInfo } from '../../prest0nSession/types';
import {
  buildServerBackedProgressLabel,
  SERVER_BACKED_TURN_PATIENCE_MS,
} from '../../prest0nSession/progress';
// INLINE-BASE64 SEND GUARD (2026-06 reliability overhaul) — threshold +
// size accounting live next to toCloudFunctionAttachments in client.ts so
// the guard mirrors the exact stripping rule the request builder applies.
import {
  cancelPrest0nTask,
  MAX_INLINE_DATAURL_TURN_CHARS,
  judgeAttachmentDelivery,
  mintClientTaskId,
  resumeOrCreatePrest0nSession,
  sumInlineDataUrlChars,
} from '../../prest0nSession/client';
import { registerLiveSend, clearLiveSend } from '../../../components/GlobalChatSystem/liveSendRegistry';
// 2026-06 reliability overhaul — reuse the authed-resume Interrupted card
// markup (calm notice + data-regenerate-prompt hook) for LIVE failed-task
// turns too, so every failure surface shares the same Regenerate affordance.
import { buildAuthedInterruptedHtml, taskMintTimestampMs } from '../../../components/GlobalChatSystem/serverTaskResume';
import { deriveTaskAnchorId } from '../../../types/taskAnchor';
import { disconnectAccount } from '../../composio/ComposioClient';
import { getPendingWidgetHtml, getStoppedWidgetHtml } from '../widgetHtml';
import { branchMergeLine, getPendingWidgetWithBranchesHtml, orderBranchEntries, type BranchLaneEntry } from '../branchLane';
import {
  getWorkflowOutput,
  toTranslatableContent,
  getDocumentAnalysis,
  getLatestDocumentAnalysis,
  type ButtonClickContext,
} from '../workflowOutputStore';
import { hasDocumentCache, readDocuments, readDocumentsInWaves, hasAnalyzableFiles } from '../documentReader';
import { finalizeAttachmentReadComplete } from '../messaging';
// Round O (2026-07-15) false-"stopped" root fix: per-thread active-stream
// registry + thread-scoped runtime-cancel predicate (see module docblock).
import {
  ActiveStreamRegistry,
  shouldCancelRuntimeForThread,
  type ActiveStreamEntry,
} from './activeStreamRegistry';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, getDoc, onSnapshot } from 'firebase/firestore';
import { useRbac } from '../../../hooks/useRbac';
import { buildPrest0nTurnDispatchOptions, shouldUseServerOwnedAttachmentTurn } from './attachmentTurnRouting';
import { maskStreamingFileDump } from './streamingFileDumpMask';
import { resolveHireRequiredWorkspace, runOrgPitchTurn, ORG_PITCH_SOURCE_TAG } from '../../orgPitch/client';

import type {
  GlobalToolId,
  SharedWorkflowContext,
  WorkflowHelpersInterface,
  ChatMessage,
  TokenUsageMetadata,
  MessagingHelpers,
} from '../types';
import type {
  DeepResearchSession,
  ResearchReport,
  TranslatableContent,
  VideoScriptSession,
} from '../../agenticWorkflows/types';
import type { OnUserMessageHandler } from '../../../components/GlobalChatSystem/types';

/**
 * Backoff for a failed turn START (R8, 2026-08-04). A fresh account's first
 * turn can lose the session-creation race (404) or hit a transient 5xx; both
 * heal in seconds, and the same clientTaskId makes the resend idempotent
 * server-side. Module scope since 2026-09-02 so the give-up log can name the
 * attempt count it actually gave up after.
 */
const SEND_START_RETRY_DELAYS_MS = [1500, 3000];

// ============================================================================
// Timeout Configuration
// ============================================================================

/**
 * Fingerprint emitted on module load so the live bundle can be verified
 * post-deploy (e.g. `curl <main.js> | grep webchat-streaming-keepalive-v20260514`
 * or the DevTools console message below).
 *
 * Bumped 2026-05-14 alongside the watchdog raise + SSE heartbeat fix for
 * task_mp5nu0ml_4uo9xmn (322s Deep Research turn silently dropped in webchat).
 */
export const WEBCHAT_STREAMING_KEEPALIVE_FINGERPRINT = 'webchat-streaming-keepalive-v20260514';
// Side-effect on module init so the fingerprint is reliably console-detectable
// in the live bundle (also keeps the string from being tree-shaken if the
// export is unused).
// eslint-disable-next-line no-console
console.log(`[GlobalTools] fingerprint=${WEBCHAT_STREAMING_KEEPALIVE_FINGERPRINT}`);

/**
 * Hard ceiling for the webchat streaming/SSE watchdog.
 *
 * Bumped 2026-05-14 from 75s → 8 min so long Deep Research turns
 * (e.g. less-specific Spanish prompts, which can take 2-5 min through the
 * multi-stage Perplexity → Claude → finalize pipeline) can complete and
 * stream back to the UI without the panel silently dropping the "thinking..."
 * indicator. Backed by:
 *   - SSE keepalive heartbeats (every 15s) from the VM web-adapter and the
 *     prest0nRunTurn Cloud Function so the connection isn't reaped by an
 *     intermediate proxy.
 *   - Cloud Function `prest0nRunTurn` timeoutSeconds raised to 540 (CF Gen-2
 *     ceiling) so the function doesn't die at 5 min while the agent is still
 *     producing the final answer.
 *   - Two progress hints (45s / 180s) so the user knows the chat is still
 *     working during long turns instead of staring at a static spinner.
 *     Since 2026-08-08 these update the ONE active pending widget's label in
 *     place — they used to render as extra stacked spinner bubbles with
 *     hard-coded Deep-Research/Spanish copy that was wrong for most turns.
 *   - At the 8-min ceiling we render a recoverable "took longer than expected"
 *     message instead of going silent.
 */
const ORCHESTRATOR_THINKING_TIMEOUT_MS = SERVER_BACKED_TURN_PATIENCE_MS;

/**
 * Liveness window for the ceiling above (2026-08-13).
 *
 * The 8-min ceiling used to be a WALL CLOCK: it fired — posted "took longer
 * than expected" and ABORTED the turn — regardless of what was happening
 * underneath. A 110-page translation streaming section-by-section was killed
 * at 8:00 mid-run; the message said "your work is preserved", and for a
 * client-side translation that was false — the run was dead.
 *
 * The ceiling now polices INACTIVITY instead (same principle as the
 * 2026-06-11 transport directive: no wall-clock cap on live work). When it
 * fires, the turn is only killed if nothing has painted for this long;
 * otherwise the ceiling re-arms. Every progress path — chunks, section
 * progress, stage labels — feeds lastStreamUpdateRef, so a stream that is
 * visibly moving can never be shot by its own reassurance timer, while a
 * genuinely dead one is still cleaned up within window + grace.
 */
export const ORCHESTRATOR_ACTIVITY_GRACE_MS = 2 * 60 * 1000;

/** First "still working" hint timestamp. */
const PROGRESS_HINT_FIRST_MS = 45 * 1000;
/** Second, stronger "still working" hint timestamp. */
const PROGRESS_HINT_SECOND_MS = 180 * 1000;
/** Workflow-agnostic, language-agnostic labels for the two hints above. */
const PROGRESS_HINT_FIRST_LABEL = 'still working — multi-step tasks can take a few minutes';
const PROGRESS_HINT_SECOND_LABEL = 'still working — complex requests can take several minutes';

// Routine Threads routing hint (2026-07-31; thread-mirror condition
// 2026-08-01): the intent regex + the per-turn decision now live in the pure
// leaf module `requestedWorkflowHint.ts` so the routing contract is directly
// testable. See that module's doc for the three priority rungs.

// ============================================================================
// Hook Return Type
// ============================================================================

export interface UseGlobalToolsReturn {
  /** Whether the current user is authorized to use global tools */
  isAuthorized: boolean;
  /** User's email address */
  userEmail?: string;

  // Shared Context State
  /** Last completed research report */
  lastResearchReport: ResearchReport | null;
  /** Set the last research report */
  setLastResearchReport: (report: ResearchReport | null) => void;
  /** Ref to latest research report (for closures) */
  lastResearchReportRef: React.MutableRefObject<ResearchReport | null>;

  /** Content available for translation/downstream workflows */
  lastTranslatableContent: TranslatableContent | null;
  /** Set translatable content */
  setLastTranslatableContent: (content: TranslatableContent | null) => void;
  /** Ref to latest translatable content (for closures) */
  lastTranslatableContentRef: React.MutableRefObject<TranslatableContent | null>;

  /** Deep research session state */
  drSession: DeepResearchSession;
  /** Set deep research session */
  setDrSession: (session: DeepResearchSession) => void;
  /** Ref to deep research session */
  drSessionRef: React.MutableRefObject<DeepResearchSession>;
  /** Reset deep research session */
  resetDrSession: () => void;

  /** Video script session state */
  videoScriptSession: VideoScriptSession;
  /** Set video script session */
  setVideoScriptSession: (session: VideoScriptSession) => void;
  /** Ref to video script session */
  videoScriptSessionRef: React.MutableRefObject<VideoScriptSession>;
  /** Reset video script session */
  resetVideoScriptSession: () => void;

  // Runtime Management
  /** Create a message handler for the chat system */
  createMessageHandler: () => OnUserMessageHandler;
  /** Handle button click events from chat messages, with optional context for workflow chaining */
  handleButtonClick: (buttonId: string, context?: ButtonClickContext) => Promise<boolean>;
  /** Get the currently running tool ID, if any */
  getRunningTool: () => GlobalToolId | null;
  /** Check if a specific tool is running */
  isToolRunning: (toolId: GlobalToolId) => boolean;
  /** Cancel the currently running tool */
  cancelRunningTool: (threadId?: string | null, reason?: 'user_stop' | 'thread_archived') => void;
  /** Set the active stream for cancellation (converts pending widget to "stopped" on cancel) */
  setActiveStream: (stream: {
    finalize: (content: { text: string; html: string; sourceTag: string }) => void;
  } | null) => void;
  /** Check if any tool is awaiting user input (allows send button when true) */
  isAnyToolAwaitingInput: () => boolean;
  /** Bridge legacy agentic workflow content to GlobalTools state */
  bridgeLegacyContent: (content: TranslatableContent) => void;
  /** Hydrate shared context from persisted chat history */
  hydrateFromHistory: (storageKey?: string) => void;

  // Chat Helpers Ref (for button delegation)
  /** Reference to chat helpers for event delegation */
  chatHelpersRef: React.MutableRefObject<WorkflowHelpersInterface | null>;

  // Page Context Ref (for orchestrator page-awareness)
  /** Reference to current page context — updated by GlobalChatSystem on each route change */
  pageContextRef: React.MutableRefObject<import('../../../config/pageContextRegistry').OrchestratorPageContext | undefined>;
  /** Update the current page context (called from GlobalChatSystem when route/config changes) */
  setPageContext: (ctx: import('../../../config/pageContextRegistry').OrchestratorPageContext | undefined) => void;

  // Transaction Recording
  /** Record an AI transaction */
  recordTransaction: (params: {
    tokenUsage: import('../../../components/GlobalChatSystem/types').TokenUsageMetadata;
    source: string;
    modelOverride?: string;
    summary?: string;
  }) => void;

  // Context Message Appending (for document analysis persistence)
  /** Set the callback for appending context-only messages to chat history */
  setAppendContextMessage: (callback: (message: ChatMessage) => void) => void;

  // Processing-state heartbeat (2026-05-07 fix for chain-handoff spinner gap)
  /**
   * Wire in refresh/release callbacks from the host useGlobalChat.
   *
   * Thread-isolation (2026-05-09 fix for live demo regression): the
   * callbacks accept an optional `threadId` so the heartbeat can target
   * the originating thread directly, even when a SECOND concurrent send
   * on a DIFFERENT thread has overwritten `currentSendThreadIdRef`. The
   * awaiting-input runtime listener and the orchestrator chain executor
   * pass the originating thread id explicitly via the
   * `currentTurnThreadIdRef` capture from createMessageHandler.
   */
  setProcessingHeartbeat: (
    refresh: ((threadId?: string | null) => void) | null,
    release: ((threadId?: string | null) => void) | null,
  ) => void;

  /**
   * Wire in the host <GlobalChatSystem> config.instanceId so the
   * cross-thread search tool can scope its localStorage reads to the
   * right chat surface (PR fix/thread-isolation-regression-and-cross-thread-retrieval).
   */
  setChatInstanceId: (instanceId: string | null) => void;

  /**
   * Routine Threads routing hint (2026-08-01 — FIXED backend contract): wire
   * in the host's threadId → routineId lookup (GlobalChatSystem, which owns
   * the thread store, keeps it current as the index mirror streams). The
   * dispatch site consults it with the ORIGINATING thread id so every turn
   * sent from a routine thread carries requestedWorkflow:'routines'
   * regardless of message text. Optional so legacy test doubles need not
   * provide it; unwired → the text-based rungs alone decide (pre-contract
   * behavior).
   */
  setThreadRoutineLookup?: (lookup: ((threadId: string) => string | null) | null) => void;

  /**
   * Wire the host's MOUNT-TIME messaging primitives (useGlobalChat's
   * addSystemMessage + updateStreamingMessageById) so the outward-decision
   * click branches (authorize-action-*, batch-*) can post receipts and
   * re-render the batch tile before any turn has run this page session —
   * the post-refresh path, where chatHelpersRef is still null (2026-08-27
   * batch-tile refresh self-healing). Optional so legacy test doubles need
   * not provide it; unwired → those branches fall back to turn-time
   * helpers only (pre-2026-08-27 behavior).
   */
  setBaseOutwardMessaging?: (m: {
    addSystemMessage: (input: unknown) => unknown;
    updateMessageById: ((id: string, update: Record<string, unknown>) => void) | null;
  } | null) => void;

  /**
   * Point 6 (Option B) — wire the server-side durable GUEST deep-research
   * config (enabled flag + persistServerTaskId callback). Called by
   * useGlobalChat once the migration gate resolves; `enabled` is true only on
   * the guest surface (no migrated prest0n session).
   */
  setGuestServerResearch: (value: SharedWorkflowContext['guestServerResearch']) => void;

  // Prest0n Session (for migrated accounts thin-client path)
  /** Wire in the current Prest0n session from usePrest0nSession (called by useGlobalChat) */
  setPrest0nSession: (
    session: import('../../prest0nSession/types').SessionInfo | null,
    sendTurn: (
      message: string,
      callbacks: import('../../prest0nSession/types').TurnStreamCallbacks,
      options?: {
        attachments?: import('../../../components/GlobalChatSystem/types').SerializedAttachment[];
        pageContext?: import('../../../config/pageContextRegistry').OrchestratorPageContext;
        signal?: AbortSignal;
        documentContent?: string;
        requestedWorkflow?: 'deep-research' | 'routines';
        /** Pre-flight client-minted task id — see TurnRequest.clientTaskId. */
        clientTaskId?: string;
        /** Reply to a specific message (2026-09-15) — see TurnRequest.replyTo. */
        replyTo?: import('../../../types/messageReply').MessageReplyRef | null;
      }
    ) => Promise<boolean>,
    consumeTurnRefusal?: () => { message: string } | null
  ) => void;

  // Shared Context (for direct access in useGlobalChat)
  /** Serverless-first (2026-08-15): lets a turn ask useGlobalChat to re-run its
      migration gate when the transport is unwired (stale mount-time gate). */
  setRequestSessionRewire: (fn: (() => void) | null) => void;
  /** Shared workflow context for updating message history before orchestrator call */
  sharedContext: SharedWorkflowContext;
}

// ============================================================================
// Tool to Permission Mapping
// ============================================================================

const TOOL_PERMISSION_MAPPING: Record<string, string> = {
  'deep-research': 'tools.deep-research.use',
  'translate': 'tools.translation.use',
  // Maps to the current catalog key. The retired 'tools.legal-doc.create'
  // (catalog v1) is normalized server-side to 'tools.document-drafting.use'
  // before the client ever sees a member's permission set, so gating on the
  // old key failed for every non-owner and only the owner bypass masked it.
  'legal-doc-creator': 'tools.document-drafting.use',
  'video-script': 'tools.video-script.use',
  'browser-use': 'tools.browser.use',
  'voice-call': 'tools.voice.call',
  // Catalog v9 (2026-09-02): the media tools share one member-level key.
  'generate_image': 'tools.media-generation.use',
  'generate_video': 'tools.media-generation.use',
  'generate_audio': 'tools.media-generation.use',
  'generate_sound': 'tools.media-generation.use',
  'convert_media': 'tools.media-generation.use',
  // Catalog v11 (2026-09-08): parallel branches — a runtime-internal tool; the
  // row keeps the client's mapping in lockstep with GATED_TOOL_CAPABILITIES.
  'open_branches': 'tools.parallel-branches.use',
};

/**
 * Human label for a permission key (catalog-driven), for denial messages.
 * Lowercased to match the brand voice; falls back to the raw key for anything
 * the catalog doesn't know.
 */
function permissionDisplayName(key: string): string {
  for (const category of PERMISSION_CATALOG_UI) {
    const match = category.permissions.find((p) => p.key === key);
    if (match) return match.label.toLowerCase();
  }
  return key;
}

// ============================================================================
// Hook Implementation
// ============================================================================

export function useGlobalTools(): UseGlobalToolsReturn {
  const { user, isAuthenticated } = useAuth();
  const { hasPermission } = useRbac();
  const { recordTransaction } = useAITransactionRecorder();

  // ========================================================================
  // Authorization
  // ========================================================================

  const isAuthorized = isAuthenticated && isAgenticWorkflowsAuthorized(user?.email);

  // ========================================================================
  // Tenant Config (Multi-Tenant)
  // ========================================================================

  // (D13-6a: the legacy orchestrator's tenant-config machinery
  // (tenantConfigRef + mount-time getTenantConfigFromFirestore load) was
  // removed with its only consumer, the severed branch below.)

  // ========================================================================
  // Shared Context State
  // ========================================================================

  // Research report state
  const [lastResearchReport, setLastResearchReport] = useState<ResearchReport | null>(null);
  const lastResearchReportRef = useRef<ResearchReport | null>(null);

  // Translatable content state
  const [lastTranslatableContent, setLastTranslatableContent] = useState<TranslatableContent | null>(null);
  const lastTranslatableContentRef = useRef<TranslatableContent | null>(null);

  // Deep research session state
  const [drSession, setDrSession] = useState<DeepResearchSession>(INITIAL_DR_SESSION);
  const drSessionRef = useRef<DeepResearchSession>(drSession);

  // Video script session state
  const [videoScriptSession, setVideoScriptSession] = useState<VideoScriptSession>(INITIAL_VIDEO_SCRIPT_SESSION);
  const videoScriptSessionRef = useRef<VideoScriptSession>(videoScriptSession);

  // Message history state (loaded by orchestrator)
  const [messageHistory, setMessageHistoryState] = useState<ChatMessage[]>([]);
  const messageHistoryRef = useRef<ChatMessage[]>([]);
  // Keep the ref synchronous with callers that seed context immediately
  // before invoking the orchestrator. React state alone lags one render,
  // so createMessageHandler / executor paths should be able to read the
  // newly seeded originating-thread history in the same tick.
  const setMessageHistory = useCallback((history: ChatMessage[]) => {
    messageHistoryRef.current = history;
    setMessageHistoryState(history);
  }, []);

  // Document content state (from orchestrator's analysis)
  const [documentContent, setDocumentContent] = useState<string | null>(null);
  const documentContentRef = useRef<string | null>(null);
  // RAW untruncated source text for whole-document tools (2026-08-11). See
  // GlobalToolsSharedContext.documentFullText for why this is separate from
  // documentContent (which holds the capped analyzer summary).
  const [documentFullText, setDocumentFullText] = useState<string | null>(null);
  const documentFullTextRef = useRef<string | null>(null);

  // Document analysis metadata (from unified document reader)
  const [documentAnalysisMeta, setDocumentAnalysisMeta] = useState<{
    filesAnalyzed: number;
    fileNames: string[];
    analyzedAt: Date;
    source: 'pre-orchestrator' | 'orchestrator' | 'direct-tool' | 'button-click' | 'workflow';
    fingerprint: string;
  } | undefined>(undefined);
  const lastDocumentAnalyzerFilesRef = useRef<File[] | null>(null);

  // Multi-document manifest state (FH-CLASSIFIER-002 — persists across turns
  // so downstream tools can reference docs by index without re-uploading).
  const [documentManifest, setDocumentManifest] = useState<
    import('../types').DocumentManifest | null
  >(null);
  const documentManifestRef = useRef<import('../types').DocumentManifest | null>(null);

  // Pending token usages state (for background API calls)
  const [pendingTokenUsages, setPendingTokenUsages] = useState<TokenUsageMetadata[]>([]);
  const pendingTokenUsagesRef = useRef<TokenUsageMetadata[]>([]);

  // Content history state (preserves content versions)
  const [contentHistory, setContentHistory] = useState<TranslatableContent[]>([]);

  // Chat helpers ref (for button delegation)
  const chatHelpersRef = useRef<WorkflowHelpersInterface | null>(null);

  // Context message appender ref (for document analysis persistence)
  // Set by useGlobalChat to allow document reader to append hidden context messages
  const appendContextMessageRef = useRef<((message: ChatMessage) => void) | null>(null);

  // Setter for appendContextMessage (called by useGlobalChat)
  const setAppendContextMessage = useCallback((callback: (message: ChatMessage) => void) => {
    appendContextMessageRef.current = callback;
  }, []);

  // Processing-state heartbeat refs (2026-05-07 fix for "nothing is spinning"
  // gap between chain steps and inside legal-doc-creator intake). Wired by
  // useGlobalChat via setProcessingHeartbeat() below, consumed by the chain
  // executor (via sharedContext.refreshProcessingHeartbeat) and by individual
  // long-running tools that want to keep the host's processing flag fresh.
  //
  // Thread-isolation (2026-05-09 fix): the callbacks accept an optional
  // threadId. The current turn's originating thread id is captured into
  // `currentTurnThreadIdRef` at the top of every `createMessageHandler`
  // invocation so async listeners (awaiting-input, post-chunk callbacks)
  // can resolve it without depending on the most-recent-send ref —
  // which gets clobbered the instant a SECOND concurrent send lands on
  // a different thread.
  const refreshProcessingHeartbeatRef = useRef<((threadId?: string | null) => void) | null>(null);
  const releaseProcessingHeartbeatRef = useRef<((threadId?: string | null) => void) | null>(null);
  /**
   * Originating thread id for the CURRENT in-flight orchestrator/handler
   * turn. Set at the top of `createMessageHandler`, restored in finally.
   * Read by the `releaseProcessingHeartbeat` shim in `sharedContext` so
   * the right thread's processing flag clears even if a concurrent send
   * on a different thread is racing to overwrite global refs.
   *
   * The literal name `currentTurnThreadIdRef` is the live-bundle
   * verification grep marker for this fix (matches PR convention).
   */
  const currentTurnThreadIdRef = useRef<string | null>(null);

  /**
   * Per-runtime originating thread id, keyed by toolId. Populated when
   * a runtime begins execution from `createMessageHandler`; consumed by
   * the runtime's `awaiting-input` event listener so the correct
   * thread's processing flag clears even when ANOTHER concurrent send
   * on a DIFFERENT thread has overwritten `currentTurnThreadIdRef`.
   *
   * Each tool has a singleton runtime instance (see `getOrCreateRuntime`)
   * so per-toolId keying is sufficient: two different tools running
   * concurrently on two different threads each get their own entry.
   *
   * Cleared on the runtime's `complete` and `cancel` events to avoid
   * leaking stale thread ids between turns.
   *
   * The literal name `runtimeThreadIdByToolRef` is a live-bundle grep
   * marker for this fix.
   */
  const runtimeThreadIdByToolRef = useRef<Map<GlobalToolId, string | null>>(new Map());

  /**
   * Chat instance id for the host <GlobalChatSystem>. Wired by
   * useGlobalChat via setChatInstanceId() so the cross-thread search
   * tool (PR fix/thread-isolation-regression-and-cross-thread-retrieval)
   * can scope its localStorage reads to the right chat surface. The
   * literal name `chatInstanceIdRef` is a live-bundle grep marker.
   */
  const chatInstanceIdRef = useRef<string | null>(null);

  // MT-QA-BUG-009 (2026-05-08) — pending thinking-indicator remover.
  // Populated by createMessageHandler() for each orchestrator turn; consumed
  // by every runtime's 'awaiting-input' event listener (attached in
  // getOrCreateRuntime below). When any tool yields the turn back to the user
  // — Legal Document Creator intake cards, Deep Research clarifying questions,
  // etc. — the orchestrator spinner must clear so the user isn't misled into
  // thinking the assistant is still working. The Option D persistent-thinking
  // lifecycle (2026-04-27) intentionally left the spinner visible across
  // routing/tool-handoff/first-chunk, but did NOT cover the case where a tool
  // posts an intake card via showSpecialMessage and immediately returns
  // status: 'awaiting-input' without ever streaming. This ref closes that gap.
  const pendingThinkingIndicatorRemoveRef = useRef<(() => void) | null>(null);

  // Ingest-on-upload progress (2026-06 reliability overhaul): live Firestore
  // onSnapshot unsubscribers for in-flight ingest jobs, keyed per turn inside
  // createMessageHandler. Tracked here ONLY so component unmount can tear down
  // any subscription whose turn never reached a terminal callback (the normal
  // teardown happens in the turn's own onDone/onError/abort paths).
  const ingestProgressUnsubsRef = useRef<Set<() => void>>(new Set());
  useEffect(() => {
    const unsubs = ingestProgressUnsubsRef.current;
    return () => {
      unsubs.forEach((unsub) => {
        try { unsub(); } catch { /* listener already torn down */ }
      });
      unsubs.clear();
    };
  }, []);

  const setProcessingHeartbeat = useCallback((
    refresh: ((threadId?: string | null) => void) | null,
    release: ((threadId?: string | null) => void) | null,
  ) => {
    refreshProcessingHeartbeatRef.current = refresh;
    releaseProcessingHeartbeatRef.current = release;
  }, []);

  /**
   * Wire in the host <GlobalChatSystem> config.instanceId so the
   * cross-thread search tool can scope its localStorage reads. Called
   * by useGlobalChat during the same effect that wires the heartbeat.
   */
  const setChatInstanceId = useCallback((instanceId: string | null) => {
    chatInstanceIdRef.current = instanceId;
  }, []);

  /**
   * Routine Threads routing hint (2026-08-01): host-provided threadId →
   * routineId lookup, kept in a ref (same pattern as chatInstanceIdRef) so
   * the long-lived dispatch closures always read the current thread store
   * without re-creating the handler. Wired by GlobalChatSystem whenever its
   * thread list changes; null when no thread store exists (legacy surfaces).
   */
  const threadRoutineLookupRef = useRef<((threadId: string) => string | null) | null>(null);
  const setThreadRoutineLookup = useCallback(
    (lookup: ((threadId: string) => string | null) | null) => {
      threadRoutineLookupRef.current = lookup;
    },
    [],
  );

  /**
   * Mount-time messaging for the outward-decision click branches — the
   * fallback that makes a post-refresh "send it" answer before any turn
   * has populated chatHelpersRef (2026-08-27 batch-tile refresh work).
   * Wired by useGlobalChat at mount, cleared on unmount.
   */
  const baseOutwardMessagingRef = useRef<{
    addSystemMessage: (input: unknown) => unknown;
    updateMessageById: ((id: string, update: Record<string, unknown>) => void) | null;
  } | null>(null);
  const setBaseOutwardMessaging = useCallback(
    (m: {
      addSystemMessage: (input: unknown) => unknown;
      updateMessageById: ((id: string, update: Record<string, unknown>) => void) | null;
    } | null) => {
      baseOutwardMessagingRef.current = m;
    },
    [],
  );

  /**
   * Point 6 (Option B) — server-side durable GUEST deep-research wiring.
   * Wired by useGlobalChat: `enabled` is true ONLY on the guest surface (no
   * migrated prest0n session); `persistServerTaskId` stamps the server taskId
   * onto the in-flight assistant message so it survives a hard refresh.
   */
  const guestServerResearchRef = useRef<SharedWorkflowContext['guestServerResearch']>(undefined);
  const setGuestServerResearch = useCallback(
    (value: SharedWorkflowContext['guestServerResearch']) => {
      guestServerResearchRef.current = value;
    },
    [],
  );

  // Page context ref (for orchestrator page-awareness — set by GlobalChatSystem)
  const pageContextRef = useRef<import('../../../config/pageContextRegistry').OrchestratorPageContext | undefined>(undefined);
  const setPageContext = useCallback((ctx: import('../../../config/pageContextRegistry').OrchestratorPageContext | undefined) => {
    pageContextRef.current = ctx;
  }, []);

  // Prest0n session ref — set by useGlobalChat after calling usePrest0nSession.
  // Holds the current session info and sendTurn function for migrated accounts.
  // Using a ref (not state) so createMessageHandler closure always gets latest values
  // without needing to be recreated on every session change.
  const prest0nSessionRef = useRef<{
    session: SessionInfo | null;
    sendTurn: (
      message: string,
      callbacks: TurnStreamCallbacks,
      options?: {
        attachments?: import('../../../components/GlobalChatSystem/types').SerializedAttachment[];
        pageContext?: import('../../../config/pageContextRegistry').OrchestratorPageContext;
        signal?: AbortSignal;
        documentContent?: string;
        responseMode?: 'stream' | 'async' | 'auto';
        threadId?: string;
        /** Ingest-on-upload job id — see TurnRequest.ingestJobId. */
        ingestJobId?: string;
        requestedWorkflow?: 'deep-research' | 'routines';
        /** Pre-flight client-minted task id — see TurnRequest.clientTaskId. */
        clientTaskId?: string;
        /** Reply to a specific message (2026-09-15) — see TurnRequest.replyTo. */
        replyTo?: import('../../../types/messageReply').MessageReplyRef | null;
      }
    ) => Promise<boolean>;
    /**
     * Reads (and clears) the typed refusal from the most recent failed send.
     * Optional so every existing caller and test double stays valid; absent
     * means "no refusal information", which is exactly today's behaviour.
     */
    consumeTurnRefusal?: () => { message: string } | null;
  } | null>(null);

  // Setter for prest0nSession (called by useGlobalChat after usePrest0nSession initializes)
  const setPrest0nSession = useCallback(
    (sessionInfo: SessionInfo | null, sendTurnFn: (
      message: string,
      callbacks: TurnStreamCallbacks,
      options?: {
        attachments?: import('../../../components/GlobalChatSystem/types').SerializedAttachment[];
        pageContext?: import('../../../config/pageContextRegistry').OrchestratorPageContext;
        signal?: AbortSignal;
        documentContent?: string;
        responseMode?: 'stream' | 'async' | 'auto';
        threadId?: string;
        /** Ingest-on-upload job id — see TurnRequest.ingestJobId. */
        ingestJobId?: string;
        requestedWorkflow?: 'deep-research' | 'routines';
        /** Pre-flight client-minted task id — see TurnRequest.clientTaskId. */
        clientTaskId?: string;
        /** Reply to a specific message (2026-09-15) — see TurnRequest.replyTo. */
        replyTo?: import('../../../types/messageReply').MessageReplyRef | null;
      }
    ) => Promise<boolean>,
    consumeTurnRefusalFn?: () => { message: string } | null) => {
      prest0nSessionRef.current = {
        session: sessionInfo,
        sendTurn: sendTurnFn,
        consumeTurnRefusal: consumeTurnRefusalFn,
      };
    },
    []
  );

  // Serverless-first (2026-08-15): useGlobalChat's migration gate runs ONCE at
  // mount — a fresh account whose config/runtime landed AFTER page mount kept
  // a permanently unwired transport while the turn-time gate (fresh read) said
  // migrated, so the old boot wait could only ever die at its timeout (owner
  // screenshot: 5-minute wait → "refresh the page"). This lets the turn ask
  // the chat layer to re-run its gate NOW instead of telling the user to
  // refresh — a page refresh is exactly a gate re-run, so do it for them.
  const requestSessionRewireRef = useRef<(() => void) | null>(null);
  const setRequestSessionRewire = useCallback((fn: (() => void) | null) => {
    requestSessionRewireRef.current = fn;
  }, []);

  // Orchestrator abort controllers keyed strictly by originating thread.
  //
  // PR #98 — remove the legacy single-controller mirror entirely. The
  // mirror was overwritten by whichever thread sent most recently, so
  // global side effects (tab-dormancy abort / global cancel fallback)
  // could target the wrong in-flight request. A send on thread B must
  // never replace, abort, or otherwise influence thread A's controller.
  const orchestratorAbortByThreadRef = useRef<Map<string, AbortController>>(new Map());
  /**
   * Resolve the originating thread id for the current message handler
   * invocation. Set by createMessageHandler from the helpers bundle
   * provided by useGlobalChat (which captures the active thread id at
   * send-time). Falls back to null when the thread switcher is disabled.
   */
  const resolveOriginatingThreadId = (helpers: unknown): string | null => {
    const h = helpers as { originatingThreadId?: string | null } | undefined;
    return h?.originatingThreadId || null;
  };
  const setOrchestratorAbortController = (
    threadId: string | null,
    controller: AbortController,
  ) => {
    if (threadId) {
      // Abort any prior in-flight request for the SAME thread before
      // overwriting (a new send on the same thread cancels its prior
      // attempt) — NEVER abort other threads' controllers.
      const prior = orchestratorAbortByThreadRef.current.get(threadId);
      if (prior && prior !== controller) {
        try { prior.abort('superseded_by_new_send'); } catch { /* ignore */ }
      }
      orchestratorAbortByThreadRef.current.set(threadId, controller);
    }
  };
  const clearOrchestratorAbortController = (
    threadId: string | null,
    controller: AbortController,
  ) => {
    if (threadId) {
      const stored = orchestratorAbortByThreadRef.current.get(threadId);
      if (stored === controller) {
        orchestratorAbortByThreadRef.current.delete(threadId);
      }
    }
  };

  // Stop fix (2026-07-31): the LIVE server task id for each originating
  // thread's in-flight turn, stamped pre-flight (mintClientTaskId /
  // onTaskQueued) and kept until the turn's terminal. cancelRunningTool reads
  // it to fire the fire-and-forget server-side cancel (cancelPrest0nTask) so
  // a stopped async turn stops burning server compute instead of completing
  // minutes later. Keyed by originating thread (null = thread-less/legacy
  // surfaces), mirroring orchestratorAbortByThreadRef's isolation discipline;
  // terminal cleanup is compare-and-delete so a stale turn can never drop a
  // newer turn's registration.
  const liveServerTaskByThreadRef = useRef<Map<string | null, string>>(new Map());

  // Active stream registry (for converting pending widgets to "stopped" on cancel)
  // When a turn/tool/orchestrator creates a streaming message, it registers here
  // so cancelRunningTool() can finalize it with "stopped" instead of just aborting.
  //
  // Round O (2026-07-15) false-"stopped" root fix: this used to be ONE global
  // ref, so cancelRunningTool STEP 1 ignored the passed threadId and archiving
  // unrelated thread B finalized thread A's live widget into a static
  // "stopped" bubble while A's server task kept running. It is now a
  // PER-THREAD map keyed by each turn's ORIGINATING thread id; cancels only
  // ever finalize the cancelled thread's entry (no id = legacy global stop).
  const activeStreamRegistryRef = useRef<ActiveStreamRegistry>(new ActiveStreamRegistry());
  // Entry-scoped bookkeeping for the LEGACY sharedContext.setActiveStream API
  // (tools register/clear without a thread id): remember the last generic
  // registration so `setActiveStream(null)` clears exactly that entry and can
  // never evict a different turn's registration under a raced thread key.
  const lastGenericActiveStreamRef = useRef<{ key: string | null; entry: ActiveStreamEntry } | null>(null);

  // Stream activity tracking (for tab dormancy detection)
  // Updated on each streaming chunk to track when stream was last active
  const lastStreamUpdateRef = useRef<number>(Date.now());
  // Dormancy threshold is 10 minutes. Deep Research's Stage 1/2/3 server-driven
  // streams can legitimately have multi-minute gaps between visible chunks
  // (Stage 1 ~30–90s, Stage 2 up to ~90s, Stage 3 3–5 min). A 60s threshold
  // mis-fires on any background tab switch during a normal run and surfaces
  // a confusing "Operation interrupted: Browser tab was inactive" message
  // even though the backend is fine. 10 min gives server-driven workflows
  // plenty of headroom while still catching true dead sockets on return.
  const DORMANCY_STALL_THRESHOLD_MS = 10 * 60 * 1000; // 10 minutes without activity = stalled

  // Orchestrator phase tracking — suppresses dormancy detection during API calls
  // The orchestrator has its own 30s per-call timeout; dormancy detection should not interfere
  const isOrchestratorActiveRef = useRef<boolean>(false);

  // ========================================================================
  // Sync Refs with State (avoid stale closures)
  // ========================================================================

  useEffect(() => {
    lastResearchReportRef.current = lastResearchReport;
  }, [lastResearchReport]);

  useEffect(() => {
    lastTranslatableContentRef.current = lastTranslatableContent;
  }, [lastTranslatableContent]);

  useEffect(() => {
    drSessionRef.current = drSession;
  }, [drSession]);

  useEffect(() => {
    videoScriptSessionRef.current = videoScriptSession;
  }, [videoScriptSession]);

  useEffect(() => {
    messageHistoryRef.current = messageHistory;
  }, [messageHistory]);

  useEffect(() => {
    documentContentRef.current = documentContent;
    documentFullTextRef.current = documentFullText;
  }, [documentContent]);

  // Keep documentManifestRef in sync with state for closure-safe reads
  useEffect(() => {
    documentManifestRef.current = documentManifest;
  }, [documentManifest]);

  useEffect(() => {
    pendingTokenUsagesRef.current = pendingTokenUsages;
  }, [pendingTokenUsages]);

  // ========================================================================
  // Tab Dormancy Detection & Stream Activity Tracking
  // ========================================================================

  /**
   * Update stream activity timestamp (heartbeat for dormancy detection).
   * Call this on each streaming chunk to track when the stream was last active.
   */
  const updateStreamActivity = useCallback(() => {
    lastStreamUpdateRef.current = Date.now();
  }, []);

  /**
   * Handle browser tab visibility changes.
   * When tab becomes visible again after being dormant, check if any active
   * stream has stalled and emergency-finalize it with a user-friendly message.
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Tab became visible - check for stalled streams
        const timeSinceLastUpdate = Date.now() - lastStreamUpdateRef.current;

        // Don't trigger dormancy during orchestrator's API call phase.
        // The orchestrator has its own 30s per-call timeout — dormancy detection
        // should not interfere with legitimate API calls in progress.
        if (isOrchestratorActiveRef.current) {
          lastStreamUpdateRef.current = Date.now(); // Reset baseline
          console.log('[GlobalTools] Tab visible during orchestrator phase - resetting dormancy timer (not interrupting)');
          return;
        }

        // Don't trigger dormancy if any server-driven workflow is currently
        // running. Deep Research in particular has multi-minute stages where
        // no chunk arrives on the client between stage transitions, but the
        // backend is fully healthy. Killing the stream here would surface a
        // misleading "Browser tab was inactive" error even though the server
        // is still producing the report. The force-finalize watchdog inside
        // deepResearch.ts handles true post-stream hangs separately.
        const hasRunningWorkflow = Array.from(toolRuntimesRef.current.values())
          .some(runtime => runtime.isRunning() && !runtime.isAwaitingInput());
        if (hasRunningWorkflow) {
          lastStreamUpdateRef.current = Date.now(); // Reset baseline
          console.log('[GlobalTools] Tab visible during active workflow - resetting dormancy timer (not interrupting)');
          return;
        }

        const shouldPreserve = shouldPreserveInFlightRunOnVisibilityRestore({
          isVisible: true,
          hasActiveStream: activeStreamRegistryRef.current.hasAny(),
          timeSinceLastUpdateMs: timeSinceLastUpdate,
          dormancyThresholdMs: DORMANCY_STALL_THRESHOLD_MS,
          isOrchestratorActive: isOrchestratorActiveRef.current,
          hasRunningWorkflow,
        });

        if (shouldPreserve) {
          if (activeStreamRegistryRef.current.hasAny() && timeSinceLastUpdate > DORMANCY_STALL_THRESHOLD_MS) {
            console.warn(
              `[GlobalTools] Visibility restored after ${Math.round(timeSinceLastUpdate / 1000)}s inactivity - preserving in-flight run and rebaselining heartbeat (no client-side abort)`
            );
          }
          lastStreamUpdateRef.current = Date.now();
          return;
        }
      } else {
        // Tab going dormant - record current time as last activity
        // This ensures we have a baseline for when the tab returns
        lastStreamUpdateRef.current = Date.now();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // ========================================================================
  // Pending Token Usage Functions
  // ========================================================================

  /**
   * Push token usage to pending accumulator.
   * Call this for any API call that doesn't produce its own visible message.
   */
  const pushPendingTokenUsage = useCallback((usage: TokenUsageMetadata) => {
    console.log('[GlobalTools] Pushing pending token usage:', usage.summary || usage.model);
    setPendingTokenUsages(prev => {
      const newUsages = [...prev, usage];
      pendingTokenUsagesRef.current = newUsages; // Sync ref immediately
      return newUsages;
    });
  }, []);

  /**
   * Consume and clear all pending token usages.
   * Called automatically by messaging finalize methods.
   */
  const consumePendingTokenUsages = useCallback((): TokenUsageMetadata[] => {
    const usages = pendingTokenUsagesRef.current;
    if (usages.length > 0) {
      console.log('[GlobalTools] Consuming', usages.length, 'pending token usages');
      setPendingTokenUsages([]);
      pendingTokenUsagesRef.current = [];
    }
    return usages;
  }, []);

  // ========================================================================
  // Reset Functions
  // ========================================================================

  const resetDrSession = useCallback(() => {
    setDrSession(INITIAL_DR_SESSION);
  }, []);

  const resetVideoScriptSession = useCallback(() => {
    setVideoScriptSession(INITIAL_VIDEO_SCRIPT_SESSION);
  }, []);

  // ========================================================================
  // Tool Runtimes (defined before sharedContext since resetToolRuntime is used in it)
  // ========================================================================

  // Map of tool ID to runtime instance
  const toolRuntimesRef = useRef<Map<GlobalToolId, GlobalToolRuntime<unknown>>>(new Map());

  // Track buttons currently being processed to prevent double-click race conditions
  const processingButtonsRef = useRef<Set<string>>(new Set());

  // 2026-08-12 (connect-card incident, deliverable B): debounce keys for the
  // automatic APP_NOT_CONNECTED connect-card offer — at most ONE auto-offer
  // per app per thread per TURN (per-turn since 2026-09-02, plan P0 #4: the
  // per-session key was one of the four layers that made a re-summon
  // impossible — D7, "never could bring up the tile again"). Keys are
  // `${threadId ?? 'no-thread'}::${cardStampEpoch}::${appKey}`; entries are
  // removed again when the offer could not actually be posted (catalog
  // fetch failed) so a later turn can retry.
  const autoConnectOfferKeysRef = useRef<Set<string>>(new Set());

  // ⓘ SERIES LINEUP ON CARDS (owner directive 2026-08-29): card tiles
  // (authorize, batch, auto-sent receipt, connect offer) post with no token
  // usage, so their info popup said "no extensive compute was necessary"
  // while the turn that prepared them ran the whole model/tool series. Card
  // posts register their message id here; when the turn's usage lands
  // (onDone — both the SSE and async-task lanes deliver through it), it
  // stamps onto every registered card via the ById updater, and a card that
  // posts AFTER the usage landed (async scan racing onDone) stamps
  // immediately from the stored copy. Both refs reset at each turn's start
  // so one turn's series never lands on another turn's cards.
  const turnCardMessageIdsRef = useRef<Set<string>>(new Set());
  const turnCardUsageRef = useRef<Partial<
    Pick<import('../../../components/GlobalChatSystem/types').ChatMessage, 'tokenUsage' | 'tokenUsages'>
  > | null>(null);
  // EPOCH SCOPE (adversarial review 2026-08-29): the refs are shared by every
  // turn, and an async task's onDone can land MINUTES after a newer turn
  // started (concurrent sends are supported) — without a fence, the stale
  // turn's closure would stamp ITS series and cost onto the newer turn's
  // cards. Each turn start bumps the epoch and captures it; a stamp whose
  // captured epoch is no longer current is dropped.
  const turnCardEpochRef = useRef(0);

  const registerCardMessageForUsage = useCallback((messageId: string) => {
    if (!messageId) return;
    turnCardMessageIdsRef.current.add(messageId);
    const usage = turnCardUsageRef.current;
    const updater = chatHelpersRef.current?.streaming?.updateStreamingMessageById;
    if (usage && updater) {
      // ONE ATTRIBUTION PER TURN (2026-09-01): the copy is marked inherited so
      // the transcript shows the ⓘ once — on the answer bubble — never twice.
      try { updater(messageId, { ...usage, tokenUsageInherited: true }); } catch { /* usage attach is never fatal */ }
    }
  }, []);

  const stampTurnUsageOntoCards = useCallback((usage: Partial<
    Pick<import('../../../components/GlobalChatSystem/types').ChatMessage, 'tokenUsage' | 'tokenUsages'>
  >, epoch: number) => {
    if (epoch !== turnCardEpochRef.current) return;
    if (!usage.tokenUsage && !(usage.tokenUsages && usage.tokenUsages.length > 0)) return;
    turnCardUsageRef.current = usage;
    const updater = chatHelpersRef.current?.streaming?.updateStreamingMessageById;
    if (!updater) return;
    for (const id of turnCardMessageIdsRef.current) {
      // ONE ATTRIBUTION PER TURN (2026-09-01): inherited copy, flagged — the
      // answer bubble owns the visible ⓘ; a card-only turn keeps its own.
      try { updater(id, { ...usage, tokenUsageInherited: true }); } catch { /* usage attach is never fatal */ }
    }
  }, []);

  /**
   * Reset a specific tool's runtime.
   * This clears the isAwaitingInput flag and any in-progress state.
   * Use this when one tool needs to prevent another tool from intercepting messages.
   */
  const resetToolRuntime = useCallback((toolId: GlobalToolId) => {
    const runtime = toolRuntimesRef.current.get(toolId);
    if (runtime) {
      console.log(`[useGlobalTools] Resetting tool runtime: ${toolId}`);
      runtime.reset();
    }
  }, []);

  /**
   * Bridge legacy agentic workflow content to GlobalTools state.
   * Call this before handling video script buttons when legacy handler might have run.
   */
  const bridgeLegacyContent = useCallback((content: TranslatableContent) => {
    if (!lastTranslatableContentRef.current) {
      setLastTranslatableContent(content);
      lastTranslatableContentRef.current = content;
      console.log('[GlobalTools] Bridged legacy content to sharedContext');
    }
  }, []);

  /**
   * Hydrate shared context from persisted chat history.
   * Call this on mount or before button handling to ensure content is available.
   * This enables workflow content to survive page refreshes.
   */
  const hydrateFromHistory = useCallback((storageKey: string = GLOBAL_CHAT_HISTORY_KEY) => {
    const history = getFromLocalStorage<ChatMessage[]>(storageKey, []);

    // Hydrate research report if not already set
    if (!lastResearchReportRef.current) {
      const researchOutput = extractLatestWorkflowOutput(history, 'research_report');
      if (researchOutput) {
        const report = {
          content: researchOutput.content,
          title: researchOutput.title || 'Research Report',
          timestamp: new Date(researchOutput.timestamp),
        };
        setLastResearchReport(report);
        lastResearchReportRef.current = report;
        console.log('[GlobalTools] Hydrated research report from history');
      }
    }

    // Hydrate translatable content if not already set
    if (!lastTranslatableContentRef.current) {
      // Try translation first (most recent processed content), then formatted, then research
      const translation = extractLatestWorkflowOutput(history, 'translation');
      const formatted = extractLatestWorkflowOutput(history, 'formatted_content');
      const research = extractLatestWorkflowOutput(history, 'research_report');
      const output = translation || formatted || research;

      if (output) {
        // Map workflow output type to TranslatableContent source
        const source = output.type === 'research_report' ? 'research' : 'formatted';
        const content: TranslatableContent = {
          content: output.content,
          title: output.title || 'Content',
          timestamp: new Date(output.timestamp),
          source: source,
        };
        setLastTranslatableContent(content);
        lastTranslatableContentRef.current = content;
        console.log('[GlobalTools] Hydrated translatable content from history, type:', output.type);
      }
    }
  }, []);

  // Hydrate from history on mount when authorized
  useEffect(() => {
    if (isAuthorized) {
      hydrateFromHistory();
    }
  }, [isAuthorized, hydrateFromHistory]);

  // ========================================================================
  // Content History Management
  // ========================================================================

  /**
   * Push new content to history stack (max 5 items).
   */
  const pushContentHistory = useCallback((content: TranslatableContent) => {
    setContentHistory(prev => {
      const newHistory = [...prev, content];
      // Keep only last 5 items
      return newHistory.slice(-5);
    });
  }, []);

  /**
   * Get content by source type from history (returns most recent match).
   */
  const getContentBySource = useCallback((source: string): TranslatableContent | null => {
    // Search from most recent to oldest
    for (let i = contentHistory.length - 1; i >= 0; i--) {
      if (contentHistory[i].source === source) {
        return contentHistory[i];
      }
    }
    return null;
  }, [contentHistory]);

  /**
   * Clear content history (e.g., when starting fresh research).
   */
  const clearContentHistory = useCallback(() => {
    setContentHistory([]);
  }, []);

  /**
   * Wrapper for setLastTranslatableContent that auto-pushes to history.
   */
  const setLastTranslatableContentWithHistory = useCallback((content: TranslatableContent | null) => {
    if (content) {
      pushContentHistory(content);
    }
    setLastTranslatableContent(content);
  }, [pushContentHistory]);

  /**
   * Set the active stream reference for cancellation.
   * Called by legacy tools (via sharedContext) when they create a pending
   * widget, so cancelRunningTool() can finalize it with "stopped" instead of
   * just aborting.
   *
   * Round O: registrations land in the PER-THREAD registry. This legacy API
   * carries no thread id, so the entry is keyed by `currentTurnThreadIdRef`
   * at call time — best-effort for the legacy tool lane (a concurrent send on
   * another thread can overwrite that shared ref mid-run; the single global
   * slot this replaces had the same limitation). Turn-scoped paths (the
   * dispatch registration in createMessageHandler and the per-turn wrapper
   * handed to runOrchestrator) register with their explicit originating
   * thread instead and are immune to that race. `setActiveStream(null)`
   * clears ONLY the entry this API registered (entry-scoped compare-and-
   * delete) so it can never evict another turn's registration.
   */
  const setActiveStream = useCallback((stream: {
    finalize: (content: { text: string; html: string; sourceTag: string }) => void;
  } | null): void => {
    if (stream) {
      const key = currentTurnThreadIdRef.current ?? null;
      const entry = activeStreamRegistryRef.current.register({
        threadId: key,
        finalize: stream.finalize,
      });
      lastGenericActiveStreamRef.current = { key, entry };
    } else {
      const last = lastGenericActiveStreamRef.current;
      lastGenericActiveStreamRef.current = null;
      if (last) {
        activeStreamRegistryRef.current.clear(last.key, last.entry);
      }
    }
  }, []);

  // ========================================================================
  // Shared Context Object (for tools)
  // ========================================================================

  const sharedContext: SharedWorkflowContext = useMemo(
    () => ({
      lastResearchReport,
      setLastResearchReport,
      lastResearchReportRef,
      lastTranslatableContent,
      setLastTranslatableContent: setLastTranslatableContentWithHistory, // Auto-pushes to history
      lastTranslatableContentRef,
      drSession,
      drSessionRef,
      setDrSession,
      resetDrSession,
      videoScriptSession,
      videoScriptSessionRef,
      setVideoScriptSession,
      resetVideoScriptSession,
      resetToolRuntime,
      // Message history and document content (loaded by orchestrator)
      messageHistory,
      setMessageHistory,
      messageHistoryRef,
      documentContent,
      setDocumentContent,
      documentContentRef,
      documentFullText,
      setDocumentFullText,
      documentFullTextRef,
      // Document analysis metadata (from unified document reader)
      documentAnalysisMeta,
      setDocumentAnalysisMeta,
      lastDocumentAnalyzerFilesRef,
      // Multi-document manifest (FH-CLASSIFIER-002)
      documentManifest,
      setDocumentManifest,
      documentManifestRef,
      // Content history (preserves content versions)
      contentHistory,
      pushContentHistory,
      getContentBySource,
      clearContentHistory,
      // Pending token usages (for background API calls)
      pendingTokenUsages,
      pushPendingTokenUsage,
      consumePendingTokenUsages,
      pendingTokenUsagesRef,
      // Active stream for cancellation (converts pending widget to "stopped" on cancel)
      setActiveStream,
      // Stream activity tracking (heartbeat for dormancy detection)
      updateStreamActivity,
      // Context message appender (for document analysis persistence)
      // Wrapper function reads ref at CALL TIME, not creation time (fixes race condition)
      appendContextMessage: (message: ChatMessage) => {
        if (appendContextMessageRef.current) {
          appendContextMessageRef.current(message);
        } else {
          console.warn('[SharedContext] appendContextMessage not set yet');
        }
      },
      // Cross-thread search context (PR fix/thread-isolation-regression-and-cross-thread-retrieval).
      // Read at call time so a tool can resolve the host's chat instance
      // id without each tool having to plumb it through.
      get chatInstanceId() { return chatInstanceIdRef.current; },
      // Point 6 (Option B) — server-side durable GUEST deep-research wiring.
      // Read at call time so deepResearchTool resolves the host's current guest
      // vs migrated decision + persist callback without per-tool plumbing.
      get guestServerResearch() { return guestServerResearchRef.current; },
      // Processing-state heartbeat (2026-05-07 fix). Reads refs at call time
      // so the chain executor + long-running tools can keep state.isProcessing
      // fresh across handoffs between tool finalize() and the next tool's first
      // showPending(). Fallback "working…" widget in GlobalChatSystem renders
      // whenever isProcessing is true but no tool-owned widget is visible.
      refreshProcessingHeartbeat: (threadId?: string | null) => {
        if (refreshProcessingHeartbeatRef.current) {
          // Resolve to the current turn's originating thread when the caller
          // didn't pass an explicit one — never to the most-recent-send ref.
          const tid = (threadId !== undefined ? threadId : currentTurnThreadIdRef.current) || null;
          refreshProcessingHeartbeatRef.current(tid);
        }
      },
      releaseProcessingHeartbeat: (threadId?: string | null) => {
        if (releaseProcessingHeartbeatRef.current) {
          const tid = (threadId !== undefined ? threadId : currentTurnThreadIdRef.current) || null;
          releaseProcessingHeartbeatRef.current(tid);
        }
      },
    }),
    [lastResearchReport, lastTranslatableContent, drSession, videoScriptSession, resetToolRuntime, messageHistory, documentContent, documentFullText, documentAnalysisMeta, documentManifest, contentHistory, pushContentHistory, getContentBySource, clearContentHistory, setLastTranslatableContentWithHistory, pendingTokenUsages, pushPendingTokenUsage, consumePendingTokenUsages, setActiveStream, updateStreamActivity]
  );

  // ========================================================================
  // Sync sharedContext to existing runtimes (prevents stale context hangs)
  // ========================================================================
  // When sharedContext is recreated by useMemo (due to any of its 18+ dependencies
  // changing), existing runtimes still hold the old reference. This causes silent
  // failures when resume() re-creates messaging helpers with stale closures.
  // By syncing on every sharedContext change, runtimes always have the latest context.
  useEffect(() => {
    for (const runtime of toolRuntimesRef.current.values()) {
      runtime.updateSharedContext(sharedContext);
    }
  }, [sharedContext]);

  // ========================================================================
  // Health Check: detect orphaned awaiting-input states (diagnostics)
  // ========================================================================
  useEffect(() => {
    const HEALTH_CHECK_INTERVAL_MS = 30 * 1000; // 30 seconds
    const interval = setInterval(() => {
      for (const [toolId, runtime] of toolRuntimesRef.current.entries()) {
        if (runtime.isAwaitingInput()) {
          const duration = runtime.getAwaitingInputDuration();
          if (duration && duration > 5 * 60 * 1000) {
            console.warn(
              `[GlobalTools] Health check: tool "${toolId}" has been awaiting input for ${Math.round(duration / 1000)}s`
            );
          }
        }
        // Detect orphaned states: tool stage says it should be awaiting but flag is false
        const state = runtime.getState() as Record<string, unknown> | undefined;
        if (
          (state?.stage === 'stage1_confirm' || state?.stage === 'stage1_retry') &&
          !runtime.isAwaitingInput() &&
          runtime.isRunning()
        ) {
          console.error(
            `[GlobalTools] ORPHANED STATE DETECTED: tool "${toolId}" has stage=${state.stage} but isAwaitingInput=false — will be recovered on next user message`
          );
        }
      }
    }, HEALTH_CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  // ========================================================================
  // Cross-Device Workflow State Sync (Firestore → localStorage)
  // ========================================================================

  // On auth, sync any persisted deep-research state from Firestore to localStorage.
  // This enables cross-device resume: user starts on Device A, signs in on Device B,
  // and the "skip to deep research" button works because localStorage is pre-populated
  // from Firestore before any button click triggers getOrCreateRuntime().
  useEffect(() => {
    if (!isAuthorized) return;
    const user = getAuth().currentUser;
    if (!user) return;

    // Fast path: if localStorage already has state, no need to fetch from Firestore
    if (localStorage.getItem('prest0_dr_workflow_state')) return;

    const db = getFirestore();
    getDoc(doc(db, 'users', user.uid, 'workflow_state', 'deep_research'))
      .then(snap => {
        if (snap.exists()) {
          const data = snap.data();
          if (data?.stage === 'stage1_confirm' && data.originalQuery) {
            // Firestore Timestamps aren't JSON-serializable — convert savedAt
            const payload = { ...data, savedAt: data.savedAt?.toMillis?.() || Date.now() };
            localStorage.setItem('prest0_dr_workflow_state', JSON.stringify(payload));
            console.log('[useGlobalTools] Synced deep-research state from Firestore (cross-device)');
          }
        }
      })
      .catch(e => console.warn('[useGlobalTools] Firestore workflow state sync failed:', e));
  }, [isAuthorized]);

  // ========================================================================
  // Tool Runtime Management
  // ========================================================================

  /**
   * Get or create a runtime for a specific tool.
   */
  const getOrCreateRuntime = useCallback(
    (toolId: GlobalToolId, originatingThreadId?: string | null): GlobalToolRuntime<unknown> | null => {
      // Round J (A1 root cause): turn-path callers pass the turn's
      // originating thread EXPLICITLY (same style as the TurnContext
      // explicit-threading refactor) so binding no longer depends on the
      // shared last-writer-wins currentTurnThreadIdRef — which a concurrent
      // send on another thread overwrites mid-await, and which is null on
      // legacy programmatic sends (→ unbound, hijackable runtime). The ref
      // remains the fallback for button-click paths that have no explicit
      // thread.
      const bindThreadId = originatingThreadId !== undefined
        ? originatingThreadId
        : currentTurnThreadIdRef.current;
      // Check if runtime already exists
      let runtime = toolRuntimesRef.current.get(toolId);
      if (runtime) {
        // Thread-isolation (2026-05-09 fix): if this runtime isn't
        // currently mid-execution, (re)bind it to the current turn's
        // originating thread so a subsequent awaiting-input release
        // targets the right chip. We never overwrite an active
        // runtime's binding (that runtime is already executing for a
        // specific thread; another thread's send must NOT relabel it).
        if (!runtime.isRunning() && bindThreadId) {
          runtimeThreadIdByToolRef.current.set(toolId, bindThreadId);
        }
        return runtime;
      }

      // Find the tool definition
      const tools = getAllRegisteredTools();
      const tool = tools.find((t) => t.id === toolId);
      if (!tool) {
        console.warn(`[useGlobalTools] Tool not found: ${toolId}`);
        return null;
      }

      // Create new runtime
      runtime = createToolRuntime(tool, sharedContext, recordTransaction, chatHelpersRef);
      toolRuntimesRef.current.set(toolId, runtime);

      // Thread-isolation (2026-05-09 fix): bind this freshly-created
      // runtime to the current turn's originating thread so its
      // awaiting-input listener releases the right thread's processing
      // flag. Only set if we have a current-turn thread id.
      if (bindThreadId) {
        runtimeThreadIdByToolRef.current.set(toolId, bindThreadId);
      }

      // MT-QA-BUG-009 (2026-05-08): clear the orchestrator's "thinking" spinner
      // the moment any tool yields the turn back to the user via an
      // awaiting-input state transition. Without this, tools that post a
      // non-streaming intake card (Legal Document Creator gather-details,
      // select-type, etc.) leave the bottom-of-chat spinner visible forever
      // even though the input field is actually active and waiting for the
      // user. The ref is populated by createMessageHandler() for each new
      // orchestrator turn and idempotently cleared after first call — so
      // subsequent awaiting-input events (resume → awaiting-input again) are
      // no-ops rather than double-removals.
      //
      // MT-QA-BUG-009 follow-up (2026-05-08 G4/G5 regression): WCAB PHS and
      // Demand Letter intakes reach awaiting-input via a button-click path
      // that never creates an orchestrator thinking indicator in the first
      // place. The stuck widget in that case is the `working...` FALLBACK
      // rendered by <GlobalChatSystem> whenever `state.isProcessing` is
      // true and no tool-owned pending widget is visible. Explicitly call
      // `releaseProcessingHeartbeat()` (wired to `stopProcessing()` in
      // useGlobalChat) so the fallback pill clears as soon as the intake
      // card posts, and every subsequent sub-step transition that yields
      // the turn back to the user also re-clears the flag.
      runtime.on('awaiting-input', (data) => {
        const remove = pendingThinkingIndicatorRemoveRef.current;
        if (remove) {
          console.log(
            `[useGlobalTools] awaiting-input from "${data.toolId}" — clearing pending thinking indicator`
          );
          try {
            remove();
          } catch (err) {
            console.warn('[useGlobalTools] Thinking indicator removal threw:', err);
          }
        }
        const release = releaseProcessingHeartbeatRef.current;
        if (release) {
          try {
            // Thread-isolation (2026-05-09 demo fix): the awaiting-input
            // handler MUST clear the ORIGINATING thread's processing
            // flag — never "whichever thread happens to be current right
            // now". Resolve via the per-runtime threadId map (populated
            // when this runtime was invoked from createMessageHandler);
            // a concurrent send on a different thread cannot overwrite
            // another runtime's entry. The literal
            // `[threadIso] awaiting-input release` is a live-bundle grep
            // marker.
            const tid = runtimeThreadIdByToolRef.current.get(data.toolId as GlobalToolId)
              ?? currentTurnThreadIdRef.current
              ?? null;
            // eslint-disable-next-line no-console
            console.debug('[threadIso] awaiting-input release', { toolId: data.toolId, tid });
            release(tid);
          } catch (err) {
            console.warn('[useGlobalTools] releaseProcessingHeartbeat threw:', err);
          }
        }
      });

      // Thread-isolation (2026-05-09 fix): clear the per-runtime thread
      // id mapping when the runtime completes/fails/cancels so we don't
      // leak a stale binding into the next turn.
      const clearRuntimeThreadBinding = () => {
        runtimeThreadIdByToolRef.current.delete(toolId);
      };
      runtime.on('workflow-completed', clearRuntimeThreadBinding);
      runtime.on('workflow-failed', clearRuntimeThreadBinding);
      runtime.on('workflow-cancelled', clearRuntimeThreadBinding);

      // Restore persisted workflow state for deep-research (survives page reload).
      // When Stage 1 completes and shows clarifying questions, the state is saved
      // to localStorage. If the user closes the tab and comes back, the runtime
      // is freshly created but the buttons still exist in chat. This restoration
      // ensures those buttons remain functional.
      if (toolId === 'deep-research') {
        try {
          // Durability audit rank #13 (2026-07-13): a checkpoint stamped by a
          // DIFFERENT uid must never restore into this account's runtime —
          // purge it so the new account's own Firestore checkpoint sync-down
          // is not blocked either. Legacy checkpoints without a stamp keep
          // restoring (backward compatible; they clear on resume).
          try {
            const checkpointOwner = localStorage.getItem('prest0_dr_workflow_state_owner');
            const currentUid = user?.id || null;
            if (checkpointOwner && currentUid && checkpointOwner !== currentUid) {
              localStorage.removeItem('prest0_dr_workflow_state');
              localStorage.removeItem('prest0_dr_workflow_state_owner');
              console.warn('[useGlobalTools] Purged foreign-account deep-research checkpoint');
            }
          } catch { /* best-effort */ }
          const savedState = localStorage.getItem('prest0_dr_workflow_state');
          if (savedState) {
            const parsed = JSON.parse(savedState);
            if (parsed?.stage === 'stage1_confirm' && parsed.originalQuery) {
              // Merge with initial state defaults to ensure all required fields exist.
              // The persisted state only saves key fields (originalQuery, stage1Findings, etc.)
              // but the runtime expects full state with userClarifications: [], cancelled: false, etc.
              const initialState = tool.createInitialState() as Record<string, unknown>;
              const fullState = { ...initialState, ...parsed };
              runtime.restoreState(fullState, 'qualify');
              console.log('[useGlobalTools] Restored deep-research state from localStorage (stage1_confirm)');
            }
          }
        } catch (e) {
          console.warn('[useGlobalTools] Failed to restore deep-research state:', e);
        }
      }

      return runtime;
    },
    [sharedContext, recordTransaction]
  );

  // ========================================================================
  // Message Handler
  // ========================================================================

  /**
   * Create a message handler for integration with GlobalChatSystem.
   */
  const createMessageHandler = useCallback((): OnUserMessageHandler => {
    return async (message: ChatMessage, helpers): Promise<boolean> => {
      // Skip if not authorized
      if (!isAuthorized) {
        return false;
      }

      // Store helpers for button delegation
      chatHelpersRef.current = helpers;

      // Thread-isolation (2026-05-09 demo fix): capture the originating
      // thread id at the top of EVERY handler invocation so async
      // listeners registered on tool runtimes can resolve which thread
      // a release/refresh should target. Any subsequent send on a
      // different thread will overwrite `currentTurnThreadIdRef`, which
      // is exactly why the per-runtime `runtimeThreadIdByToolRef` map
      // is the authoritative source for awaiting-input releases.
      const turnOriginatingThreadId =
        ((helpers as unknown as { originatingThreadId?: string | null })?.originatingThreadId)
        || null;
      currentTurnThreadIdRef.current = turnOriginatingThreadId;
      // Capture the seeded history for THIS handler invocation. Another
      // rapid send can call sharedContext.setMessageHistory before this
      // async handler reached the legacy browser orchestrator (severed —
      // D13-6a); the per-call history snapshot that closed that race went
      // with its only reader.

      // Hire-required created org (task #75, 2026-08-18): the ACTIVE
      // workspace hasn't hired its prest0n — every turn runs the page-aware
      // pitch lane (guest-style, hiring is the only CTA) and NO orchestration
      // happens, on either the session or the legacy lane. A pitch failure
      // falls through to the normal lanes, where the server-side paymentGate
      // still refuses real work with the honest hire message. Time-boxed so
      // a slow Firestore read can never hang the send path — timeout fails
      // toward the normal lanes.
      try {
        const pitchWs = await Promise.race([
          resolveHireRequiredWorkspace(),
          new Promise<null>((resolve) => { setTimeout(() => resolve(null), 2000); }),
        ]);
        if (pitchWs) {
          try {
            const reply = await runOrgPitchTurn({
              accountId: pitchWs.accountId,
              message: message.text || '',
            });
            helpers.addMessage({
              text: reply,
              isUser: false,
              timestamp: new Date(),
              sourceTag: ORG_PITCH_SOURCE_TAG,
            });
            return true;
          } catch (pitchErr) {
            if ((pitchErr as Error)?.message === 'workspace-active') {
              // Hired since the cache — fall through to the real lanes.
            } else {
              helpers.addMessage({
                text: `i couldn’t answer just now — try again in a moment. (this organization hasn’t hired its Prest0n yet — hire on the subscription page and i start working immediately.)`,
                isUser: false,
                timestamp: new Date(),
                sourceTag: ORG_PITCH_SOURCE_TAG,
              });
              return true;
            }
          }
        }
      } catch { /* posture unknown → normal lanes; server gate backstops */ }

      // Global thinking cleanup hook for this message. It is assigned once the
      // stage1 spinner is created, and stays callable by fallback/pattern paths.
      let removeThinkingIndicator: () => void = () => {};

      // D8 tail (2026-08-25): when the break-out predicate returns
      // verdict 'ambiguous', the message dispatches as a normal server turn
      // carrying a pending-question hint and the waiting form is PARKED —
      // runtime.cancel() destroyed it irreversibly (no persistence on the
      // legal-doc creator; a 20-field form gone on one loose regex). The
      // guard flag also keeps the fallback recovery loop below from
      // re-swallowing the same message as a force-resume (double-turn
      // hazard).
      let ambiguousPendingWorkflow: { toolId: string; matched?: string } | null = null;

      // Check if any tool is awaiting input
      for (const [toolId, runtime] of toolRuntimesRef.current.entries()) {
        if (runtime.isAwaitingInput()) {
          // Round J A1 (cross-thread hijack): a runtime may only capture a
          // send originating from its own bound thread. Without this scope,
          // thread A's awaiting-input runtime resumed with thread B's text
          // and returned handled=true before B ever got a spinner — a fully
          // silent send on B. Unbound runtimes only capture thread-less
          // sends (single-thread/legacy surfaces). Same binding source as
          // the thread-scoped hasRunningWorkflow gate below.
          if (!canRuntimeCaptureSend(
            runtimeThreadIdByToolRef.current.get(toolId),
            turnOriginatingThreadId,
          )) {
            continue;
          }
          // FH-CLASSIFIER-005: Before handing the message to the awaiting
          // runtime as a field answer, check whether it is an explicit
          // re-route / negation / abort signal. If so, cancel the runtime
          // and fall through to the orchestrator (which runs the PR #37
          // deterministic pre-router first). This closes the hijack path
          // where a mid-workflow manifest request was being swallowed as
          // a field answer.
          const breakOut = shouldBreakOutOfAwaitingInput(
            message.text,
            message.attachments,
            toolId
          );
          if (breakOut.breakOut) {
            if (breakOut.verdict === 'ambiguous') {
              // D8 tail: PARK, never cancel — dispatch with the hint; the
              // primary judges answer-vs-new-topic. The form stays open
              // (awaiting-input never blocks the orchestrator and nothing
              // reaps it), so an on-topic follow-up can still resume it.
              ambiguousPendingWorkflow = { toolId, matched: breakOut.matched };
              console.warn(
                `[useGlobalTools] AWAITING-INPUT AMBIGUOUS: tool "${toolId}" parked (NOT cancelled) — dispatching to the primary with a pending-question hint (condition=${breakOut.condition}, matched=${breakOut.matched})`
              );
              break;
            }
            console.warn(
              `[useGlobalTools] AWAITING-INPUT BREAK-OUT: tool "${toolId}" cancelled by explicit negation/manifest intent — re-routing through orchestrator (condition=${breakOut.condition}, matched=${breakOut.matched}, reason=${breakOut.reason})`
            );
            try {
              runtime.cancel();
            } catch (cancelErr) {
              console.warn(
                `[useGlobalTools] AWAITING-INPUT BREAK-OUT: runtime.cancel() threw for "${toolId}"`,
                cancelErr
              );
            }
            // Fall through to the orchestrator block below; do NOT resume
            // and do NOT return true.
            break;
          }

          // ============================================================
          // BUG-DR-DOUBLE-DISPATCH (2026-06-11, Fernando 2026-06-10
          // session): on migrated accounts the SERVER prest0n-session owns
          // the deep-research workflow (task-queue clarifying card,
          // clarifyingCardEmitted marker, skip-loop guard). A stale LEGACY
          // browser DR runtime left awaiting-input (e.g. created before the
          // migration gate resolved, or surviving from an older bundle's
          // turn) would resume here and drive a SECOND DR stack into the
          // same thread surface — the legacy qualification card (with the
          // "write video script" CTA + helper text) interleaving with the
          // server card. Gate the resume: on migrated accounts, cancel the
          // stale legacy runtime and fall through to the prest0n-session
          // path below, which is the sole DR dispatcher for these accounts.
          // The gate check runs ONLY when a stale DR runtime is actually
          // present (rare), so the normal send path pays nothing.
          // ============================================================
          if (toolId === 'deep-research') {
            const drGateAccountId = resolveAccountIdFromAuth();
            const drGate = drGateAccountId ? await shouldUsePrest0nRuntimeForUser(drGateAccountId) : null;
            if (drGate?.isMigrated) {
              console.warn(
                '[useGlobalTools] BUG-DR-DOUBLE-DISPATCH guard: cancelling stale legacy deep-research awaiting-input runtime on migrated account — server prest0n-session owns DR'
              );
              try {
                runtime.cancel();
              } catch (cancelErr) {
                console.warn('[useGlobalTools] BUG-DR-DOUBLE-DISPATCH guard: runtime.cancel() threw', cancelErr);
              }
              break; // fall through to the prest0n-session path below
            }
          }

          // Resume the tool with user input
          console.log(`[useGlobalTools] Message handler: routing to awaiting tool "${toolId}"`);
          // Thread-isolation: bind this resume to the originating thread
          // so the runtime's awaiting-input listener releases the right
          // thread's processing flag.
          runtimeThreadIdByToolRef.current.set(toolId, turnOriginatingThreadId);
          await runtime.resume(
            message.text,
            message.attachments,
            message
          );
          return true;
        }
      }

      // FALLBACK: Check tool state directly for stuck workflows where isAwaitingInput was lost.
      // This prevents the workflow from hanging permanently if the flag was cleared by a
      // React re-render or stale sharedContext race condition.
      for (const [toolId, runtime] of toolRuntimesRef.current.entries()) {
        // D8 tail: an ambiguous dispatch owns this message — the recovery
        // loop must not force-resume a parked runtime with it (double-turn).
        if (ambiguousPendingWorkflow) break;
        const toolState = runtime.getState() as Record<string, unknown> | undefined;
        if (toolState?.stage === 'stage1_confirm' || toolState?.stage === 'stage1_retry') {
          // Round J A1: same thread scoping as the awaiting-input loop above
          // — a stuck workflow on thread A must never swallow thread B's
          // send as a force-resume.
          if (!canRuntimeCaptureSend(
            runtimeThreadIdByToolRef.current.get(toolId),
            turnOriginatingThreadId,
          )) {
            continue;
          }
          // FH-CLASSIFIER-005: Same break-out guard as the awaiting-input
          // loop above. If the incoming message is a re-route / negation /
          // abort signal, cancel the stale runtime and fall through to the
          // orchestrator rather than force-resuming.
          const breakOut = shouldBreakOutOfAwaitingInput(
            message.text,
            message.attachments,
            toolId
          );
          if (breakOut.breakOut) {
            if (breakOut.verdict === 'ambiguous') {
              // Same park-don't-cancel handling as the primary loop above.
              ambiguousPendingWorkflow = { toolId, matched: breakOut.matched };
              console.warn(
                `[useGlobalTools] AWAITING-INPUT AMBIGUOUS (fallback): tool "${toolId}" stage=${toolState.stage} parked (NOT cancelled) — dispatching with a pending-question hint (condition=${breakOut.condition}, matched=${breakOut.matched})`
              );
              break;
            }
            console.warn(
              `[useGlobalTools] AWAITING-INPUT BREAK-OUT (fallback): tool "${toolId}" stage=${toolState.stage} cancelled by explicit negation/manifest intent — re-routing through orchestrator (condition=${breakOut.condition}, matched=${breakOut.matched}, reason=${breakOut.reason})`
            );
            try {
              runtime.cancel();
            } catch (cancelErr) {
              console.warn(
                `[useGlobalTools] AWAITING-INPUT BREAK-OUT (fallback): runtime.cancel() threw for "${toolId}"`,
                cancelErr
              );
            }
            break;
          }

          // BUG-DR-DOUBLE-DISPATCH (2026-06-11): same migrated-account
          // guard as the awaiting-input loop above — never force-resume a
          // stale legacy deep-research runtime when the server
          // prest0n-session owns DR for this account.
          if (toolId === 'deep-research') {
            const drGateAccountId = resolveAccountIdFromAuth();
            const drGate = drGateAccountId ? await shouldUsePrest0nRuntimeForUser(drGateAccountId) : null;
            if (drGate?.isMigrated) {
              console.warn(
                '[useGlobalTools] BUG-DR-DOUBLE-DISPATCH guard (recovery loop): cancelling stale legacy deep-research runtime on migrated account'
              );
              try {
                runtime.cancel();
              } catch (cancelErr) {
                console.warn('[useGlobalTools] BUG-DR-DOUBLE-DISPATCH guard (recovery loop): runtime.cancel() threw', cancelErr);
              }
              break; // fall through to the prest0n-session path below
            }
          }

          console.warn(
            `[useGlobalTools] RECOVERY: Tool "${toolId}" has stage=${toolState.stage} but isAwaitingInput=false — force-resuming`
          );
          await runtime.resume(message.text, message.attachments, message);
          return true;
        }
      }

      // ========================================================================
      // Orchestrator Pre-Check (NEW)
      // ========================================================================

      // Check if message is from a button click
      const isFromButton =
        message.text.startsWith('[Button:') ||
        message.sourceTag?.includes('button');

      // Check if any workflow is currently running (NOT awaiting-input).
      //
      // MT-RELIABILITY (2026-06-29): this gate MUST be THREAD-SCOPED. The
      // previous implementation checked every runtime globally, so a
      // long-running workflow on thread A (e.g. Deep Research) caused a
      // fresh send on thread B to skip the orchestrator entirely. No
      // assistant placeholder was ever created, the message fell through to
      // pattern matching, matched nothing, and the processing chip span
      // forever with no reply and no recovery card (the "Hello" failure
      // signature). We only treat a running workflow as blocking when it
      // belongs to the SAME originating thread as this turn. A running
      // runtime's thread binding is captured at start and never overwritten
      // by another thread (see getOrCreateRuntime), so it is authoritative.
      // When we cannot resolve a thread id (or a runtime has no recorded
      // binding) we fall back to the original GLOBAL behaviour to stay safe
      // for single-thread / legacy use.
      const hasRunningWorkflow = Array.from(toolRuntimesRef.current.entries())
        .some(([runningToolId, runtime]) => {
          if (!runtime.isRunning() || runtime.isAwaitingInput()) {
            return false;
          }
          if (!turnOriginatingThreadId) {
            return true; // No thread context — preserve legacy global gate.
          }
          const runtimeThreadId =
            runtimeThreadIdByToolRef.current.get(runningToolId) ?? null;
          if (runtimeThreadId === null) {
            // Un-isolated/legacy runtime — treat as same-thread so we never
            // lose the original guard.
            return true;
          }
          return runtimeThreadId === turnOriginatingThreadId;
        });

      // Round J (A2, null-thread gate skip): when a running workflow blocks
      // the orchestrator AND no originating thread could be resolved (the
      // helpers bundle is the authoritative source — both send envelopes now
      // always pass it on thread surfaces), the old code silently skipped the
      // orchestrator block, fell through to pattern matching, matched
      // nothing, and returned false with zero output. Fail LOUDLY instead:
      // the caller's send envelope surfaces the retryable error bubble.
      if (!isFromButton && hasRunningWorkflow && !turnOriginatingThreadId) {
        console.error(
          '[useGlobalTools] NULL_THREAD_GATE: running workflow blocked a send with no resolvable originating thread — returning unhandled so the send envelope surfaces a retryable error instead of a silent skip',
        );
        return false;
      }

      // Resolve migration status up-front so we can decide whether an attachment
      // turn should be server-owned (skip the browser pre-read) BEFORE the
      // pre-orchestrator read block below. The migrated branch later recomputes
      // its own gate; these locals are scoped to this decision only.
      const serverOwnedAttachmentAccountId = resolveAccountIdFromAuth();
      // Resolve the member's canonical account before gating (see the send-path
      // gate below): a non-owner member's raw uid is not a migrated account, so
      // the raw-uid gate would wrongly treat their attachment turn as legacy.
      const serverOwnedAttachmentGate = serverOwnedAttachmentAccountId
        ? await shouldUsePrest0nRuntimeForUser(serverOwnedAttachmentAccountId)
        : { isMigrated: false, accountId: null, reason: 'No authenticated account ID' };
      const serverOwnedAttachmentTurn = shouldUseServerOwnedAttachmentTurn({
        isMigrated: serverOwnedAttachmentGate.isMigrated,
        hasAccountId: Boolean(serverOwnedAttachmentAccountId),
        attachments: message.attachments,
      });

      // Track orchestrator errors to show only if pattern matching also fails
      let orchestratorError: Error | undefined;

      // Run orchestrator if NOT from button AND no workflow is running
      if (!isFromButton && !hasRunningWorkflow) {
        // Create abort controller with automatic timeout
        // Thread-isolation: store under the originating thread id so a
        // send on thread B never aborts an in-flight request on thread A.
        const abortController = new AbortController();
        const originatingThreadId = resolveOriginatingThreadId(helpers);
        setOrchestratorAbortController(originatingThreadId, abortController);

        // ========================================================================
        // Pre-Orchestrator Document Analysis (NEW)
        // ========================================================================
        // When attachments are present, analyze them FIRST before orchestrator runs.
        // This ensures:
        // 1. Document analysis happens exactly once (not redundantly in orchestrator + workflows)
        // 2. Analysis is cached in sharedContext.documentContent for all downstream use
        // 3. Orchestrator sees the analysis in context and can make informed routing decisions
        // ========================================================================

        // Store pre-analyzed document content to pass directly to orchestrator
        // (bypasses React state timing issues with sharedContext)
        let preAnalyzedDocumentContent: string | undefined;

        if (!serverOwnedAttachmentTurn && message.attachments?.length && hasAnalyzableFiles(message.attachments)) {
          console.log('[GlobalTools] Pre-orchestrator: Analyzing attached documents');

          // 2026-05-06: Fresh attachments always force-refresh document analysis.
          // Without this, same-thread repeated cases reuse stale cached content
          // from a prior packet (FH-CLASSIFIER-005 / Task 3).
          // Clear stale manifest + translatable content tied to prior packet too.
          if (sharedContext.documentManifest) {
            sharedContext.setDocumentManifest?.(null);
            if (sharedContext.documentManifestRef) {
              sharedContext.documentManifestRef.current = null;
            }
          }
          if (sharedContext.lastTranslatableContentRef?.current?.source === 'document_analysis') {
            sharedContext.lastTranslatableContentRef.current = null;
          }

          // Show "reading attachments" indicator
          const { index: attachmentReadIndex, id: attachmentReadId } = helpers.streaming.startStreamingMessage({
            text: 'reading attachments...',
            html: getPendingWidgetHtml('reading attachments'),
            sourceTag: 'pre_orchestrator_attachment_read_pending',
          });

          // ────────────────────────────────────────────────────────────────
          // Hard outer-budget for pre-orchestrator document analysis.
          // Fernando fax-freeze (2026-05-19): scanned-fax PDFs fall through
          // pdf.js (< 500 chars extracted) into the Opus-4.7 vision path,
          // which has only an internal 5-min stream timeout and no outer
          // budget — leaving the UI on "reading attachments..." for several
          // minutes. We cap the pre-orchestrator wait at 90 s and abort the
          // underlying fetch on timeout; the existing catch handler below
          // then emits the file-listing fallback so the conversation can
          // proceed without the user perceiving a freeze.
          // We use a DEDICATED abort controller for this slice so a doc
          // timeout does not poison the subsequent orchestrator request,
          // and we forward parent aborts in case the user cancels the turn.
          // ────────────────────────────────────────────────────────────────
          const ENABLE_DOC_WAVE_PRE_ORCH =
            String(process.env.REACT_APP_ENABLE_DOC_WAVE_PRE_ORCH || '1').trim() !== '0';
          // Wave-based budgeting replaces the old single 120s sprint timeout.
          // This prevents one heavy scan from starving the entire attachment batch.
          const PRE_ORCH_DOC_WAVE_TOKEN_BUDGET = 2400;
          const PRE_ORCH_DOC_PER_FILE_TIMEOUT_MS = 45 * 1000;
          const docAbortController = new AbortController();
          const forwardParentAbort = () => {
            try { docAbortController.abort(); } catch { /* noop */ }
          };
          if (abortController.signal.aborted) {
            forwardParentAbort();
          } else {
            abortController.signal.addEventListener('abort', forwardParentAbort, { once: true });
          }

          try {
            // Run document analysis FIRST (before orchestrator).
            // forceRefresh: true ensures fingerprint mismatch or same-thread
            // packet change triggers fresh analysis, not stale cache reuse.
            const docReadPromise = ENABLE_DOC_WAVE_PRE_ORCH
              ? readDocumentsInWaves({
                  files: message.attachments,
                  query: message.text,
                  signal: docAbortController.signal,
                  sharedContext,
                  recordTransaction,
                  forceRefresh: true,
                  source: 'pre-orchestrator',
                  waveTokenBudget: PRE_ORCH_DOC_WAVE_TOKEN_BUDGET,
                  perFileTimeoutMs: PRE_ORCH_DOC_PER_FILE_TIMEOUT_MS,
                  onProgress: ({ processed, total }) => {
                    const text = `processing ${processed} of ${total}...`;
                    if (helpers.streaming.updateStreamingMessageById) {
                      helpers.streaming.updateStreamingMessageById(attachmentReadId, {
                        text,
                        html: getPendingWidgetHtml(text),
                      });
                    } else {
                      helpers.streaming.updateStreamingMessage(attachmentReadIndex, {
                        text,
                        html: getPendingWidgetHtml(text),
                      });
                    }
                  },
                })
              : readDocuments({
                  files: message.attachments,
                  query: message.text,
                  signal: docAbortController.signal,
                  sharedContext,
                  recordTransaction,
                  forceRefresh: true,
                  source: 'pre-orchestrator',
                  // A SCANNED document renders every page and then makes a
                  // vision call per batch of four — minutes of work that used
                  // to sit behind a motionless "reading attachment" spinner
                  // (2026-08-21). Say where it is.
                  onReadProgress: (info) => {
                    if (!info?.total) return;
                    const text = info.phase === 'rendering'
                      ? `reading attachment \u00b7 page ${info.done} of ${info.total}`
                      : `transcribing the scan \u00b7 page ${info.done} of ${info.total}`;
                    try {
                      if (helpers.streaming.updateStreamingMessageById) {
                        helpers.streaming.updateStreamingMessageById(attachmentReadId, {
                          text, html: getPendingWidgetHtml(text),
                        });
                      } else {
                        helpers.streaming.updateStreamingMessage(attachmentReadIndex, {
                          text, html: getPendingWidgetHtml(text),
                        });
                      }
                      lastStreamUpdateRef.current = Date.now();
                    } catch { /* a progress paint must never break the read */ }
                  },
                });
            const analysisResult = await docReadPromise;

            // Show "X attachment(s) read" notification (same UX as current)
            if (analysisResult.analysis && !analysisResult.fromCache) {
              // Create StreamingHandle compatible object - prefer ID-based operations
              const streamHandle = {
                index: attachmentReadIndex,
                id: attachmentReadId,
                finalize: (final: { text?: string; html?: string; sourceTag?: string; tokenUsage?: TokenUsageMetadata }) =>
                  helpers.streaming.finalizeStreamingMessageById
                    ? helpers.streaming.finalizeStreamingMessageById(attachmentReadId, final)
                    : helpers.streaming.finalizeStreamingMessage(attachmentReadIndex, final),
                update: (update: { text?: string; html?: string }) =>
                  helpers.streaming.updateStreamingMessageById
                    ? helpers.streaming.updateStreamingMessageById(attachmentReadId, update)
                    : helpers.streaming.updateStreamingMessage(attachmentReadIndex, update),
                abort: () =>
                  helpers.streaming.removeMessageById
                    ? helpers.streaming.removeMessageById(attachmentReadId)
                    : helpers.streaming.removeMessage(attachmentReadIndex),
              };

              finalizeAttachmentReadComplete(
                streamHandle,
                {
                  analysis: analysisResult.analysis,
                  filesAnalyzed: analysisResult.filesAnalyzed,
                  skippedFiles: analysisResult.skippedFiles,
                  // tokenUsage omitted - readDocuments() already recorded the transaction
                },
                recordTransaction,
                {
                  sourceTag: 'pre_orchestrator_attachment_read_complete',
                }
              );

              // Push to pending accumulator for display on workflow's first message
              if (analysisResult.tokenUsage) {
                sharedContext.pushPendingTokenUsage({
                  ...analysisResult.tokenUsage,
                  summary: 'document analysis',
                });
              }
            } else {
              // Cache hit - just remove the pending indicator
              if (helpers.streaming.removeMessageById) {
                helpers.streaming.removeMessageById(attachmentReadId);
              } else {
                helpers.streaming.removeMessage(attachmentReadIndex);
              }
              console.log('[GlobalTools] Pre-orchestrator: Using cached document analysis');
            }

            // Store analysis result to pass directly to orchestrator (bypasses React state timing)
            if (analysisResult.analysis) {
              preAnalyzedDocumentContent = analysisResult.analysis;
              console.log('[GlobalTools] Pre-orchestrator: Document analysis complete');
              console.log('[GlobalTools] Pre-orchestrator: preAnalyzedDocumentContent is SET (' +
                preAnalyzedDocumentContent.substring(0, 50) + '...)');
              console.log('[GlobalTools] Pre-orchestrator: sharedContext.documentContent is',
                sharedContext.documentContent ? 'SET' : 'EMPTY (expected - React state timing)');

              // Also set lastTranslatableContent so subsequent workflows can access it
              // This enables "translate to spanish" to work after document analysis
              const translatableContent = {
                content: preAnalyzedDocumentContent,
                title: 'Document Transcription',
                timestamp: new Date(),
                source: 'document_analysis' as const,
              };
              sharedContext.setLastTranslatableContent(translatableContent);
              if (sharedContext.lastTranslatableContentRef) {
                sharedContext.lastTranslatableContentRef.current = translatableContent;
              }
              console.log('[GlobalTools] Pre-orchestrator: Set lastTranslatableContent for workflow chaining');
            }
          } catch (error) {
            const errMsg = error instanceof Error ? error.message : String(error);
            const isTimeout = /timeout/i.test(errMsg);
            console.error(
              `[GlobalTools] Pre-orchestrator document analysis failed${isTimeout ? ' (TIMEOUT)' : ''}:`,
              error
            );
            if (helpers.streaming.removeMessageById) {
              helpers.streaming.removeMessageById(attachmentReadId);
            } else {
              helpers.streaming.removeMessage(attachmentReadIndex);
            }
            // Analysis failed — build a fallback document listing so the VM at least
            // knows which files were attached. Without this, the VM gets no context and
            // tells the user no documents were uploaded.
            if (message.attachments?.length) {
              const fallbackLines = message.attachments.map((f: File) =>
                `- ${f.name} (${f.type || 'unknown type'}, ${Math.round(f.size / 1024)}KB)`
              ).join('\n');
              const header = isTimeout
                ? `[Document analysis timed out while processing attachment waves — ${message.attachments.length} file${message.attachments.length !== 1 ? 's' : ''} attached but could not be read in time. File list below for reference:]`
                : `[Document analysis failed — ${message.attachments.length} file${message.attachments.length !== 1 ? 's' : ''} were attached but could not be read. File list below for reference:]`;
              preAnalyzedDocumentContent = [
                header,
                fallbackLines,
                `[The user has uploaded these files. Please acknowledge them and proceed based on the file names and the user's message.]`,
              ].join('\n');
              console.warn('[GlobalTools] Pre-orchestrator: using fallback document listing due to analysis failure');
            }
          } finally {
            try {
              abortController.signal.removeEventListener('abort', forwardParentAbort);
            } catch { /* noop */ }
          }
        }

        // ========================================================================
        // Orchestrator Phase
        // ========================================================================

        // Show "thinking" indicator while orchestrator analyzes
        // This will stay visible until orchestrator creates streaming widget on first chunk
        const { index: thinkingIndicatorIndex, id: thinkingIndicatorId } = helpers.streaming.startStreamingMessage({
          text: 'thinking...',
          html: getPendingWidgetHtml('thinking'),
          sourceTag: 'stage1_assessment_pending',
        });

        // Round O: the thinking indicator's active-stream registration moved
        // BELOW the activePending tracking block (search for
        // `turnActiveStreamEntry`) so its finalize closure can follow
        // onToolStart widget rotations. Nothing can cancel in between — the
        // code from here to that registration is fully synchronous.

        let thinkingTimedOut = false;

        // ───────────────────────────────────────────────────────────────
        // Progress hints (webchat-streaming-keepalive, 2026-05-14; folded
        // into the single pending widget 2026-08-08)
        // ───────────────────────────────────────────────────────────────
        // Long multi-stage turns can legitimately take minutes. Without
        // progress feedback the user stares at a static "thinking..."
        // spinner and assumes the chat is dead — the silent-failure mode
        // Fernando hit on task_mp5nu0ml_4uo9xmn.
        //
        // The 45s/180s reassurance used to render as EXTRA spinner bubbles
        // stacked under the pending widget (with hard-coded Deep-Research /
        // "Spanish-language" copy that was wrong for most turns — 2026-08-08
        // report: three simultaneous indicators on one English query). One
        // turn owns ONE indicator: the hints now update the active pending
        // widget's label IN PLACE and are naturally superseded by the next
        // real progress signal (server stage label, elapsed ticker, tool
        // start, first text chunk). Suppressed while the session boot wait
        // owns the widget label — boot status is already progress feedback.
        const applyProgressHint = (label: string) => {
          try {
            if (activePendingRemoved || sessionBootWaitActive) return;
            if (isServerBackedTurn) {
              // Only upgrade GENERIC copy ('still working…') — a real stage
              // label ("Step 2 of 3: deep researching…") is strictly more
              // informative than any hint and must never be clobbered.
              if (!/^still\s+working\b/i.test(latestServerBackedStageLabel.trim())) return;
              latestServerBackedStageLabel = label;
              refreshServerBackedProgressLabel();
            } else {
              updateActivePendingWidget(label);
            }
          } catch (err) {
            console.warn('[GlobalTools] Failed to apply progress hint', err);
          }
        };

        const hintTimeoutIds: ReturnType<typeof setTimeout>[] = [];
        hintTimeoutIds.push(
          setTimeout(() => applyProgressHint(PROGRESS_HINT_FIRST_LABEL), PROGRESS_HINT_FIRST_MS),
        );
        hintTimeoutIds.push(
          setTimeout(() => applyProgressHint(PROGRESS_HINT_SECOND_LABEL), PROGRESS_HINT_SECOND_MS),
        );

        const clearProgressHintTimers = () => {
          for (const t of hintTimeoutIds) {
            try { clearTimeout(t); } catch { /* ignore */ }
          }
          hintTimeoutIds.length = 0;
        };

        // Inactivity-policed ceiling (2026-08-13): fires only when the stream
        // has ALSO been silent for ORCHESTRATOR_ACTIVITY_GRACE_MS. A live
        // long-document run (a 110-page translation is 10-25 minutes of
        // continuous section streaming) re-arms the timer instead of dying at
        // 8:00 mid-stream, shot by its own watchdog.
        let thinkingTimeoutId!: ReturnType<typeof setTimeout>;
        const armThinkingCeiling = (delayMs: number) => {
          thinkingTimeoutId = setTimeout(() => {
            // A TOOL STILL OWNS THIS TURN (2026-08-15, fourth abort of the
            // 200-page manual).
            //
            // Three heartbeat implementations in a row changed nothing: the
            // run died at the same ~13 minutes every time. Whatever the reason
            // the heartbeat does not arrive — a second hook instance, a stale
            // sharedContext captured at runtime construction — chasing it
            // across module boundaries has cost four test cycles and produced
            // four wrong diagnoses.
            //
            // This check needs no plumbing at all. toolRuntimesRef lives in
            // THIS hook, beside the ceiling and beside lastStreamUpdateRef. A
            // runtime reporting isRunning() has not returned from execute(),
            // which means the turn is not dead by definition — it is being
            // worked on.
            //
            // The ceiling exists to catch a turn nothing owns. A running tool
            // carries its own guards, and they are the appropriate ones: a 60s
            // per-chunk timeout on every stream read, and
            // TRANSLATION_TOTAL_TIMEOUT_MS (15 min) on the whole translation.
            // Those fail with a message describing what actually happened,
            // which is strictly better than this one's guess.
            // isRunning() is also true for a tool PARKED on awaiting-input, which
            // is a turn nobody is working on — deferring for that would disable
            // the ceiling entirely. Executing means running AND not waiting.
            const toolStillRunning = Array.from(toolRuntimesRef.current.values())
              .some((runtime) => {
                try { return runtime.isRunning() && !runtime.isAwaitingInput(); } catch { return false; }
              });
            if (toolStillRunning) {
              console.log('[GlobalTools] Ceiling fired but a tool runtime is still executing — deferring to its own timeouts');
              armThinkingCeiling(ORCHESTRATOR_ACTIVITY_GRACE_MS);
              return;
            }
            const sinceActivity = Date.now() - lastStreamUpdateRef.current;
            if (sinceActivity < ORCHESTRATOR_ACTIVITY_GRACE_MS) {
              armThinkingCeiling(
                Math.max(ORCHESTRATOR_ACTIVITY_GRACE_MS - sinceActivity, 30_000),
              );
              return;
            }
            console.warn(`[GlobalTools] Orchestrator thinking timeout after ${ORCHESTRATOR_THINKING_TIMEOUT_MS}ms ceiling + ${Math.round(sinceActivity / 1000)}s of stream silence - aborting`);
            thinkingTimedOut = true;
            clearProgressHintTimers();
            // User-facing message so the panel never goes silent at the
            // ceiling. R14 (2026-08-13): the old copy promised "your work is
            // preserved", which is only true for turns that reached
            // server-side finalize — an inline SSE turn reaped at this
            // ceiling has persisted NOTHING (the 04:52 datacenter-hvac repro:
            // streamed text vanished, "reply could no longer be found"). Be
            // honest and actionable instead of reassuring and wrong.
            try {
              helpers.addSystemMessage({
                text:
                  "This turn took longer than expected and had to stop — it may "
                  + "not have finished. Tap Regenerate (or resend your request) "
                  + "to run it again.",
                sourceTag: 'webchat_streaming_keepalive_timeout',
              });
            } catch (err) {
              console.warn('[GlobalTools] Failed to surface streaming-timeout message', err);
            }
            abortController.abort();
          }, delayMs);
        };
        armThinkingCeiling(ORCHESTRATOR_THINKING_TIMEOUT_MS);

        // Reset dormancy baseline so the timer starts fresh for this orchestrator call
        // (prevents false positive if component was mounted long ago)
        lastStreamUpdateRef.current = Date.now();

        // ───────────────────────────────────────────────────────────────
        // Active pending widget tracking (BUG-T1-DEAD-THINKING-DOT, 2026-05-19)
        // ───────────────────────────────────────────────────────────────
        // The visible "pending" widget rotates during a turn:
        //   1) initial "thinking..." dot (thinkingIndicatorId)
        //   2) when the backend emits SSE `tool_start` for deep-research
        //      (etc), onToolStart creates a NEW pending widget bubble
        //   3) subsequent SSE `thinking` events (the deep-research stage
        //      labels "Step 1 of 3: clarifying questions…" / "Step 2 of 3:
        //      deep researching the web…" / "Step 3 of 3: generating
        //      downloadable memo …") must update WHICHEVER pending widget
        //      is currently on screen — not the long-since-removed
        //      original thinking dot.
        //
        // Pre-fix: onToolStart removed thinkingIndicatorId but the
        // replacement widget's id was discarded. onThinking kept trying
        // to update the dead id and silently failed (reducer logged
        // "UPDATE_MESSAGE_BY_ID: Message not found"). User stared at a
        // frozen "Working..." widget for ~5 minutes with no stage
        // feedback — Fernando QA 2026-05-19 14:11-14:22 UTC, Slack
        // C0AM8U804KZ/1778281880.499349.
        //
        // Post-fix: activePendingId/Index rotate on each onToolStart and
        // are the target of onThinking updates AND removeThinkingIndicator.
        let activePendingId: string = thinkingIndicatorId;
        let activePendingIndex: number = thinkingIndicatorIndex;
        let activePendingRemoved = false;
        // BUG-DR-CLARIFYING-CARD-MISSING (2026-05-20) — one-shot guard so we
        // only finalize the pending widget into a clarifying-questions card
        // ONCE per turn. The polling loop fires onTaskStatus every ~2s with
        // the same currentStage payload until the next stage transitions;
        // without this guard we'd re-finalize on every poll and the chat
        // would accumulate duplicate cards.
        let clarifyingCardFinalized = false;
        // BUG-DR-STAGE1-UNBILLED (2026-07-13) — the clarifying card is
        // finalized MID-TASK from the stage payload (above), before the task
        // doc's result.usage exists, so the card's ⓘ popup showed "no
        // extensive compute was necessary" while the backend debited the
        // wallet for the real Stage-1 model call. Stash the card's message
        // id + finalize payload here so onDone can re-finalize the SAME
        // message (race-immune ById path — the same pattern
        // agenticWorkflows/handlers.ts uses for the report finalize) with
        // the turn's tokenUsage/tokenUsages once the done event delivers
        // them from the task doc's result.usage.
        let clarifyingCardMessageId: string | null = null;
        let clarifyingCardFinalizePayload: {
          text: string;
          html: string;
          workflowActionButtonsHtml: string;
          sourceTag: string;
        } | null = null;

        let isServerBackedTurn = false;
        let serverBackedTurnStartedAt = 0;
        let latestServerBackedStageLabel = 'still working';
        // True while the cold-boot session wait owns the pending widget's
        // label (see the boot loop in the migrated lane). The 45s/180s
        // progress hints defer to it — boot status IS the progress feedback.
        let sessionBootWaitActive = false;
        let serverBackedProgressIntervalId: ReturnType<typeof setInterval> | null = null;
        // Round K (duplicate-answer convergence, 2026-07): the turn's durable
        // task id (see the retained comment block above `startStreamMessage`
        // in the migrated branch for the full contract). HOISTED here
        // (2026-07-31 frozen-clock/stop fix) from the migrated IIFE so the
        // post-await cleanup can tell a still-live async 202 turn (task id
        // stamped, pending widget alive) from a synchronously finished one.
        // Legacy-lane turns never set it.
        let turnServerTaskId: string | null = null;
        // Parallel branches (PLAN-prest0n-parallel-branches-20260908): the
        // freshest ledger from the polled task doc — the pill's html gains a
        // row per branch while any exist. Empty on every ordinary turn.
        let latestBranches: BranchLaneEntry[] = [];

        // ⓘ SERIES LINEUP (2026-08-29): a fresh turn's cards start clean —
        // the previous turn's usage must never stamp onto this turn's cards,
        // and (epoch fence) a previous turn's LATE onDone must never stamp
        // onto this turn's either.
        turnCardMessageIdsRef.current.clear();
        turnCardUsageRef.current = null;
        turnCardEpochRef.current += 1;
        const cardStampEpoch = turnCardEpochRef.current;

        const stopServerBackedProgressTicker = () => {
          if (serverBackedProgressIntervalId !== null) {
            clearInterval(serverBackedProgressIntervalId);
            serverBackedProgressIntervalId = null;
          }
        };

        const updateActivePendingWidget = (label: string) => {
          if (activePendingRemoved) return;
          const updatePayload = {
            text: label,
            html: latestBranches.length > 0
              ? getPendingWidgetWithBranchesHtml(label, latestBranches, Date.now())
              : getPendingWidgetHtml(label),
          };
          if (helpers.streaming.updateStreamingMessageById) {
            helpers.streaming.updateStreamingMessageById(activePendingId, updatePayload);
          } else if (typeof activePendingIndex === 'number' && activePendingIndex >= 0) {
            helpers.streaming.updateStreamingMessage(activePendingIndex, updatePayload);
          }
        };

        // PR-3B: the merge line marks the MOMENT the last branch lands — shown
        // ONCE; the server's next stage label overwrites it.
        let branchMergeLineShown = false;
        const showBranchMergeLineOnce = () => {
          if (branchMergeLineShown) return;
          const mergeLine = branchMergeLine(latestBranches);
          if (!mergeLine) return;
          branchMergeLineShown = true;
          updateActivePendingWidget(mergeLine);
        };

        /**
         * ONE CLOCK (Miles, 2026-09-21 — the screenshot showed 2:29 on the pill
         * beside 0:27 on the card for the SAME turn).
         *
         * The card counts from the server's `startedAtMs`, which PR-F1 made the
         * task's own mint stamp. This ticker started counting at `Date.now()`
         * when the send left the browser, which is a different instant — and
         * the gap is every millisecond of network, queueing and intake.
         *
         * The moment the durable task id lands, its mint stamp IS the number
         * the card is showing, so the pill adopts it and the two agree. Before
         * the id arrives there is nothing to adopt and the send instant stands,
         * which is what the pill has always used.
         */
        const serverBackedElapsedBase = (): number => {
          const minted = taskMintTimestampMs(turnServerTaskId);
          return minted && minted > 0 ? minted : serverBackedTurnStartedAt;
        };

        const refreshServerBackedProgressLabel = () => {
          if (!isServerBackedTurn) return;
          if (activePendingRemoved) return;
          const elapsedMs = Date.now() - serverBackedElapsedBase();
          const progressLabel = buildServerBackedProgressLabel(latestServerBackedStageLabel, elapsedMs);
          updateActivePendingWidget(progressLabel);
        };

        const startServerBackedProgressTicker = (initialStageLabel?: string) => {
          isServerBackedTurn = true;
          serverBackedTurnStartedAt = Date.now();
          latestServerBackedStageLabel = (initialStageLabel || 'still working').trim() || 'still working';
          refreshServerBackedProgressLabel();
          stopServerBackedProgressTicker();
          serverBackedProgressIntervalId = setInterval(() => {
            refreshServerBackedProgressLabel();
          }, 1000);
        };

        const removeActivePending = () => {
          if (activePendingRemoved) return;
          stopServerBackedProgressTicker();
          try {
            if (helpers.streaming.removeMessageById) {
              helpers.streaming.removeMessageById(activePendingId);
            } else if (typeof activePendingIndex === 'number' && activePendingIndex >= 0) {
              helpers.streaming.removeMessage(activePendingIndex);
            }
          } catch {
            // Already gone — non-fatal.
          }
          activePendingRemoved = true;
        };

        removeThinkingIndicator = () => {
          // When the spinner is removed by the streaming pipeline (first text
          // chunk, tool start, etc.), also cancel the pending hint timers —
          // they were only meant to bridge the silence before real output.
          clearProgressHintTimers();
          removeActivePending();
        };

        // ────────────────────────────────────────────────────────────────
        // Round O (false-"stopped" root fix, 2026-07-15): register THIS
        // turn's stream in the per-thread registry, keyed by the turn's
        // ORIGINATING thread (the explicit turn-scoped value Round J
        // introduced) — never the shared last-writer-wins ref. STEP 1 of
        // cancelRunningTool finalizes ONLY the cancelled thread's entry, so
        // archiving thread B can no longer convert thread A's live thinking
        // widget into a static "stopped" bubble while A's server task keeps
        // running (the false-stopped + minutes-later-answer symptom).
        //
        // The finalize closure resolves the CURRENT active pending widget at
        // call time (`activePendingId` rotates on onToolStart), so a Stop
        // mid-tool-phase finalizes the widget the user actually sees, and it
        // no-ops once the pending widget is gone (streaming text/tool output
        // replaced it — nothing left to convert to "stopped").
        // ────────────────────────────────────────────────────────────────
        let turnActiveStreamEntry: ActiveStreamEntry | null = activeStreamRegistryRef.current.register({
          threadId: turnOriginatingThreadId,
          messageId: thinkingIndicatorId,
          finalize: (content) => {
            if (activePendingRemoved) return;
            activePendingRemoved = true;
            stopServerBackedProgressTicker();
            if (helpers.streaming.finalizeStreamingMessageById) {
              helpers.streaming.finalizeStreamingMessageById(activePendingId, content);
            } else if (typeof activePendingIndex === 'number' && activePendingIndex >= 0) {
              helpers.streaming.finalizeStreamingMessage(activePendingIndex, content);
            }
          },
        });
        // Lifecycle hygiene (Round O): entry-scoped clear so a stale entry
        // can never be finalized after the turn ends. Compare-and-delete —
        // if a NEWER turn re-registered this thread's slot, this is a no-op.
        const clearTurnActiveStream = () => {
          if (!turnActiveStreamEntry) return;
          activeStreamRegistryRef.current.clear(turnOriginatingThreadId, turnActiveStreamEntry);
          turnActiveStreamEntry = null;
        };

        // ────────────────────────────────────────────────────────────────
        // Stop fix (2026-07-31): the thread's AbortController used to be
        // cleared at the post-await cleanup — which on the async 202 lane
        // runs ~2-3s after dispatch, while the background poll keeps
        // running. Stop then found no controller: the poll survived, the
        // server task survived, and the "stopped" turn painted its answer
        // minutes later. Release the controller ONLY at the turn's true
        // terminal (onDone / non-recoverable onError / the send-failure
        // branches / the sync post-await cleanup). Identity-scoped exactly
        // like clearOrchestratorAbortController, so a stale release can
        // never remove a NEWER turn's controller. Also drops this turn's
        // live server-task registration (compare-and-delete) so a later
        // Stop can't fire a server cancel at an already-finished task.
        // ────────────────────────────────────────────────────────────────
        const releaseTurnAbortController = () => {
          clearOrchestratorAbortController(originatingThreadId, abortController);
          if (turnServerTaskId) {
            const storedTaskId = liveServerTaskByThreadRef.current.get(originatingThreadId ?? null);
            if (storedTaskId === turnServerTaskId) {
              liveServerTaskByThreadRef.current.delete(originatingThreadId ?? null);
            }
          }
        };

        // MT-QA-BUG-009 (2026-05-08): publish this turn's thinking remover so
        // tool runtimes can clear the spinner when they yield the turn back to
        // the user via an awaiting-input event (see getOrCreateRuntime).
        pendingThinkingIndicatorRemoveRef.current = removeThinkingIndicator;

        // Mark orchestrator as active (suppresses dormancy detection during API calls)
        isOrchestratorActiveRef.current = true;

        try {
          // (D13-6a: the per-turn client-side connected-apps fetch was
          // removed with its only reader, the severed browser orchestrator.
          // The migrated lane gets connected apps SERVER-side — the backend
          // accountResolver feeds them into the prompt.)

          // Persistent thinking indicator: keep the spinner visible through routing,
          // tool handoff, backend thinking, and progress setup. It is removed only
          // when a user-visible response/progress/error has actually been created.
          const onStreamingStart = () => {
            console.debug('[GlobalTools] persistent thinking: streaming/progress started; spinner stays until visible output is ready');
          };

          const accountId = resolveAccountIdFromAuth();
          // Gate on the member's CANONICAL account, not the raw auth UID. For a
          // NON-OWNER member, resolveAccountIdFromAuth() returns their own uid,
          // which has no uid-keyed migrated account — so shouldUsePrest0nRuntime(uid)
          // fails Gate 1 ("No runtime config document") and returns
          // isMigrated=false, sending the turn down the legacy browser
          // orchestrator (below). That legacy path NEVER calls prest0nRunTurn, so
          // the member's group-thread turns are never mirrored to the shared
          // store and the other participants never see them (owner→member worked;
          // member→owner did not). shouldUsePrest0nRuntimeForUser resolves the
          // account_membership back-pointer to the workspace account FIRST —
          // exactly as session establishment does in useGlobalChat
          // (shouldUsePrest0nRuntimeForUser at mount) — so a member now resolves to
          // the same migrated workspace as the owner and takes the prest0n-session
          // path. For an owner (uid === accountId) the result is unchanged.
          const migrationGate = accountId
            ? await shouldUsePrest0nRuntimeForUser(accountId)
            : { isMigrated: false, accountId: null, reason: 'No authenticated account ID' };

          const isStrict = isFernandoStrictMode(accountId) || isFernandoStrictMode(migrationGate.accountId);
          fernandoTrace(
            'MSG_HANDLER_MIGRATION_GATE',
            {
              accountId,
              isMigrated: migrationGate.isMigrated,
              reason: migrationGate.reason,
              branch: accountId ? 'prest0n_session' : 'legacy_orchestrator',
              code: (!migrationGate.isMigrated && isStrict) ? FERN_FE_CODES.LEGACY_FALLBACK : undefined,
            },
            isStrict
          );

          // ══════════════════════════════════════════════════════════════
          // NO LEGACY REGRESSION FOR SIGNED-IN ACCOUNTS (owner directive,
          // 2026-08-21): the in-browser single-model orchestrator is NOT an
          // acceptable lane for ANY authenticated account — a failed
          // migration-gate read is an identity blip (token refresh, cold
          // incognito boot), never a routing verdict. Re-resolve briefly;
          // if the gate still fails, fail LOUDLY with the turn unsent.
          // Silent one-model degradation hid the fernando-hidalgo outage
          // for a day, and the identical failure shape was hotfixed once
          // before (users/*/account_membership fixReason,
          // rbac-hotfix-20260423). The no-auth-uid branch below no longer
          // runs ANY brain (D13-6a): it surfaces the retryable card for the
          // teardown-race shape and nothing else.
          // ══════════════════════════════════════════════════════════════
          let effectiveGate = migrationGate;
          if (accountId && !effectiveGate.isMigrated) {
            for (const retryDelayMs of [1_000, 3_000]) {
              await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
              try {
                effectiveGate = await shouldUsePrest0nRuntimeForUser(accountId);
              } catch { /* keep the last gate result — the loop decides */ }
              if (effectiveGate.isMigrated) break;
            }
            fernandoTrace(
              'MSG_HANDLER_GATE_RETRY',
              {
                accountId,
                recovered: effectiveGate.isMigrated,
                reason: effectiveGate.reason,
              },
              isStrict
            );
          }

          // ============================================================
          // ORCHESTRATOR-AUTHORITATIVE ROUTING (2026-05-10)
          // ============================================================
          // Pre-compute the regex tool hint so the migrated prest0n-session
          // branch can thread it into pageContext.contextHint as
          // informational text (the legacy browser branch is severed —
          // D13-6a). The LLM
          // is the sole routing authority — regex matches never pre-empt.
          // ============================================================
          const turnTopRegexMatch = matchToolTrigger(message.text);
          const turnTopRoutingHint = ORCHESTRATOR_AUTHORITATIVE_ROUTING && turnTopRegexMatch
            ? buildRoutingTriggerHint(turnTopRegexMatch.tool.id)
            : undefined;
          // Loose translation-intent observation (2026-08-24; formerly the
          // Track C pre-empt): translate-intent anywhere in the message —
          // resolved across a conversational lead-in — plus a PDF/DOCX
          // attachment. Under the master rule (Primacy Audit D2) this is
          // EVIDENCE for the primary, never a route: the hint carries the
          // observation and the full-document deliverable contract.
          const turnHasPdfOrDocx = Array.isArray(message.attachments) && message.attachments.some((f: File) => {
            if (!f) return false;
            const name = (f.name || '').toLowerCase();
            const mime = (f.type || '').toLowerCase();
            return name.endsWith('.pdf') || name.endsWith('.docx') || name.endsWith('.doc') ||
              mime.includes('pdf') || mime.includes('wordprocessingml') || mime === 'application/msword';
          });
          const turnLooseTranslate = turnHasPdfOrDocx
            ? resolveTranslationIntentWithHistory(message.text, messageHistoryRef.current)
            : null;
          const turnTranslateHint = turnLooseTranslate?.isTranslate
            ? buildTranslateIntentHint(turnLooseTranslate)
            : undefined;
          if (turnTranslateHint) {
            fernandoTrace(
              'TRANSLATE_INTENT_HINT',
              {
                accountId,
                targetLanguage: turnLooseTranslate?.targetLanguage,
                attachmentCount: message.attachments?.length || 0,
                carriedFromHistory: turnLooseTranslate?.carriedFromHistory,
              },
              isStrict
            );
          }
          // D8 tail: the pending-question hint rides the same evidence wire.
          const turnPendingQuestionHint = ambiguousPendingWorkflow
            ? buildPendingQuestionHint({
                toolId: ambiguousPendingWorkflow.toolId,
                ambiguousMatched: ambiguousPendingWorkflow.matched ?? null,
              })
            : undefined;
          const turnTopHints = [turnTopRoutingHint, turnTranslateHint, turnPendingQuestionHint].filter(Boolean);
          const turnTopAugmentedPageCtx = turnTopHints.length
            ? {
                pathname: pageContextRef.current?.pathname || '/',
                pageName: pageContextRef.current?.pageName,
                summary: pageContextRef.current?.summary || '',
                contextHint: [pageContextRef.current?.contextHint, ...turnTopHints]
                  .filter(Boolean)
                  .join('\n\n'),
              }
            : pageContextRef.current;

          // ============================================================
          // Direct-connect pre-route REMOVED (2026-08-24 master rule)
          // ============================================================
          // From 2026-08-12 to 2026-08-24 an anchored "connect [app]" phrase
          // ran the connect-app tool client-side BEFORE any planner dispatch,
          // justified at the time by "only the client can post the connect
          // card". That premise is obsolete: the backend session lane authors
          // durable connect cards itself (the RingCentral tile was one), and
          // the 2026-08-24 owner ruling (Primacy Audit D1) requires the
          // primary orchestrator — with the thread, permissions, and
          // environment in view — to make the decision. Connect phrases now
          // ride sendTurn like every other message, carrying the regex match
          // as the informational contextHint above (2026-05-10 authoritative-
          // routing contract); click-time OAuth mint + popup remain client
          // execution of the primary's decision. Do NOT reintroduce a
          // client-side pre-empt here.

          const orchestratorResult = accountId
            ? await (async () => {
                // Signed-in and the gate STILL failed after retries: refuse to
                // run at partial capability. The turn stays unsent, the user
                // sees an honest retry prompt, and the failure is loud in
                // both traces — never a silent one-model reply (2026-08-21).
                if (!effectiveGate.isMigrated) {
                  console.error(
                    '[GlobalTools] migration gate failed for a signed-in account after retries — refusing legacy fallback:',
                    effectiveGate.reason,
                  );
                  removeThinkingIndicator();
                  // No on-call notice here (follow-up to #1233/#1275): this
                  // branch REFUSES to send, so the turn never reaches a
                  // server that could file anything. Every incident reporter
                  // lives under functions/src — a client-side claim is only
                  // true when the same failure also reached one of them.
                  helpers.addSystemMessage({
                    text: "I couldn't verify your workspace connection just now, so I didn't run this request at reduced capability. Please resend in a few seconds — if this keeps happening, sign out and back in.",
                    sourceTag: 'workspace_gate_unresolved',
                  });
                  return { handled: true };
                }
                // ============================================================
                // Phase 1: Prest0n Session Thin-Client Path
                // ============================================================
                // For migrated accounts, delegate to the backend session API
                // instead of running the browser-side orchestrator.
                //
                // If the session is not yet available (still loading) or the
                // API call fails, fall back to the legacy orchestrator path.
                // ============================================================

                // Wait for Prest0n session to be ready (up to 15 seconds).
                // For migrated accounts, NEVER fall back to the legacy browser orchestrator.
                let prest0nSession = prest0nSessionRef.current;

                // ── Serverless-first (2026-08-15 owner directive) ──────────
                // Chat must be ready in seconds after onboarding; the ONLY
                // thing allowed to wait on the VM is actual VM work. Sessions
                // are deterministic (S3): sendTurn re-resolves its own session
                // on every call and the SERVER creates the doc from threadId —
                // so a missing ref session is only the React wiring race,
                // never a workspace boot. When the transport is already wired,
                // synthesize the session inline and proceed with ZERO wait.
                // (The old 5-minute "starting your workspace" hold blocked a
                // fresh account's first question for 1:54+ — owner screenshot
                // — on a wait nothing actually needed.)
                if (!prest0nSession?.sendTurn) {
                  // Transport never wired — the mount-time gate is stale.
                  // Re-run it now; the bounded wait below picks up the result.
                  try { requestSessionRewireRef.current?.(); } catch { /* non-fatal */ }
                }
                if (!prest0nSession?.session && prest0nSession?.sendTurn && accountId) {
                  try {
                    const synthThreadId =
                      resolveOriginatingThreadId(helpers) || turnOriginatingThreadId || 'default';
                    const synthesized = await resumeOrCreatePrest0nSession(accountId, synthThreadId);
                    prest0nSession = { ...prest0nSession, session: synthesized };
                    prest0nSessionRef.current = prest0nSession;
                  } catch (synthErr) {
                    console.warn('[GlobalTools] inline session synthesis failed — falling back to bounded wait', synthErr);
                  }
                }

                if (!prest0nSession?.session) {
                  // Wait up to 5 minutes for new VMs (cold boot takes 3-5 min on first provision).
                  // ============================================================
                  // Cold-boot UX (FH-QA-P0-T0.1 fix, 2026-05-20; reworked 2026-08-08)
                  // ============================================================
                  // The boot wait used to post a PLAIN system message and
                  // delete/re-add it every 10s. That message (a) rendered as a
                  // normal chat bubble instead of the thinking widget, (b) hid
                  // the real pending overlay (every re-add minted a newer
                  // timestamp), (c) leaked into persisted history whenever the
                  // remove raced a thread switch/refresh — the "still going
                  // after 10 minutes" report — and (d) stacked with the hint
                  // bubbles as a third simultaneous indicator. The boot status
                  // now updates the SAME active pending widget in place — one
                  // turn, one indicator — so the normal widget lifecycle
                  // (finalize/stop/error/hydrate-reconcile) owns its cleanup,
                  // including user Stop, which the old loop ignored entirely.
                  // Serverless-first: with inline synthesis above, this path
                  // only runs while the transport (sendTurn) is not yet wired
                  // — a React-mount race measured in ticks, not a VM boot.
                  // Seconds, not minutes; on expiry the refresh guidance shows.
                  const SESSION_WAIT_MS = 20_000;
                  const SESSION_POLL_MS = 250;
                  const REASSURANCE_ROTATE_MS = 5_000;
                  const waitStart = Date.now();
                  const BOOT_REASSURANCE_LINES = [
                    'loading your legal tools…',
                    'connecting to research pipeline…',
                    'warming up document handlers…',
                    'almost there — finalizing your workspace…',
                    'still here, just a few more seconds…',
                  ];
                  const renderBootStatus = (elapsedMs: number): string => {
                    const tickIdx = Math.min(
                      BOOT_REASSURANCE_LINES.length - 1,
                      Math.floor(elapsedMs / REASSURANCE_ROTATE_MS)
                    );
                    // "<label> · m:ss" — same compact form as the server-backed
                    // progress ticker, so the widget reads consistently across
                    // boot wait and live turns.
                    return buildServerBackedProgressLabel(
                      `connecting your workspace — ${BOOT_REASSURANCE_LINES[tickIdx]}`,
                      elapsedMs,
                    );
                  };
                  sessionBootWaitActive = true;
                  try {
                    updateActivePendingWidget(renderBootStatus(0));
                    let lastWidgetUpdate = 0;
                    let lastLogAt = 0;
                    while (
                      !prest0nSession?.session
                      && !abortController.signal.aborted
                      && Date.now() - waitStart < SESSION_WAIT_MS
                    ) {
                      await new Promise(r => setTimeout(r, SESSION_POLL_MS));
                      prest0nSession = prest0nSessionRef.current;
                      // The moment the transport wires, synthesize and go —
                      // never keep a user waiting for state nothing consumes.
                      if (!prest0nSession?.session && prest0nSession?.sendTurn && accountId) {
                        try {
                          const lateThreadId =
                            resolveOriginatingThreadId(helpers) || turnOriginatingThreadId || 'default';
                          const lateSession = await resumeOrCreatePrest0nSession(accountId, lateThreadId);
                          prest0nSession = { ...prest0nSession, session: lateSession };
                          prest0nSessionRef.current = prest0nSession;
                          break;
                        } catch { /* keep polling */ }
                      }
                      const elapsed = Date.now() - waitStart;
                      if (elapsed - lastWidgetUpdate >= 1000 && !prest0nSession?.session) {
                        lastWidgetUpdate = elapsed;
                        updateActivePendingWidget(renderBootStatus(elapsed));
                      }
                      if (elapsed - lastLogAt >= 10_000) {
                        lastLogAt = elapsed;
                        const mins = Math.floor(elapsed / 60000);
                        const secs = Math.floor((elapsed % 60000) / 1000);
                        console.log(`[GlobalTools] Still waiting for Prest0n session... ${mins}m ${secs}s`);
                      }
                    }
                  } finally {
                    sessionBootWaitActive = false;
                  }
                  if (prest0nSession?.session) {
                    // Session arrived — hand the widget label back to the
                    // normal turn lifecycle.
                    updateActivePendingWidget('thinking...');
                  }
                }

                // User stopped during the boot wait: cancelRunningTool already
                // finalized the widget as "stopped" — nothing more to paint,
                // and the boot-failure error below must not fire.
                if (abortController.signal.aborted && !prest0nSession?.session) {
                  return { handled: true, error: undefined };
                }

                if (!prest0nSession?.session) {
                  console.error('[GlobalTools] Prest0n session failed to initialize for migrated account — NOT falling back to legacy orchestrator');
                  fernandoTrace(
                    'SESSION_NOT_READY_ON_SEND',
                    { accountId, code: FERN_FE_CODES.SESSION_NOT_READY_ON_SEND },
                    isStrict
                  );
                  // 2026-08-08: this terminal branch used to return handled:true
                  // with the pending widget still animating and the processing
                  // heartbeat held — a permanent spinner next to the error
                  // bubble (the post-await handled-path deliberately never
                  // removes the spinner), with the thread's clock running until
                  // the 10-min safety floor. Clean up like every other terminal
                  // error lane before surfacing the error.
                  removeThinkingIndicator();
                  releaseTurnAbortController();
                  try { releaseProcessingHeartbeatRef.current?.(originatingThreadId); }
                  catch (relErr) { console.warn('[useGlobalTools] boot-failure releaseProcessingHeartbeat threw:', relErr); }
                  const errorText = isStrict
                    ? `Session unavailable [${FERN_FE_CODES.SESSION_NOT_READY_ON_SEND}] — please refresh and try again.`
                    : 'Your workspace is taking longer than expected to start. Please refresh the page and try again — it should be ready within a few seconds.';
                  helpers.addSystemMessage({
                    text: errorText,
                    sourceTag: 'prest0n_session_boot_error',
                  });
                  return { handled: true, error: undefined };
                }

                // ============================================================
                // TOOL BRIDGE: Run agentic tools browser-side for migrated accounts
                // ============================================================
                // Deep research, video script, translation, legal docs, format-report,
                // and document analyzer all run browser-side using Perplexity/Claude
                // APIs directly — regardless of migration status.
                // Only general conversation falls through to prest0nSession.sendTurn().
                // This preserves full tool quality (3-stage deep research, PDF/DOCX
                // generation, Firebase Storage uploads) without any backend changes.
                // ============================================================

                // Track C (2026-04-22 → 2026-08-24) routed loose translate
                // intent + PDF/DOCX straight to the browser translate tool
                // from here. Removed under the master rule (Primacy Audit
                // D2): the detector survives as the TRANSLATE_INTENT_HINT
                // observation woven into turnTopAugmentedPageCtx above — the
                // primary decides, then delegates to the document-specialist
                // routing, whose deliverables are full downloadable files.
                // Do NOT reintroduce a client-side pre-empt here.

                // ============================================================
                // ORCHESTRATOR-AUTHORITATIVE ROUTING (2026-05-10)
                // ============================================================
                // Per Miles directive (Slack C0AMX0ACS9K/1778451390.755569):
                // the LLM is the sole routing authority. Regex matches are
                // hints, never pre-empts. The previous code path here invoked
                // getOrCreateRuntime() directly on a regex match, which caused
                // "what tools do you have access to?" to misroute into the
                // RBAC tool's hardcoded 9-role list.
                //
                // When ORCHESTRATOR_AUTHORITATIVE_ROUTING is ON the regex
                // hint (turnTopRoutingHint, computed above the migration
                // gate) is threaded into pageContext.contextHint and the
                // Prest0n session decides whether (and how) to invoke any
                // matched tool. We only fall through to a manual pre-empt
                // when the kill-switch is flipped off.
                // ============================================================
                if (turnTopRegexMatch) {
                  if (ORCHESTRATOR_AUTHORITATIVE_ROUTING) {
                    console.log(
                      `[GlobalTools] Prest0n path: regex matched tool "${turnTopRegexMatch.tool.id}" — passing as informational hint, NOT pre-empting (ORCHESTRATOR_AUTHORITATIVE_ROUTING=on)`
                    );
                    fernandoTrace(
                      'TOOL_BRIDGE_HINT_ONLY',
                      { accountId, toolId: turnTopRegexMatch.tool.id, mode: 'orchestrator_authoritative' },
                      isStrict
                    );
                  } else {
                    // Legacy regex pre-empt — only when the kill-switch is flipped off.
                    console.log(`[GlobalTools] Prest0n path: intercepted tool trigger "${turnTopRegexMatch.tool.id}" — running browser-side`);
                    fernandoTrace(
                      'TOOL_BRIDGE_INTERCEPT',
                      { accountId, toolId: turnTopRegexMatch.tool.id },
                      isStrict
                    );
                    const prest0nToolRuntime = getOrCreateRuntime(turnTopRegexMatch.tool.id, turnOriginatingThreadId);
                    if (prest0nToolRuntime) {
                      // Pass onStreamingStart to the tool so it can clear the thinking indicator
                      // exactly when its own first progress widget appears (eliminating the gap).
                      const { matches: _toolBridgeMatches, ...prest0nTriggerInput } = turnTopRegexMatch.match;
                      await prest0nToolRuntime.start(message, {
                        ...prest0nTriggerInput,
                        onStreamingStart
                      });
                      return { handled: true };
                    }
                    console.warn(`[GlobalTools] Tool bridge: matched "${turnTopRegexMatch.tool.id}" but getOrCreateRuntime returned null — falling through to VM`);
                  }
                }
                // ============================================================
                // END TOOL BRIDGE
                // ============================================================

                // DO NOT call onStreamingStart() here — it removes the thinking
                // indicator immediately, leaving a blank gap while the backend
                // processes the request. Instead, keep the thinking spinner visible
                // and only remove it when the first text chunk actually arrives.

                // Stream state: accumulate text chunks into a live message
                let streamingMessageId: string | null = null;
                let streamingMessageIndex: number = -1;
                let accumulatedText = '';
                // SMOOTH STREAMING (owner directive 2026-08-23) — the paced
                // reveal that makes every delivery lane look the same. It
                // decides only how much of `accumulatedText` is PAINTED right
                // now; `accumulatedText` itself stays the whole truth, so
                // finalize, billing (outputTextLength), and the file-dump mask
                // are all unaffected. Turn-scoped like the ids above; created
                // lazily on the first chunk and stopped on EVERY terminal path
                // via stopStreamReveal() so a queued frame can never repaint
                // over a finalized message.
                let streamReveal: StreamRevealHandle | null = null;
                const paintStreamedText = (visibleText: string): void => {
                  if (helpers.streaming.updateStreamingMessageById && streamingMessageId) {
                    helpers.streaming.updateStreamingMessageById(streamingMessageId, {
                      text: visibleText,
                    });
                  } else if (streamingMessageIndex >= 0) {
                    helpers.streaming.updateStreamingMessage(streamingMessageIndex, {
                      text: visibleText,
                    });
                  }
                };
                /** Paint everything pending and stop the reveal loop. Idempotent. */
                const stopStreamReveal = (): void => {
                  if (!streamReveal) return;
                  const reveal = streamReveal;
                  streamReveal = null;
                  try {
                    reveal.flush();
                  } catch (err) {
                    console.warn('[GlobalTools] stream reveal flush failed', err);
                  }
                };
                // Round K (duplicate-answer convergence, 2026-07): the turn's
                // durable task id (`turnServerTaskId`), retained so the streamed
                // ANSWER bubble — not just the thinking widget — carries
                // serverTaskId from creation and through finalize.
                // freezeTaskAnchors (reducer) and
                // buildFinalizedPersistedMessage (persist) then derive
                // taskAnchorId = prest0n_task_<id>, so the client copy converges
                // with the server's durable copy (taskQueue
                // persistCompletedTaskAnswerToThread) at every merge layer
                // instead of rendering a duplicate reply. Turn-scoped by
                // construction (per-turn closure, like originatingThreadId), so
                // concurrent sends on other threads can never cross-stamp.
                // NOTE (2026-07-31): the declaration itself is hoisted next to
                // the server-backed ticker state above so the post-await
                // cleanup can read it — the contract is unchanged.
                let turnHandled = false;
                // Round J empty-turn guard: set when the onDone fallback has
                // already rendered the retryable error card, so the
                // post-sendTurn recoverable-error guard below never doubles it.
                let emptyTurnCardRendered = false;
                // Finding 4 (2026-07-15): the turn painted a visible tool
                // surface via onToolStart/onToolResult even though no text
                // streamed (streamingMessageId null, accumulatedText empty).
                // OR'd into decideOnDoneFallback's clientSurfaceOwned input so
                // a done:{handled:true} with an undefined surfaceOwnedByTool
                // flag doesn't render the failure card + Retry UNDER the
                // successfully painted widget (Retry would re-execute the
                // whole turn — a duplicate). Turn-scoped closure, same
                // discipline as turnServerTaskId/originatingThreadId.
                let paintedThisTurn = false;

                // Ingest-on-upload progress (2026-06 reliability overhaul):
                // live unsubscriber for this turn's ingest-job Firestore
                // subscription (created just before sendTurn below).
                // Idempotent teardown — called from onDone/onError, the
                // failure branches, and user abort; the hook-level unmount
                // effect is the last-resort sweeper.
                let unsubscribeIngestProgress: (() => void) | null = null;
                const stopIngestProgress = () => {
                  if (!unsubscribeIngestProgress) return;
                  const unsub = unsubscribeIngestProgress;
                  unsubscribeIngestProgress = null;
                  ingestProgressUnsubsRef.current.delete(unsub);
                  try { unsub(); } catch { /* listener already torn down */ }
                };

                // Start a placeholder streaming message for text responses
                const startStreamMessage = () => {
                  if (streamingMessageId === null) {
                    const { index, id } = helpers.streaming.startStreamingMessage({
                      text: '',
                      html: '',
                      sourceTag: 'prest0n_session_streaming',
                    });
                    streamingMessageId = id;
                    streamingMessageIndex = index;
                    // Round K: anchor the answer bubble from creation — the
                    // pre-flight onTaskQueued stamp landed on the thinking
                    // widget, which onTextChunk removes; without this the
                    // answer itself is anchorless and the server's durable
                    // prest0n_task_<id> copy renders as a duplicate. (The
                    // onTaskQueued re-stamp still covers an older backend that
                    // mints its own id after this message exists.)
                    if (turnServerTaskId) {
                      if (helpers.streaming.updateStreamingMessageById) {
                        helpers.streaming.updateStreamingMessageById(id, {
                          serverTaskId: turnServerTaskId,
                        });
                      } else if (index >= 0) {
                        helpers.streaming.updateStreamingMessage(index, {
                          serverTaskId: turnServerTaskId,
                        });
                      }
                    }
                  }
                };

                // ============================================================
                // ORCHESTRATOR-DECIDED CONNECT OFFERS (2026-08-19; supersedes
                // the 2026-08-12 APP_NOT_CONNECTED auto-offer)
                // ============================================================
                // The card renders ONLY when the orchestrator itself decided
                // to offer a connection (offer_app_connection →
                // CONNECT_OFFER::<app> marker / typed signal) — with the
                // thread's context and the account's actual connections. The
                // model's prose is never the mechanism, and neither is a
                // client-side tool→app guess. Two EXISTING channels carry the
                // verdict (no new backend marker was needed):
                //   • inline SSE lane — the `tool_result` event has always
                //     shipped the ToolResult's `error` (runtime.ts spreads
                //     the whole ToolResult into data); streamProcessor now
                //     forwards it as onToolResult's 4th argument;
                //   • async 202/task lane — the completed task doc's
                //     result.toolsInvoked[] (taskQueue.completeTask persist;
                //     turnPayloadSlim keeps toolName/success/error under the
                //     size ladder) arrives via onTaskStatus on the terminal
                //     poll tick.
                // Debounced via autoConnectOfferKeysRef: at most one
                // auto-offer per app per thread per session. The card itself
                // is the existing connect-app machinery (offerConnectForQuery
                // → postConnectOffer: data-only payload + buttons riding
                // workflowActionButtonsHtml; clicks handled in
                // handleButtonClick via launchConnectFlow).
                const maybeAutoOfferConnectCard = (
                  failedToolName: string | undefined,
                  failedToolError: string | undefined,
                  structuredSignal?: { kind?: string; app?: string } | null,
                ): void => {
                  // Two triggers share this renderer (2026-08-13): a backend
                  // tool failing APP_NOT_CONNECTED, and the model itself
                  // CALLING offer_app_connection — its result carries a
                  // CONNECT_OFFER::<app> marker so Preston can summon the
                  // card agentically instead of coaching magic phrases.
                  const errorStr = String(failedToolError || '');
                  // Outward-action authorize card (2026-08-18): a HELD
                  // send-email arrives on this same failed-tool-result
                  // transport with its own marker. Debounced per actionId
                  // through the same key set; the card render peeks the
                  // held preview through prest0nAuthorizeAction.
                  // TAUGHT AUTO-SEND (2026-08-25): the held send already
                  // executed under the user's standing instruction — render
                  // its buttonless "auto sent" receipt from the stamped doc.
                  // BATCH TILE FIRST (2026-08-27, Justin's six-emails report):
                  // a hold stamped with a batchId joins ONE tile — a row per
                  // staged send, updated in place — instead of minting a card
                  // per marker. Holds without a batchId (pre-deploy docs, non
                  // -batch paths) fall through to the single card unchanged,
                  // and so does everything when the in-place updater is
                  // missing (renderOrUpdateBatchTile returns false).
                  // ⓘ SERIES LINEUP (2026-08-29): every card posted through
                  // these flows registers its message id, so the turn's
                  // model/tool series stamps onto the tile at onDone and the
                  // info popup shows the whole lineup instead of "no
                  // extensive compute was necessary".
                  const withCardUsageTracking = (m: ReturnType<typeof createMessagingHelpers>) => ({
                    ...m,
                    addSystemMessage: ((input: Parameters<typeof m.addSystemMessage>[0]) => {
                      const posted = m.addSystemMessage(input);
                      const pid = (posted as { id?: string } | null | undefined)?.id;
                      if (pid) registerCardMessageForUsage(String(pid));
                      return posted;
                    }) as typeof m.addSystemMessage,
                  });
                  const batchTileMessaging = (marker: ReturnType<typeof createMessagingHelpers>) => ({
                    addSystemMessage: marker.addSystemMessage,
                    updateMessageById: helpers.streaming.updateStreamingMessageById
                      ? (id: string, update: { workflowActionButtonsHtml?: string }) =>
                          helpers.streaming.updateStreamingMessageById!(id, update)
                      : undefined,
                  });
                  if (errorStr.startsWith('AUTO_SENT::')) {
                    const autoActionId = errorStr.slice('AUTO_SENT::'.length).trim().slice(0, 60);
                    const autoKey = `autosent::${autoActionId}`;
                    if (autoConnectOfferKeysRef.current.has(autoKey)) return;
                    autoConnectOfferKeysRef.current.add(autoKey);
                    const autoMessaging = withCardUsageTracking(createMessagingHelpers(helpers, sharedContext));
                    void import('../tools/batchSendTile')
                      .then(({ renderOrUpdateBatchTile }) =>
                        renderOrUpdateBatchTile(autoActionId, turnOriginatingThreadId, batchTileMessaging(autoMessaging)))
                      .then((joined) => joined
                        ? true
                        : import('../tools/authorizeAction').then(({ renderAutoSentReceiptFromMarker }) =>
                            renderAutoSentReceiptFromMarker(errorStr, autoMessaging)))
                      .then((posted) => {
                        if (!posted) autoConnectOfferKeysRef.current.delete(autoKey);
                      })
                      .catch(() => {
                        autoConnectOfferKeysRef.current.delete(autoKey);
                      });
                    return;
                  }
                  if (errorStr.startsWith('AUTHORIZE_SEND::')) {
                    const heldActionId = errorStr.slice('AUTHORIZE_SEND::'.length).trim().slice(0, 60);
                    const authorizeKey = `authorize::${heldActionId}`;
                    if (autoConnectOfferKeysRef.current.has(authorizeKey)) return;
                    autoConnectOfferKeysRef.current.add(authorizeKey);
                    const authorizeMessaging = withCardUsageTracking(createMessagingHelpers(helpers, sharedContext));
                    void import('../tools/batchSendTile')
                      .then(({ renderOrUpdateBatchTile }) =>
                        renderOrUpdateBatchTile(heldActionId, turnOriginatingThreadId, batchTileMessaging(authorizeMessaging)))
                      .then((joined) => joined
                        ? true
                        : import('../tools/authorizeAction').then(({ renderAuthorizeCardFromMarker }) =>
                            renderAuthorizeCardFromMarker(errorStr, authorizeMessaging)))
                      .then((posted) => {
                        if (!posted) autoConnectOfferKeysRef.current.delete(authorizeKey);
                      })
                      .catch(() => {
                        autoConnectOfferKeysRef.current.delete(authorizeKey);
                      });
                    return;
                  }
                  // 2026-08-19 (trigger-audit Phase 5, shadow mode): the
                  // STRUCTURED signal is preferred; the prose marker below
                  // stays as the fallback until shadow telemetry shows the
                  // field survives both transports with parity — then the
                  // marker sniffing is deleted, never before.
                  //
                  // ORCHESTRATOR-DECIDED ONLY (owner directive 2026-08-19,
                  // Gmail-card-to-an-Outlook-firm incident): a bare
                  // APP_NOT_CONNECTED failure no longer mints a card here.
                  // The client used to INFER the app from the failed tool's
                  // name (a static tool→app table that hardcoded gmail for
                  // every email tool), offering apps the user doesn't use.
                  // Cards now render ONLY from the orchestrator's own verdict
                  // — the offer_app_connection tool's CONNECT_OFFER:: marker
                  // or its typed signal — decided with the thread's context
                  // and the account's actual connections. Bare failures post
                  // nothing: the model's prose carries remediation, and the
                  // orchestrator is prompted to decide the right offer.
                  const signalApp =
                    structuredSignal?.kind === 'connect_offer'
                      ? String(structuredSignal.app || '').trim().slice(0, 80) || null
                      : null;
                  let markerApp: string | null = null;
                  if (errorStr.startsWith('CONNECT_OFFER::')) {
                    markerApp = errorStr.slice('CONNECT_OFFER::'.length).trim().slice(0, 80) || null;
                  }
                  if (markerApp && !signalApp) {
                    // Expected while old backends / stripped transports are
                    // in the wild — the parity datum the deletion waits on.
                    console.log('[GlobalTools] connect-offer shadow: marker-only (no structured signal)', {
                      failedToolName, markerApp,
                    });
                  } else if (signalApp && markerApp && signalApp !== markerApp) {
                    console.warn('[GlobalTools] connect-offer shadow: signal/marker DISAGREE', {
                      signalApp, markerApp,
                    });
                  }
                  const appSlug = signalApp || markerApp;
                  if (!appSlug) return;
                  // Canonicalized key (owner repro 2026-08-20): the app name
                  // is model-authored free text — "RingCentral" and "Ring
                  // Central" must debounce as ONE app, or a re-ask mints a
                  // duplicate card. (Two sessions fixed this the same day;
                  // merged onto the SHARED normalizer so there is one
                  // implementation.) The in-memory ref is the in-session
                  // belt, PER TURN (2026-09-02): cardStampEpoch bumps on every
                  // user turn, and the async lane's late onTaskStatus runs in
                  // this same closure, so the SSE + task pair of one turn
                  // dedupes while the next turn's ask posts a fresh card.
                  const offerKey = `${turnOriginatingThreadId ?? 'no-thread'}::${cardStampEpoch}::${connectAppOfferKey(appSlug)}`;
                  if (autoConnectOfferKeysRef.current.has(offerKey)) return;
                  autoConnectOfferKeysRef.current.add(offerKey);
                  console.log(`[GlobalTools] orchestrator connect offer (tool "${failedToolName}") — posting ${appSlug} connect card`);
                  // Wrapped messaging so the data-only payload flows through
                  // the post-tool renderer exactly like the connect-app
                  // tool's own step (never hardcoded prose).
                  const autoOfferMessaging = withCardUsageTracking(createMessagingHelpers(helpers, sharedContext));
                  const readThreadMessages = (helpers as {
                    readThreadMessages?: (tid: string) => Array<Record<string, unknown>>;
                  })?.readThreadMessages;
                  const offerTranscript =
                    turnOriginatingThreadId && typeof readThreadMessages === 'function'
                      ? (() => { try { return readThreadMessages(turnOriginatingThreadId); } catch { return undefined; } })()
                      : undefined;
                  // Re-summon (plan P0 #4 / §9.4, 2026-09-02): an orchestrator-
                  // decided offer ALWAYS posts — the primary decided it with the
                  // whole thread in view, so `force: true` bypasses the (now
                  // same-turn) transcript guard; the per-turn key above is the
                  // in-session duplicate belt. The card's {en, es} chrome
                  // follows the user's own last message (the clarifying-footer
                  // idiom); the transcript rows are ChatMessage-shaped.
                  const lastUserText = (() => {
                    if (!offerTranscript) return '';
                    for (let i = offerTranscript.length - 1; i >= 0; i--) {
                      const row = offerTranscript[i] as { isUser?: boolean; text?: unknown } | undefined;
                      if (row?.isUser === true) return typeof row.text === 'string' ? row.text : '';
                    }
                    return '';
                  })();
                  void offerConnectForQuery(
                    appSlug,
                    autoOfferMessaging,
                    {
                      ...(offerTranscript ? { transcript: offerTranscript } : {}),
                      force: true,
                      lang: detectInputLanguage(lastUserText),
                    },
                  )
                    .then((posted) => {
                      // Catalog fetch failed / no resolvable app — release the
                      // debounce key so a later turn can retry.
                      if (!posted) autoConnectOfferKeysRef.current.delete(offerKey);
                    })
                    .catch(() => {
                      autoConnectOfferKeysRef.current.delete(offerKey);
                    });
                };

                // Async 202/task lane of the auto-offer: the terminal poll
                // tick delivers the completed task doc whose
                // result.toolsInvoked[] carries per-tool error codes (kept by
                // the Firestore payload slimmer). Called from onTaskStatus
                // BEFORE its stage-label early-returns, which would otherwise
                // skip stage-less dedicated-VM completions. Hoisted here so
                // the onTaskStatus body stays within its source-contract pin
                // window (asyncTurnStopAndFrozenClock.test.ts §3.1a).
                // "SHOW ME ON THE DESKTOP PAGE" (P6b, 2026-09-20). A computer
                // run's result carries a typed `desktop_run` signal naming the
                // workstation session it actually drove. Until this landed the
                // id reached Firestore in the result's metadata and stopped —
                // nothing read it, the reply DESCRIBED a screen ("GitHub is
                // open on your desktop") and the person was watching a
                // different one. The card is the same primitive the connect
                // and authorize cards use (data-only payload + the html in
                // workflowActionButtonsHtml; re-projected from the payload at
                // every hydrate by connectTileRehydrate), deduped per thread +
                // turn + session through the same key set, so the SSE and
                // task-poll copies of one turn post ONE card. Unlike the
                // connect card it fires on a SUCCESSFUL result — a completed
                // run is the case that matters most — and on a stuck one.
                const maybeShowDesktopRunCard = (
                  signal: { kind?: string; sessionId?: string; svc?: string; app?: string; outcome?: string } | null | undefined,
                ): void => {
                  if (!signal || signal.kind !== 'desktop_run') return;
                  const sessionId = typeof signal.sessionId === 'string' ? signal.sessionId.trim() : '';
                  if (!sessionId) return;
                  const runKey = `${turnOriginatingThreadId ?? 'no-thread'}::${cardStampEpoch}::desktop_run::${sessionId}`;
                  if (autoConnectOfferKeysRef.current.has(runKey)) return;
                  autoConnectOfferKeysRef.current.add(runKey);
                  try {
                    const readThreadMessages = (helpers as {
                      readThreadMessages?: (tid: string) => Array<Record<string, unknown>>;
                    })?.readThreadMessages;
                    const transcript =
                      turnOriginatingThreadId && typeof readThreadMessages === 'function'
                        ? (() => { try { return readThreadMessages(turnOriginatingThreadId); } catch { return undefined; } })()
                        : undefined;
                    const lastUserText = (() => {
                      if (!transcript) return '';
                      for (let i = transcript.length - 1; i >= 0; i--) {
                        const row = transcript[i] as { isUser?: boolean; text?: unknown } | undefined;
                        if (row?.isUser === true) return typeof row.text === 'string' ? row.text : '';
                      }
                      return '';
                    })();
                    const lang = detectInputLanguage(lastUserText) === 'es' ? 'es' : 'en';
                    const data = {
                      sessionId,
                      app: typeof signal.app === 'string' && signal.app.trim() ? signal.app.trim().slice(0, 80) : 'the browser',
                      outcome: typeof signal.outcome === 'string' && signal.outcome.trim() ? signal.outcome.trim().slice(0, 40) : 'unknown',
                      ...(typeof signal.svc === 'string' && signal.svc.trim() ? { svc: signal.svc.trim() } : {}),
                      lang,
                    } as const;
                    const html = buildDesktopRunCardHtml(data);
                    if (!html) { autoConnectOfferKeysRef.current.delete(runKey); return; }
                    const messaging = createMessagingHelpers(helpers, sharedContext);
                    const posted = messaging.addSystemMessage({
                      payload: {
                        kind: DESKTOP_RUN_CARD_KIND,
                        toolId: 'run_computer_task',
                        severity: 'info',
                        data: { ...data },
                        // Machine context only: no renderer composes from this.
                        summary: `desktop run ${data.outcome} in ${data.app} on session ${sessionId}`,
                      },
                      renderPolicy: 'never',
                      sourceTag: DESKTOP_RUN_CARD_SOURCE_TAG,
                      workflowActionButtonsHtml: html,
                    });
                    const pid = (posted as { id?: string } | null | undefined)?.id;
                    if (pid) registerCardMessageForUsage(String(pid));
                    if (!posted) autoConnectOfferKeysRef.current.delete(runKey);
                  } catch (err) {
                    autoConnectOfferKeysRef.current.delete(runKey);
                    console.warn('[GlobalTools] desktop-run card not posted', err);
                  }
                };
                // Async-lane twin: the task doc's toolsInvoked[] keeps `signal`
                // through every payload slimmer, the minimal one included.
                const scanCompletedTaskForDesktopRuns = (
                  task: Parameters<NonNullable<TurnStreamCallbacks['onTaskStatus']>>[0],
                ): void => {
                  const completedToolsInvoked =
                    task?.status === 'completed' ? task.result?.toolsInvoked : undefined;
                  if (!Array.isArray(completedToolsInvoked)) return;
                  for (const invokedTool of completedToolsInvoked) {
                    const sig = (invokedTool as { signal?: { kind?: string } } | null | undefined)?.signal;
                    if (sig && sig.kind === 'desktop_run') maybeShowDesktopRunCard(sig);
                  }
                };
                const scanCompletedTaskForConnectOffers = (
                  task: Parameters<NonNullable<TurnStreamCallbacks['onTaskStatus']>>[0],
                ): void => {
                  const completedToolsInvoked =
                    task?.status === 'completed' ? task.result?.toolsInvoked : undefined;
                  if (!Array.isArray(completedToolsInvoked)) return;
                  for (const invokedTool of completedToolsInvoked) {
                    if (invokedTool && invokedTool.success === false) {
                      maybeAutoOfferConnectCard(
                        invokedTool.toolName,
                        invokedTool.error,
                        (invokedTool as { signal?: { kind?: string; app?: string } }).signal,
                      );
                    }
                  }
                  // The desktop-run card (P6b, 2026-09-20) rides this same scan
                  // rather than a call of its own from onTaskStatus: that
                  // handler's body sits inside a 1,600-char source-contract
                  // window with 28 chars of slack (asyncTurnStopAndFrozenClock
                  // §3.1a), and this scan's own call there is a pinned marker
                  // (midTurnFoldMechanics' merge-line window). Same input, same
                  // moment, no gate on success — see scanCompletedTaskForDesktopRuns.
                  scanCompletedTaskForDesktopRuns(task);
                };
                // Note-turn replies (2026-09-15, docs/PLAN-mid-turn-fold-in-20260915.md
                // §4.2): the task doc carries every reply the primary wrote for a
                // mid-turn note (`noteReplies[]`). Each renders ONCE, in order, as a
                // Prest0n message under the id the server also wrote to the thread,
                // so the live listener's copy collapses into it. The poll brings the
                // full doc every ~2s, so the already-rendered set lives here.
                const renderedNoteReplyIds = new Set<string>();
                const renderNoteReplies = (
                  task: Parameters<NonNullable<TurnStreamCallbacks['onTaskStatus']>>[0],
                ): void => {
                  const replies = Array.isArray(task?.noteReplies) ? task.noteReplies : [];
                  for (const reply of replies) {
                    const id = typeof reply?.id === 'string' ? reply.id.trim() : '';
                    const text = typeof reply?.text === 'string' ? reply.text.trim() : '';
                    if (!id || !text || renderedNoteReplyIds.has(id)) continue;
                    renderedNoteReplyIds.add(id);
                    const atMs = typeof reply?.atMs === 'number' && Number.isFinite(reply.atMs) ? reply.atMs : Date.now();
                    try {
                      helpers.addMessage({
                        id,
                        text,
                        isUser: false,
                        timestamp: new Date(atMs),
                        sourceTag: 'note_reply',
                      });
                    } catch (err) {
                      console.warn('[GlobalTools] note reply render failed (non-fatal)', err);
                    }
                  }
                };

                // Delivery ladder (PR-2): the ledger row this turn reports to —
                // set once the task id is known (below), read by onAccepted.
                let deliveryKeyForTurn: string | null = null;
                // Build SSE callbacks that wire into the existing message helpers
                const streamCallbacks: TurnStreamCallbacks = {
                  // Delivery ladder (PR-2): 2xx from prest0nRunTurn — the server
                  // has the turn. In a solo thread that is delivery.
                  onAccepted: () => {
                    deliveryLedger.markAccepted(deliveryKeyForTurn);
                    // Send-ack watchdog (2026-09-15): the server HAVING the
                    // turn IS the acknowledgment. Before this, only a streamed
                    // message, an appended reply or a released flag acked it —
                    // so a turn whose first step read a screenshot for 51 s
                    // (an accepted 202, live status polls throughout) fired
                    // "couldn't start this reply" at 25 s while it was running.
                    try {
                      (helpers as { ackSendWatchdog?: () => void } | undefined)?.ackSendWatchdog?.();
                    } catch { /* an ack must never break the turn */ }
                  },
                  // Point 6 (Option B) — AUTHENTICATED-path refresh-survival.
                  // The authed turn runs as a durable server-side background
                  // task (usePrest0nSession polls getPrest0nTaskStatus). Stamp
                  // that durable taskId onto the in-flight streaming message as
                  // `serverTaskId` so it persists (thread store / Firestore)
                  // and survives a hard refresh: on hydrate, useGlobalChat's
                  // authed resume effect re-polls the task and finalizes the
                  // message instead of showing the Interrupted/Regenerate card.
                  //
                  // This is purely ADDITIVE — it stamps serverTaskId onto the
                  // in-flight message(s) so a hard refresh can re-attach. It must
                  // NOT remove the active pending widget; the existing
                  // onThinking/onTaskStatus stage-card behavior is untouched.
                  //
                  // CRITICAL (BUG-POINT6-WRONG-STAMP-TARGET, 2026-06-04): on the
                  // deep-research / async authed path the message that actually
                  // SURVIVES a hard refresh as the orphaned in-flight widget is
                  // the VISIBLE pending widget (`activePendingId`, sourceTag
                  // stage1_assessment_pending / rotated by onToolStart) — NOT the
                  // lazily-created `streamingMessageId` (prest0n_session_streaming),
                  // which on a pure deep-research turn may never be created or
                  // carry no body. Stamping only streamingMessageId left the
                  // surviving spinner WITHOUT a serverTaskId, so findResumableServerTasks
                  // skipped it and the hydrate reconcile marked it Interrupted
                  // ("session expired") — exactly the regression Malaika hit twice.
                  // FIX: stamp the active pending widget (the real orphan) FIRST,
                  // and also streamingMessageId when it already exists, so whichever
                  // one persists carries the id.
                  onTaskQueued: (taskId: string) => {
                    // 2026-08-14 (Keystone repro): every stream callback feeds the
                    // inactivity baseline — without this the "inactivity-policed"
                    // 8-minute ceiling was a flat guillotine on this lane (none of
                    // these callbacks ever re-armed it) and only tab-switching
                    // reset the clock, the exact inverse of the owner's rule.
                    updateStreamActivity();
                    if (!taskId) return;
                    // Round K: retain the turn's task id so the ANSWER bubble
                    // (startStreamMessage / onDone finalMsg) carries it too —
                    // the widget stamps below die with the widget.
                    turnServerTaskId = taskId;
                    // Stop fix (2026-07-31): register the thread's live server
                    // task so cancelRunningTool can fire the server-side
                    // cancel for it. Runs on the pre-flight stamp AND the real
                    // 202 (same id when the backend honors clientTaskId; the
                    // re-registration keeps older backends' minted ids
                    // correct). Released at the turn's terminal
                    // (releaseTurnAbortController) or consumed by Stop itself.
                    liveServerTaskByThreadRef.current.set(originatingThreadId ?? null, taskId);
                    try {
                      // Primary: the visible pending widget that survives refresh.
                      if (!activePendingRemoved && activePendingId) {
                        if (helpers.streaming.updateStreamingMessageById) {
                          helpers.streaming.updateStreamingMessageById(activePendingId, {
                            serverTaskId: taskId,
                          });
                        } else if (activePendingIndex >= 0) {
                          helpers.streaming.updateStreamingMessage(activePendingIndex, {
                            serverTaskId: taskId,
                          });
                        }
                      }
                      // Secondary: if a text-stream placeholder already exists,
                      // stamp it too (covers the streaming-text authed path).
                      if (helpers.streaming.updateStreamingMessageById && streamingMessageId) {
                        helpers.streaming.updateStreamingMessageById(streamingMessageId, {
                          serverTaskId: taskId,
                        });
                      } else if (streamingMessageIndex >= 0) {
                        helpers.streaming.updateStreamingMessage(streamingMessageIndex, {
                          serverTaskId: taskId,
                        });
                      }
                    } catch (err) {
                      console.warn('[GlobalTools] onTaskQueued serverTaskId stamp failed', err);
                    }
                  },

                  onThinking: (msg?: string) => {
                    updateStreamActivity();
                    // Thinking indicator is already showing via the stage1_assessment_pending
                    // widget added before sendTurn(). Keep it visible — do NOT remove it here.
                    // It will be removed by onTextChunk when the first real text arrives.
                    //
                    // dr-frontend-thinking-label (2026-05-18 / 2026-05-19): when
                    // the backend sends a non-empty `msg` (e.g. deep-research
                    // stage labels "Step 1 of 3: clarifying questions…" /
                    // "Step 2 of 3: deep researching the web…" / "Step 3 of 3:
                    // generating downloadable memo (PDF + DOCX)…") swap the
                    // visible pending widget's text to that label so the user
                    // can see which stage is actually running instead of
                    // staring at a generic "Working..." widget for several
                    // minutes (BUG-T1-DEAD-THINKING-DOT).
                    //
                    // CRITICAL: update activePendingId — which rotates whenever
                    // onToolStart creates a new pending widget — NOT the
                    // original thinkingIndicatorId. By the time stage labels
                    // arrive for a tool call, the original thinking dot has
                    // already been replaced by the tool-start widget.
                    const label = (msg || '').trim();
                    if (!label) return;
                    if (activePendingRemoved) return;
                    try {
                      latestServerBackedStageLabel = label;
                      refreshServerBackedProgressLabel();
                    } catch (err) {
                      // non-fatal — widget update can fail if the indicator
                      // was already removed by onTextChunk in a race.
                      console.warn('[GlobalTools] onThinking widget update failed', err);
                    }
                  },

                  // BUG-T1-STAGE-CARDS-MISSING Plan B (2026-05-19) —
                  // direct-update fallback for the VM-routed / async polling
                  // path. The polling loop in usePrest0nSession calls
                  // callbacks.onThinking(label) on every currentStage transition,
                  // BUT in async/VM contexts the onThinking closure can lose
                  // its handle to activePendingId before the first transition
                  // fires. onTaskStatus runs every poll (~every 2s) with the
                  // full task record, including currentStage. We use it to
                  // refresh the pending widget directly from the freshest
                  // closure, guaranteeing the widget id is current.
                  onTaskStatus: (task) => {
                    updateStreamActivity();
                    // Parallel branches: the ledger rides the same task doc.
                    latestBranches = orderBranchEntries(task?.branches, task?.branchOrder);
                    showBranchMergeLineOnce(); // PR-3B: once, when the last branch lands
                    // 2026-08-12: async-lane APP_NOT_CONNECTED auto-offer
                    // (see scanCompletedTaskForConnectOffers above).
                    scanCompletedTaskForConnectOffers(task);
                    // 2026-09-15: the note turn's replies ride the same doc.
                    renderNoteReplies(task);
                    const currentStage = task?.currentStage;
                    const stageLabel = (currentStage?.label || '').trim();
                    // Frozen-clock fix (2026-07-31): repaint the elapsed label
                    // on EVERY ~2s poll tick, BEFORE the empty-stage
                    // early-return — dedicated-VM tasks never carry a
                    // currentStage.label, so this used to bail without ever
                    // refreshing the "working · N:NN" clock. The helper
                    // self-guards (isServerBackedTurn + activePendingRemoved),
                    // so a finalized/legacy widget is never touched.
                    try {
                      refreshServerBackedProgressLabel();
                    } catch (err) {
                      console.warn('[GlobalTools] onTaskStatus elapsed repaint failed', err);
                    }
                    if (!stageLabel) return;
                    if (activePendingRemoved) return;

                    // BUG-DR-CLARIFYING-CARD-MISSING (2026-05-20) — Stage-1
                    // clarifying-questions branch. Finalize the pending
                    // widget into a real qualification card with a Skip
                    // button when the backend ships the structured payload.
                    // We re-use formatDeepResearchQualificationHtml so the
                    // styling matches the global-tools / legacy widget
                    // surfaces. sourceTag='deep_research_qualification' is
                    // critical: it's the signal that
                    // src/services/globalTools/orchestrator/midClarifyingFreeTextPreRoute.ts
                    // grep's for to route the user's next-turn free-text
                    // reply directly back through deep-research with
                    // skipClarification + folded answers.
                    if (
                      !clarifyingCardFinalized &&
                      currentStage?.stage === 'clarifying-questions'
                    ) {
                      // BUG-DR-CLARIFYING-CARD-JSX-MISSING (2026-05-20) —
                      // Resolve the payload from one of two sources:
                      //  (1) PRIMARY: structured `currentStage.payload`
                      //      shipped by toolRouter.ts:302 emitProgress →
                      //      runtime.ts:405 emit-lambda →
                      //      taskQueue.ts:196-204 stage event persist.
                      //  (2) FALLBACK: parse the markdown body of
                      //      `task.result.finalResponse` (rendered by
                      //      clarifyingPreStage.ts:266
                      //      renderClarifyingCardMarkdown) via
                      //      parseClarifyingMarkdownToPayload. This branch
                      //      catches the regression Malaika hit on
                      //      2026-05-20 where the structured payload was
                      //      not reaching the bundle (the live JS had zero
                      //      occurrences of "task-queue-dr-clarifying-skip
                      //      -btn") and the user only saw raw prose. The
                      //      markdown body always exists because it is the
                      //      tool's primary `output`, so the card now
                      //      renders unconditionally for any
                      //      stage=clarifying-questions transition.
                      let resolvedPayload: {
                        initialFindings?: string;
                        clarifyingQuestions?: Array<{ question: string; context?: string }>;
                        language?: 'en' | 'es';
                        // BUG-DR-SKIP-ORIGINAL-QUERY (2026-06-05) — original DR
                        // prompt, stamped onto the skip button below so skip is
                        // thread-fragmentation proof.
                        originalQuery?: string;
                        // PLAN-deep-research-dynamic-clarify-20260713 — round
                        // progress for the dynamic multi-round loop; renders a
                        // "round N of M" chip on follow-up cards.
                        clarifyingRound?: number;
                        clarifyingMaxRounds?: number;
                      } | null = null;

                      if (currentStage.payload && typeof currentStage.payload === 'object') {
                        resolvedPayload = currentStage.payload as unknown as typeof resolvedPayload;
                      } else {
                        const markdownBody = task?.result?.finalResponse;
                        const parsed = parseClarifyingMarkdownToPayload(markdownBody);
                        if (parsed) {
                          resolvedPayload = {
                            initialFindings: parsed.initialFindings,
                            clarifyingQuestions: parsed.clarifyingQuestions,
                            language: parsed.language,
                            // Markdown-parse fallback has no structured
                            // originalQuery; leave undefined so the skip
                            // handler falls back to history-walk (existing
                            // same-thread behavior).
                          };
                          console.warn(
                            '[GlobalTools] clarifying-questions stage missing structured payload — falling back to markdown parse',
                            { questionCount: parsed.clarifyingQuestions.length, language: parsed.language },
                          );
                        }
                      }

                      // Defer until at least one source resolves; for a
                      // stage transition with no payload AND no markdown
                      // body (status still 'running' on the very first
                      // stage emit before the tool's output is persisted)
                      // we fall through to the generic widget update below
                      // and try again on the next 2s poll tick.
                      if (!resolvedPayload) {
                        // Intentional: skip this branch this tick; the
                        // generic stage label update still runs below so
                        // the user sees the "preparing clarifying
                        // questions…" progress widget.
                      } else {
                      const payload = resolvedPayload;
                      const lang: 'en' | 'es' = payload.language === 'es' ? 'es' : 'en';
                      const questions = Array.isArray(payload.clarifyingQuestions)
                        ? payload.clarifyingQuestions
                            .map((q) => (q && typeof q.question === 'string' ? q.question.trim() : ''))
                            .filter((q): q is string => Boolean(q))
                        : [];
                      const initialFindings = typeof payload.initialFindings === 'string'
                        ? payload.initialFindings
                        : '';
                      try {
                        const { mainHtml, buttonsHtml } = formatDeepResearchQualificationHtml(
                          initialFindings,
                          null, // howAnswersHelp — backend doesn't ship this; renderer falls back to static helper text
                          questions,
                          // Distinct button id for the TASK-QUEUE clarifying
                          // card (vs the legacy in-browser
                          // `global-tool-deep-research-skip-btn` whose
                          // handler in useGlobalTools.handleButtonClick
                          // requires an awaiting-input runtime that does not
                          // exist on this path). A dedicated handler in
                          // GlobalChatSystem.tsx routes this id to
                          // sendProgrammaticMessage('skip') which then flows
                          // through midClarifyingFreeTextPreRoute (the
                          // sourceTag below is what triggers that intercept)
                          // so the next turn dispatches deep-research with
                          // skipClarification=true.
                          'task-queue-dr-clarifying-skip-btn',
                          // BUG-DR-CARD-FINAL-CLEANUP (2026-05-20, PR
                          // #196) — secondary CTA is now overridden with
                          // a dedicated id below ("task-queue-dr-
                          // clarifying-answer-btn"), so the
                          // `videoScriptButtonId` positional argument is
                          // unused on this path. We still pass
                          // GLOBAL_VIDEO_SCRIPT_BUTTON_IDS.videoScript
                          // to keep the call shape stable for any
                          // future surface that does not override id.
                          GLOBAL_VIDEO_SCRIPT_BUTTON_IDS.videoScript,
                          undefined,
                          lang,
                          // FINDINGS-FIRST REDESIGN (owner directive
                          // 2026-08-19, supersedes the PR #196 "Answer
                          // questions" secondary): the card carries ONE
                          // button — the helper line built into the card
                          // tells the user to answer any question below
                          // in chat. The 'task-queue-dr-clarifying-
                          // answer-btn' click handler stays for cards
                          // already in thread history.
                          { hidden: true },
                          // Helper line renders on every surface now (it
                          // IS the answer-below affordance) — the flag is
                          // inert in the renderer; false for clarity.
                          false,
                          // BUG-DR-SKIP-ORIGINAL-QUERY (2026-06-05) — stamp the
                          // original DR query onto the skip button so the skip
                          // click recovers it WITHOUT walking the active
                          // thread's history (thread-fragmentation proof).
                          typeof payload.originalQuery === 'string' ? payload.originalQuery : undefined,
                          // PLAN-deep-research-dynamic-clarify-20260713 — round
                          // chip on follow-up rounds of the dynamic clarifying
                          // loop ("round 2 of 3"). Round-1 cards are unchanged.
                          typeof payload.clarifyingRound === 'number' && typeof payload.clarifyingMaxRounds === 'number'
                            ? { round: payload.clarifyingRound, maxRounds: payload.clarifyingMaxRounds }
                            : undefined,
                          // ANSWER-FIRST (2026-08-20): strategic report offer —
                          // only an explicit false from the Stage-1 payload
                          // suppresses the offer/run button.
                          (payload as { offerDeepResearch?: unknown }).offerDeepResearch !== false,
                        );
                        // ANSWER-FIRST (2026-08-20, adversarial-review
                        // blocker): a pure answer card (offer declined +
                        // zero questions) must NOT carry the qualification
                        // sourceTag — midClarifyingFreeTextPreRoute keys on
                        // it and would force-dispatch the declined report on
                        // the user's next free-text message. The answer tag
                        // still renders as a research tile.
                        const isPureAnswerCard =
                          (payload as { offerDeepResearch?: unknown }).offerDeepResearch === false &&
                          questions.length === 0;
                        const finalizePayload = {
                          text: stageLabel,
                          html: mainHtml,
                          workflowActionButtonsHtml: buttonsHtml,
                          sourceTag: isPureAnswerCard ? 'deep_research_answer' : 'deep_research_qualification',
                        };
                        if (helpers.streaming.finalizeStreamingMessageById) {
                          helpers.streaming.finalizeStreamingMessageById(activePendingId, finalizePayload);
                        } else if (typeof activePendingIndex === 'number' && activePendingIndex >= 0) {
                          helpers.streaming.finalizeStreamingMessage(activePendingIndex, finalizePayload);
                        }
                        clarifyingCardFinalized = true;
                        // BUG-DR-STAGE1-UNBILLED (2026-07-13) — remember the
                        // card message so onDone can attach the turn's usage
                        // (see the stash declaration above for the contract).
                        clarifyingCardMessageId = activePendingId;
                        clarifyingCardFinalizePayload = finalizePayload;
                        stopServerBackedProgressTicker();
                        activePendingRemoved = true;
                      } catch (err) {
                        console.warn('[GlobalTools] onTaskStatus clarifying card finalize failed', err);
                      }
                      return;
                      } // end: resolvedPayload truthy branch
                    }

                    try {
                      latestServerBackedStageLabel = stageLabel;
                      refreshServerBackedProgressLabel();
                    } catch (err) {
                      console.warn('[GlobalTools] onTaskStatus widget update failed', err);
                    }
                  },

                  // Track A1 (2026-04-22) — VM emitted structured workflow-handoff.
                  // Remove the thinking indicator and invoke the translator tool
                  // runtime browser-side to generate real PDF/DOCX downloads.
                  onWorkflowHandoff: (handoff) => {
                    try {
                      if (!handoff || !handoff.type) return false;
                      console.log('[GlobalTools] Track A1: onWorkflowHandoff', handoff);
                      const runtime = getOrCreateRuntime('translate' as any, turnOriginatingThreadId);
                      if (!runtime) {
                        console.warn('[GlobalTools] Track A1: translate runtime unavailable — falling through to text rendering');
                        return false;
                      }
                      // Fire and forget; translator tool owns its own streaming UI
                      const targetLanguage = handoff.targetLanguage ||
                        (handoff.type === 'translate-es-to-en' ? 'English' : 'Spanish');
                      runtime.start(message, {
                        matches: true,
                        targetLanguage,
                        inlineContent: handoff.sourceText || undefined,
                      } as any).catch((e: any) => {
                        console.error('[GlobalTools] Track A1: translator runtime failed:', e);
                      });
                      return true; // consumed — skip inline text rendering
                    } catch (e) {
                      console.error('[GlobalTools] Track A1: onWorkflowHandoff error', e);
                      return false;
                    }
                  },

                  onTextChunk: (chunk: string) => {
                    updateStreamActivity();
                    // Replace thinking indicator only when real text is visible.
                    startStreamMessage();
                    removeThinkingIndicator();
                    accumulatedText += chunk;
                    // R14 (2026-08-13 owner directive): raw file dumps never
                    // render in the bubble — the STREAMED VIEW replaces a
                    // labeled dump with a "Generating <name>…" line. The
                    // accumulated text itself stays untouched: finalize's
                    // finalTextOverride (inline-file lift → pointer +
                    // attachment card) owns the final message.
                    const streamedView = maskStreamingFileDump(accumulatedText);
                    // Paced reveal (2026-08-23) instead of painting the whole
                    // accumulation on arrival: identical result, but the text
                    // flows in at one consistent speed whether this lane sent
                    // 4 characters or the entire answer in this call.
                    if (!streamReveal) {
                      streamReveal = createStreamReveal({
                        onPaint: paintStreamedText,
                        immediate: prefersReducedMotion(),
                      });
                    }
                    streamReveal.push(streamedView);
                  },

                  onToolStart: (toolId: string, label: string) => {
                    updateStreamActivity();
                    // Replace generic thinking with the visible tool progress widget.
                    //
                    // BUG-T1-DEAD-THINKING-DOT (2026-05-19): capture the new
                    // widget's id/index and promote it to the active pending
                    // widget so subsequent SSE `thinking` events (deep-research
                    // stage labels emitted from toolRouter.ts:emitProgress)
                    // update THIS widget rather than the old one we are about
                    // to remove below.
                    let nextId: string | null = null;
                    let nextIndex: number = -1;
                    try {
                      latestServerBackedStageLabel = (label || '').trim() || 'still working';
                      const initialLabel = isServerBackedTurn
                        ? buildServerBackedProgressLabel(
                            latestServerBackedStageLabel,
                            Date.now() - serverBackedElapsedBase(),
                          )
                        : latestServerBackedStageLabel;
                      const started = helpers.streaming.startStreamingMessage({
                        text: initialLabel,
                        html: getPendingWidgetHtml(initialLabel),
                        sourceTag: `prest0n_tool_start_${toolId}`,
                      });
                      nextId = started.id;
                      nextIndex = started.index;
                    } catch (err) {
                      console.warn('[GlobalTools] onToolStart: startStreamingMessage failed', err);
                    }
                    // Remove the previous active pending widget (the original
                    // thinking dot, or a prior tool widget) BEFORE rotating the
                    // tracked ids so removeActivePending targets the old one.
                    removeActivePending();
                    // Belt-and-suspenders: cancel the pending hint timers too,
                    // mirroring removeThinkingIndicator's side effects without
                    // double-removing the pending widget we just created.
                    clearProgressHintTimers();
                    if (nextId !== null) {
                      activePendingId = nextId;
                      activePendingIndex = nextIndex;
                      activePendingRemoved = false;
                      // Finding 4: the tool painted a visible surface.
                      paintedThisTurn = true;
                      // Round O (B2 family gap): the rotated widget is a FRESH
                      // message — it does NOT inherit the serverTaskId stamped
                      // onto the original thinking widget (onTaskQueued /
                      // pre-flight stamp), making it orphan-eligible for the
                      // hydrate-reconcile "Interrupted" card on switch-away/
                      // return during a tool phase (the serverTaskId guard in
                      // interruptedTurn.isOrphanedInFlightMessage is the only
                      // protection). Re-stamp the turn-scoped task id
                      // (turnServerTaskId, Round K per-turn closure) onto the
                      // rotated widget so it stays resumable, not "orphaned".
                      if (turnServerTaskId) {
                        try {
                          if (helpers.streaming.updateStreamingMessageById) {
                            helpers.streaming.updateStreamingMessageById(nextId, {
                              serverTaskId: turnServerTaskId,
                            });
                          } else if (nextIndex >= 0) {
                            helpers.streaming.updateStreamingMessage(nextIndex, {
                              serverTaskId: turnServerTaskId,
                            });
                          }
                        } catch (err) {
                          console.warn('[GlobalTools] onToolStart serverTaskId re-stamp failed', err);
                        }
                      }
                    }
                  },

                  onToolResult: (toolId: string, success: boolean, resultHtml?: string, toolError?: string, toolSignal?: { kind: string; app?: string; sessionId?: string; svc?: string; outcome?: string }) => {
                    updateStreamActivity();
                    // P6b: a computer run's card posts whatever the result's
                    // success — a completed run is a success, a stuck one is
                    // the take-over case. Outside the failed-tool gate below.
                    if (toolSignal?.kind === 'desktop_run') maybeShowDesktopRunCard(toolSignal);
                    // 2026-08-12: inline-SSE lane of the APP_NOT_CONNECTED
                    // auto connect-offer (see maybeAutoOfferConnectCard).
                    // `toolId` carries the backend ToolResult.toolName on the
                    // CF lane (streamProcessor toolName fallback).
                    if (success === false) {
                      maybeAutoOfferConnectCard(toolId, toolError, toolSignal);
                      // A connect/authorize card IS the turn's visible
                      // surface (owner repro 2026-08-20: the client's own
                      // surface-ownership stayed blind to card turns).
                      if (toolSignal?.kind || /^(CONNECT_OFFER::|AUTHORIZE_SEND::|AUTO_SENT::)/.test(toolError || '')) {
                        paintedThisTurn = true;
                      }
                    }
                    if (resultHtml) {
                      // Finalize the tool progress indicator with the result
                      helpers.streaming.startStreamingMessage({
                        text: '',
                        html: resultHtml,
                        sourceTag: `prest0n_tool_result_${toolId}`,
                      });
                      // Finding 4: a rendered tool result IS the turn's
                      // visible surface (browser tiles, widgets, etc.).
                      paintedThisTurn = true;
                    }
                  },

                  onDone: (
                    handled: boolean,
                    tokenUsage?: { inputTokens?: number; outputTokens?: number; model?: string; provider?: string; upstreamCostUsd?: number; reasoningTokens?: number },
                    attachmentsSerialized?: Array<{ name: string; type: string; size: number; storageUrl?: string; data?: string }>,
                    // BUG-DR-DONE-FALLBACK (2026-05-21 — follow-up to PR #198,
                    // Slack C0AM8U804KZ/1778281880.499349 msg 1779363309.734849).
                    // When the backend's planner loop suppressed its prose
                    // because a surface-owning tool (deep-research
                    // clarifying-questions card, full-pipeline DR Report,
                    // etc.) already painted the only user-visible surface
                    // for this turn, the SSE `done` event carries this
                    // mirror flag so we can suppress the bare-"Done."
                    // system-message fallback below.
                    surfaceOwnedByTool?: boolean,
                    // BUG-DR-INLINE-REPORT-FINALIZE (2026-05-21 — follow-up
                    // to PR #219, Slack C0AM8U804KZ/1778281880.499349 msg
                    // 1779387730.553859, attachment F0B4YQ14E4F.pdf). Full
                    // Deep Research report markdown for surface-owning DR
                    // full-pipeline turns. Used by the orphan-attachment
                    // branch below to render the inline report ABOVE the
                    // PDF + DOCX chips. Empty / undefined for non-DR
                    // turns and for clarifying-card turns.
                    surfaceOwnedText?: string,
                    // Per-model usage breakdown when multiple ladder models
                    // served this turn (tier fallbacks mid-turn). Attached to
                    // the finalized message so the ⓘ popup renders its
                    // multi-model sections + combined total. 2026-08-16
                    // (Miles): effort + billedSeparately are backend-stamped
                    // and must survive this boundary.
                    tokenUsages?: Array<{ inputTokens?: number; outputTokens?: number; model?: string; provider?: string; upstreamCostUsd?: number; reasoningTokens?: number; effort?: string; billedSeparately?: boolean }>,
                    // R11 (2026-08-04): server-side clean final text for VM
                    // SSE multi-response turns. The live bubble glues
                    // per-response fragments client-side ("I'llTheTheThe");
                    // the backend's R4 reassembly ships the clean text here
                    // so the streamed-bubble finalize below replaces the
                    // glued accumulation before persisting.
                    finalTextOverride?: string,
                  ) => {
                    turnHandled = handled;
                    stopServerBackedProgressTicker();
                    // Smooth streaming (2026-08-23): the turn is terminal on
                    // EVERY onDone path below (including the early-returning
                    // clarifying-card and orphan-deliverable branches), so the
                    // paced reveal paints the rest and stops here — before any
                    // finalize writes the full text — and no queued frame can
                    // repaint a half-revealed prefix over the finished answer.
                    stopStreamReveal();
                    // Turn is terminal — the ingest-progress label has no
                    // pending widget left to paint.
                    stopIngestProgress();
                    // Stop fix (2026-07-31): the turn is over on EVERY onDone
                    // path (including the orphan-deliverable early-return
                    // below) — release this thread's AbortController + live
                    // server-task registration now that there is nothing left
                    // to stop.
                    releaseTurnAbortController();

                    // BUG-DR-ATTACH-WHEN-NO-STREAMING (2026-05-21 — final
                    // fix for the 4-day Skip-to-Deep-Research demo
                    // blocker, Slack C0AM8U804KZ/1778281880.499349 msg
                    // 1779385039.677619, screenshot F0B5AFF7W5T.png).
                    //
                    // On the surface-owning DR full-pipeline turn:
                    //   1. Backend completes end-to-end and uploads PDF +
                    //      DOCX to GCS (deliverable_persist.uploaded x2
                    //      visible in Cloud Logging at 17:31 UTC).
                    //   2. SSE `done` event carries
                    //      `attachmentsSerialized=[PDF, DOCX]` AND
                    //      `surfaceOwnedByTool=true`.
                    //   3. PR #198 zeroed the planner's `finalResponse`
                    //      for surface-owning tools → backend never
                    //      emitted an SSE `text` chunk for this turn →
                    //      `startStreamingMessage` was never called →
                    //      `streamingMessageId` stays null.
                    //   4. Pre-fix `onDone` hit the `streamingMessageId
                    //      === null` else-branch where the ONLY path was
                    //      `decideOnDoneFallback` (PR #207 Layer D). That
                    //      correctly suppresses the bare-`Done.` bubble
                    //      for surface-owning tools, but had NO path to
                    //      surface the deliverables → PDF + DOCX
                    //      silently dropped on the floor.
                    //   5. User saw stage labels → `Done.` bubble →
                    //      infinite spinner. No deliverable tile.
                    //
                    // Fix: when there is no in-flight streaming message
                    // AND the done event carries deliverables, emit a
                    // fresh assistant message with the attachments
                    // BEFORE falling through to the existing branches.
                    // The shape mirrors what the streamingMessageId
                    // branch below builds at lines 2415-2440 — same
                    // filter, same map, same dataUrl fallback,
                    // sourceTag='research_result' so the renderer
                    // routes through the DR attachment variant
                    // (download buttons).
                    //
                    // Token usage on this path is intentionally omitted:
                    // accumulatedText is empty so the existing
                    // estimation heuristic in the streaming branch
                    // would only emit reasoning-tokens passthrough +
                    // model label. The deliverable tile is the surface
                    // that matters; token usage is a follow-up.
                    // BUG-DR-INLINE-REPORT-FINALIZE (2026-05-21 — follow-up
                    // to PR #219). When the backend ships the full Deep
                    // Research report markdown on the done event
                    // (`surfaceOwnedText`), render it inline ABOVE the PDF +
                    // DOCX chips so the user sees the full report body in
                    // the chat thread — not just `Message received.` +
                    // attachment chips (the regression Malaika filed at msg
                    // 1779387730.553859 with screenshot evidence). The
                    // helper stays DOM-free; we render markdown→HTML at
                    // the call site where window/document are available.
                    const orphanDeliverableHandled = handleOrphanResearchDeliverableDone({
                      streamingMessageId,
                      attachmentsSerialized,
                      surfaceOwnedText,
                      addMessage: helpers.addMessage,
                      removeThinkingIndicator,
                      releaseProcessingHeartbeat: releaseProcessingHeartbeatRef.current,
                      // H1 hardening (2026-07-14): release the TURN'S OWN
                      // originating thread (closure constant, Phase 5 rule) —
                      // NOT the shared last-writer-wins currentTurnThreadIdRef,
                      // which a concurrent send on another thread overwrites
                      // during a multi-minute DR run. The old ref read released
                      // the WRONG thread's flag in that race (leaking the
                      // originating thread's spinner). Mirrors onError's
                      // release below, which already used originatingThreadId.
                      currentTurnThreadId: originatingThreadId,
                      // THE ANCHOR (2026-09-18): the same per-turn closure
                      // constant the streaming-finalize branch below already
                      // stamps. Without it this bubble is anchorless and the
                      // server's durable copy of the same report renders as a
                      // SECOND element in the thread.
                      serverTaskId: turnServerTaskId,
                      markdownToHtml,
                      // Token usage (2026-07-13) — surface real compute on the
                      // full-pipeline DR deliverable's ⓘ popup instead of "no
                      // extensive compute". Mapped through the same shared
                      // builder as the streaming-finalize branch below.
                      tokenUsage,
                      tokenUsages,
                      userMessageLength: (message?.text || '').length,
                      // INTERSTITIAL-MESSAGE ELIMINATION (owner directive
                      // 2026-08-13): when this turn already finalized the
                      // clarifying card, a body-only done event (whose
                      // surfaceOwnedText on pre-fix servers is the one-line
                      // skip breadcrumb) must NOT spawn a standalone bubble.
                      // Returning false here falls through to the clarifying
                      // re-finalize block below, which attaches the turn's
                      // usage to the card itself — the most recent real
                      // message — instead of to a phantom breadcrumb bubble.
                      clarifyingCardFinalizedThisTurn: clarifyingCardFinalized,
                      onReleaseError: (releaseErr) => {
                        console.warn(
                          '[useGlobalTools] orphan deliverable releaseProcessingHeartbeat threw:',
                          releaseErr,
                        );
                      },
                    });
                    if (orphanDeliverableHandled) {
                      return;
                    }

                    // Finalize the streaming text message if we had one. If no text/tool
                    // ever appeared, replace the spinner below to avoid a stuck indicator.
                    if (streamingMessageId !== null) {
                      // R11 (2026-08-04): VM SSE multi-response turns glue
                      // per-response fragments client-side into this one
                      // bubble ("I'llTheTheThe"). The backend's R4 reassembly
                      // already produced the clean turn text and ships it as
                      // `finalTextOverride` on the done event — replace the
                      // glued accumulation with it before finalizing/
                      // persisting. Non-empty strings only, so this can never
                      // blank a message, and ONLY on this streamed-bubble
                      // finalize path: clarifying cards, orphan deliverables,
                      // and translation/legal-doc sanitize flows finalize
                      // elsewhere and are untouched.
                      if (typeof finalTextOverride === 'string' && finalTextOverride.trim()) {
                        accumulatedText = finalTextOverride;
                      }
                      // Convert markdown → HTML so the chat renders bullets, bold, headers
                      // correctly instead of showing raw ## / ** / - syntax.
                      // This matches what the legacy orchestrator path does via markdownToHtml().
                      // dr-compose-v2 — when the backend ships deep-research
                      // deliverables (PDF + DOCX) on the done event, surface
                      // them through AttachmentsSection with the
                      // `research_result` sourceTag so the renderer routes
                      // them through the deep-research attachment variant
                      // (download buttons, no Storage hop).
                      // 2026-09-08 (smooth playback): ONE mapper (attachmentRecords) — every
                      // durable locator survives (this copy used to drop storagePath / gcsPath),
                      // and a base64 body becomes a dataUrl only when nothing durable exists.
                      const builtAttachments = (attachmentsSerialized || [])
                        .map((att) => toSerializedAttachment(att as RawAttachmentRecord))
                        .filter((att): att is SerializedAttachment => att !== null);
                      const hasAttachments = builtAttachments.length > 0;
                      // 2026-07-31 one-reposition-per-turn fix: NO `timestamp`
                      // in this finalize payload. Both merge sites (reducer
                      // UPDATE_MESSAGE_BY_ID and streamFinalizePersist's
                      // buildFinalizedPersistedMessage) keep the previous
                      // value when the payload omits it, so the answer bubble
                      // keeps its MINT timestamp instead of being re-dated to
                      // stream-END — the old `new Date()` here stepped the
                      // sidebar sort key (and forced an index write) at the
                      // end of every turn. Turn-terminal ERROR cards below
                      // deliberately keep their fresh timestamps: they are
                      // genuine new activity, not a re-date of an existing
                      // bubble.
                      const finalMsg: Partial<import('../types').ChatMessage> = {
                        text: accumulatedText,
                        html: markdownToHtml(accumulatedText),
                        isUser: false,
                        sourceTag: hasAttachments ? 'research_result' : 'prest0n_session_response',
                        // Round K: carry the turn's task id on the finalized
                        // answer (belt-and-suspenders for the creation stamp —
                        // covers cross-thread routed bubbles the reducer never
                        // saw). The persist boundary
                        // (buildFinalizedPersistedMessage) freezes
                        // taskAnchorId = prest0n_task_<id> from it and then
                        // clears serverTaskId, exactly as before, so resume
                        // never re-attaches a finalized bubble.
                        ...(turnServerTaskId ? { serverTaskId: turnServerTaskId } : {}),
                        ...(hasAttachments
                          ? { attachmentsSerialized: builtAttachments }
                          : {}),
                      };
                      // Token usage (2026-07-13) — mapped through the shared
                      // buildMessageTokenUsage helper so this streaming-finalize
                      // path and the orphan-deliverable path (full-pipeline DR
                      // reports) produce IDENTICAL usage shapes and can never
                      // drift. The helper owns the VM-gateway reliability
                      // heuristics (input < 500 / output <= 10 → estimate from
                      // text length), the never-fabricate-a-model-id rule, the
                      // reasoning-token passthrough, and the authoritative-cost
                      // gate. outputTextLength is the accumulated stream text on
                      // this path.
                      const mappedUsage = buildMessageTokenUsage(tokenUsage, tokenUsages, {
                        outputTextLength: accumulatedText.length,
                        userMessageLength: (message?.text || '').length,
                      });
                      if (mappedUsage.tokenUsage) {
                        finalMsg.tokenUsage = mappedUsage.tokenUsage;
                      }
                      if (mappedUsage.tokenUsages && mappedUsage.tokenUsages.length > 0) {
                        finalMsg.tokenUsages = mappedUsage.tokenUsages;
                      }
                      // ⓘ SERIES LINEUP ON CARDS (2026-08-29): the same
                      // mapped series the answer bubble gets stamps onto
                      // every card tile this turn posted.
                      stampTurnUsageOntoCards(mappedUsage, cardStampEpoch);
                      if (helpers.streaming.finalizeStreamingMessageById) {
                        helpers.streaming.finalizeStreamingMessageById(streamingMessageId, finalMsg);
                      } else {
                        helpers.streaming.finalizeStreamingMessage(streamingMessageIndex, finalMsg);
                      }
                    } else {
                      // BUG-DR-DONE-FALLBACK (2026-05-21 — follow-up to
                      // PR #198, Slack C0AM8U804KZ/1778281880.499349 msg
                      // 1779363662.984699). When the backend signals
                      // surfaceOwnedByTool=true on the SSE done event,
                      // a surface-owning tool (deep-research clarifying
                      // card, full-pipeline DR Report, etc.) already
                      // rendered the only user-visible surface. We MUST
                      // stay silent — emitting a bare "Done." below the
                      // card both duplicates the surface AND displaces
                      // the most-recent prior assistant turn from the
                      // perspective of midClarifyingFreeTextPreRoute,
                      // which breaks Skip-to-Deep-Research routing.
                      //
                      // The four-branch contract lives in the pure
                      // decideOnDoneFallback() helper so it's testable
                      // independently of the React hook surface.
                      //
                      // BUG-DR-FERNANDO-VM-DONE-LEAK (2026-05-21, Layer D
                      // — follow-up to PR #204, Slack
                      // C0AM8U804KZ/1778281880.499349 messages
                      // 1779367362.424239 + 1779366196.402759). Fernando's
                      // dedicated-VM tenant routes through
                      // web-adapter/server.mjs which does NOT emit the
                      // `surfaceOwnedByTool` flag on its done event, so
                      // Layer A (backend-emit) and Layer B (frontend-
                      // consume) of PR #204 never fire on that path. The
                      // clarifying card on Fernando's path is rendered
                      // entirely client-side by parseClarifyingMarkdown-
                      // ToPayload at line 2161 above, which sets
                      // `clarifyingCardFinalized = true` at line 2268.
                      //
                      // Derive surface ownership from that client-side
                      // observable instead: if we already finalized the
                      // pending widget into a clarifying-questions card
                      // in this turn, we own the visible surface and MUST
                      // suppress the bare-"Done." fallback — regardless
                      // of which backend (Cloud Functions task-queue,
                      // in-browser orchestrator, or dedicated-VM web-
                      // adapter) is serving the session. Account-
                      // agnostic, derived from what the user actually
                      // sees on screen, NOT from a backend flag that may
                      // never arrive.
                      // Finding 4 (2026-07-15): a tool widget painted via
                      // onToolStart/onToolResult is a client-owned surface
                      // too — without it, a widget-only turn ending in
                      // done:{handled:true, surfaceOwnedByTool: undefined}
                      // rendered the failure card + Retry under the
                      // successfully painted widget (Retry re-executed the
                      // turn — duplicate).
                      const clientSurfaceOwned =
                        Boolean(surfaceOwnedByTool) || clarifyingCardFinalized || paintedThisTurn;
                      const surfaceActuallyPainted =
                        clarifyingCardFinalized || paintedThisTurn;

                      // BUG-DR-STAGE1-UNBILLED (2026-07-13) — the clarifying
                      // card was finalized mid-task from the stage payload,
                      // BEFORE the backend stamped the turn's usage aggregate
                      // (Stage-1 model tokens + authoritative OpenRouter
                      // cost) on the task doc's result.usage. The done event
                      // now delivers that usage (mapped by
                      // usePrest0nSession.pollTaskUntilTerminal), so
                      // re-finalize the SAME card message with
                      // tokenUsage/tokenUsages attached — the ById finalize
                      // both updates the live message and persists the
                      // updated copy to thread storage, so the ⓘ popup shows
                      // the real numbers instead of "no extensive compute
                      // was necessary" on live AND rebuilt views. No
                      // estimation heuristics here (unlike the VM-lane
                      // streaming branch above): the CF-lane counts come
                      // straight from OpenRouter usage accounting. Wrapped
                      // defensively — a usage-attach failure must never
                      // break the turn's completion flow.
                      const mapCardUsage = (u: {
                        inputTokens?: number;
                        outputTokens?: number;
                        model?: string;
                        provider?: string;
                        upstreamCostUsd?: number;
                        reasoningTokens?: number;
                      }) => ({
                        inputTokens: u.inputTokens ?? 0,
                        outputTokens: u.outputTokens ?? 0,
                        ...(u.reasoningTokens !== undefined && u.reasoningTokens > 0
                          ? { reasoningTokens: u.reasoningTokens }
                          : {}),
                        // Never fabricate a model id; leave unset so the
                        // popup resolver falls back to the provider's
                        // codename family (mirrors the streaming branch).
                        model: u.model && u.model !== 'unknown' ? u.model : undefined,
                        provider: (u.provider && u.provider !== 'unknown'
                          ? u.provider
                          : 'openrouter') as import('../../../components/GlobalChatSystem/types').AIProvider | undefined,
                        // Only carry an authoritative upstream cost
                        // (|| treats a backend-sent 0 as missing).
                        ...(u.upstreamCostUsd ? { upstreamCostUsd: u.upstreamCostUsd } : {}),
                        timestamp: new Date(),
                      });
                      try {
                        if (
                          clarifyingCardFinalized &&
                          clarifyingCardMessageId &&
                          clarifyingCardFinalizePayload &&
                          helpers.streaming.finalizeStreamingMessageById &&
                          (tokenUsage || (tokenUsages && tokenUsages.length > 0))
                        ) {
                          helpers.streaming.finalizeStreamingMessageById(clarifyingCardMessageId, {
                            ...clarifyingCardFinalizePayload,
                            ...(tokenUsage ? { tokenUsage: mapCardUsage(tokenUsage) } : {}),
                            ...(tokenUsages && tokenUsages.length > 0
                              ? { tokenUsages: tokenUsages.map(mapCardUsage) }
                              : {}),
                          });
                        }
                      } catch (cardUsageErr) {
                        console.warn(
                          '[GlobalTools] clarifying card usage attach failed (non-fatal):',
                          cardUsageErr,
                        );
                      }

                      // ⓘ SERIES LINEUP ON CARDS (2026-08-29): the
                      // surface-owned lane's counterpart of the streaming
                      // branch's stamp — same CF-lane mapping as the
                      // clarifying card (no estimation heuristics; the
                      // counts come straight from usage accounting).
                      try {
                        if (tokenUsage || (tokenUsages && tokenUsages.length > 0)) {
                          stampTurnUsageOntoCards({
                            ...(tokenUsage ? { tokenUsage: mapCardUsage(tokenUsage) } : {}),
                            ...(tokenUsages && tokenUsages.length > 0
                              ? { tokenUsages: tokenUsages.map(mapCardUsage) }
                              : {}),
                          }, cardStampEpoch);
                        }
                      } catch (cardStampErr) {
                        console.warn(
                          '[GlobalTools] card series stamp failed (non-fatal):',
                          cardStampErr,
                        );
                      }

                      // INTERSTITIAL-MESSAGE ELIMINATION (2026-08-13): the
                      // orphan branch used to own the heartbeat release for
                      // clarifying turns (it created the phantom breadcrumb
                      // bubble). Now that those turns decline the orphan
                      // branch and suppress instead, release the originating
                      // thread's processing flag here — idempotent, mirrors
                      // the orphan branch's release.
                      if (clarifyingCardFinalized) {
                        try { releaseProcessingHeartbeatRef.current?.(originatingThreadId); }
                        catch (relErr) { console.warn('[useGlobalTools] clarifying-card releaseProcessingHeartbeat threw:', relErr); }
                      }

                      const decision = decideOnDoneFallback(
                        handled,
                        clientSurfaceOwned,
                        Boolean(accumulatedText),
                        surfaceActuallyPainted,
                      );
                      if (decision.kind === 'system') {
                        helpers.addSystemMessage({
                          text: decision.text,
                          sourceTag: decision.sourceTag,
                        });
                      } else if (decision.kind === 'retryable_error') {
                        // Round J empty-turn guard: the terminal carried zero
                        // assistant content and no owned surface (old servers
                        // emit exactly this bare done on an empty VM stream).
                        // Render the retryable card — same interrupted +
                        // regeneratePrompt affordance as the terminal-error
                        // card in onError — and release the originating
                        // thread's processing flag (nothing will stream).
                        //
                        // Finding 8d: attachment-only turns have no prompt
                        // text → no Retry button renders → use the copy that
                        // doesn't advertise one.
                        emptyTurnCardRendered = true;
                        const emptyTurnRegeneratePrompt = (message.text || '').trim() || undefined;
                        helpers.addMessage({
                          text: emptyTurnRegeneratePrompt
                            ? decision.text
                            : sendFailureBubbleText(message.text),
                          isUser: false,
                          timestamp: new Date(),
                          sourceTag: decision.sourceTag,
                          status: 'interrupted',
                          interrupted: true,
                          regeneratePrompt: emptyTurnRegeneratePrompt,
                        });
                        try { releaseProcessingHeartbeatRef.current?.(originatingThreadId); }
                        catch (relErr) { console.warn('[useGlobalTools] empty-turn releaseProcessingHeartbeat threw:', relErr); }
                      }
                      // decision.kind === 'suppress' → stay silent,
                      // the widget IS the response.
                      if (decision.kind === 'suppress' && !clarifyingCardFinalized) {
                        // Card/widget-owned turn that streamed no text (e.g. a
                        // connect-offer card completing with an empty body,
                        // owner incident 2026-08-20 #2): nothing else on this
                        // path releases the originating thread's processing
                        // flag — the stuck-flag sweep would clear it in ~20s,
                        // but the spinner must stop the moment the card IS the
                        // answer. Idempotent; mirrors the clarifying-card
                        // release above.
                        try { releaseProcessingHeartbeatRef.current?.(originatingThreadId); }
                        catch (relErr) { console.warn('[useGlobalTools] card-surface releaseProcessingHeartbeat threw:', relErr); }
                      }
                    }
                    removeThinkingIndicator();
                    // Round O lifecycle hygiene: the turn finalized normally —
                    // release this turn's active-stream registration so no
                    // later cancel/archive can finalize a stale entry.
                    clearTurnActiveStream();
                  },

                  onError: (errorMsg: string, recoverable?: boolean, errorInfo?: TaskErrorInfo) => {
                    console.error('[GlobalTools] Prest0n session error:', errorMsg, 'recoverable:', recoverable, 'errorInfo:', errorInfo);
                    stopServerBackedProgressTicker();
                    // Paint whatever streamed before the failure and stop the
                    // reveal — a recoverable error falls through to the legacy
                    // recovery lanes, which must not race a pending frame.
                    stopStreamReveal();
                    stopIngestProgress();
                    // Stop fix (2026-07-31): a NON-recoverable error is the
                    // turn's terminal (Interrupted card below) — release this
                    // thread's AbortController + live server-task
                    // registration. Recoverable errors fall through to the
                    // legacy recovery lanes, whose own cleanup (post-await /
                    // pattern-match paths) still owns the release.
                    if (!recoverable) {
                      releaseTurnAbortController();
                    }
                    // Replace the spinner with visible recovery/error output.
                    if (recoverable) {
                      // Set orchestrator error so we fall through to pattern matching
                      orchestratorError = new Error(errorMsg);
                    } else {
                      // 2026-06 reliability overhaul: failed task docs now carry
                      // structured errorInfo. Render its human-readable message
                      // (+ a subtle traceId ref for support) instead of the
                      // generic connection blurb — and NEVER the raw errorMsg
                      // string, which can be a JSON/stack dump on this path.
                      // Language mirrors the user's own message for THIS turn
                      // (same detector the deep-research language mirror uses);
                      // userMessage is the fallback when the Es variant is absent.
                      const preferSpanish = detectInputLanguage(message.text || '') === 'es';
                      const humanMessage = (
                        (preferSpanish ? errorInfo?.userMessageEs : undefined)
                        || errorInfo?.userMessage
                        || ''
                      ).trim();
                      // Owner directive 2026-08-13: platform errors carry the
                      // on-call notice. Server-built humanMessage already
                      // arrives with it (fernandoStrict appends centrally);
                      // withOncallNotice skips copies that already state it.
                      //
                      // The no-errorInfo branch does NOT carry the notice
                      // (deferred from #1233): with no structured errorInfo
                      // there is no server-side failure record, because this
                      // is the local connectivity fallback — the request never
                      // reached a server that could file anything. Claiming a
                      // report here was a claim about a filing that cannot
                      // exist, and leaves the user hunting a ref that was
                      // never minted.
                      const failureText = humanMessage
                        ? `${withOncallNotice(humanMessage)} (ref: ${errorInfo!.traceId})`
                        : "I'm having trouble connecting right now. Please try again in a moment.";
                      // Same Interrupted-card + Regenerate affordance as the
                      // refresh-resume path (serverTaskResume): interrupted +
                      // regeneratePrompt make the renderer show the one-click
                      // Regenerate button under the notice.
                      const regeneratePrompt = (message.text || '').trim() || undefined;
                      // Single-writer failure surface (2026-07-31): the server
                      // now durably writes THIS failure as the Interrupted card
                      // under the task anchor (persistTaskFailureToThread —
                      // status 'interrupted', same "(ref: …)" text). Give the
                      // client's card the SAME anchor identity so the two
                      // converge on one Firestore doc / one rendered card
                      // instead of the stacked duplicate the 2026-07-31
                      // incident showed (plain bubble + separate card). The
                      // text is built from errorInfo, never by appending to an
                      // existing message, so the ref suffix cannot double.
                      const failureAnchorId = deriveTaskAnchorId(turnServerTaskId);
                      helpers.addMessage({
                        ...(failureAnchorId
                          ? { id: failureAnchorId, taskAnchorId: failureAnchorId, serverTaskId: turnServerTaskId ?? undefined }
                          : {}),
                        text: failureText,
                        html: buildAuthedInterruptedHtml(failureText, regeneratePrompt),
                        isUser: false,
                        timestamp: new Date(),
                        sourceTag: 'interrupted_turn',
                        status: 'interrupted',
                        interrupted: true,
                        regeneratePrompt,
                      });
                      // Terminal (non-recoverable) error — release the originating
                      // thread's processing flag so the elapsed/"watch" clock stops.
                      // The error card is rendered via addMessage (not a streaming
                      // finalize), so nothing else clears processing on this path;
                      // without this the chip clock keeps ticking until the 10-min
                      // PROCESSING_SAFETY_TIMEOUT floor (the "timer keeps running
                      // after Interrupted" report). Mirrors onDone's release.
                      try { releaseProcessingHeartbeatRef.current?.(originatingThreadId); }
                      catch (relErr) { console.warn('[useGlobalTools] terminal-error releaseProcessingHeartbeat threw:', relErr); }
                    }
                    removeThinkingIndicator();
                    // Round O lifecycle hygiene: the stream reached a terminal
                    // error (recoverable or not) and its widget is gone —
                    // release this turn's active-stream registration.
                    clearTurnActiveStream();
                  },
                };

                try {
                  const serializedAttachments = message.attachmentsSerialized || [];

                  // ============================================================
                  // INLINE-BASE64 SEND GUARD (2026-06 reliability overhaul)
                  // ============================================================
                  // The async turn is persisted into a Firestore task doc with a
                  // hard 1 MiB limit. Attachments whose upload never finished
                  // (no storageUrl AND no gcsPath) keep their inline base64
                  // dataUrl after toCloudFunctionAttachments stripping, and an
                  // oversized doc write used to come back as a confusing backend
                  // 500 mid-turn. Refuse the send up front with an actionable
                  // message instead. sumInlineDataUrlChars mirrors the exact
                  // stripping rule, so this only counts bytes the turn would
                  // genuinely carry inline.
                  // ============================================================
                  // ATTACHMENT-DELIVERY GUARD (2026-09-09)
                  // ============================================================
                  // The inline guard below refuses a turn carrying too many
                  // inline bytes. It cannot see the opposite failure: a file
                  // too large to inline whose upload did not finish carries no
                  // payload at all, so the inline sum is ZERO and the turn
                  // ships with the attachment silently gone.
                  //
                  // Measured in production on 2026-09-09 (50 clips / 398 MB):
                  // the task arrived with attachments: 0, no ingest job was
                  // created, and Prest0n answered — correctly — that it could
                  // not see any clips. Never let that reach the model: the
                  // user cannot tell a lost upload from a broken Prest0n.
                  const attachedNames = (message.attachments || []).map((f) => f?.name || '');
                  const delivery = judgeAttachmentDelivery(attachedNames, serializedAttachments);
                  if (delivery.kind === 'none-delivered') {
                    console.warn(
                      `[GlobalTools] Attachment-delivery guard: refusing turn — ${delivery.attachedCount} file(s) attached, none carry a payload or a storage locator`
                    );
                    helpers.addSystemMessage({
                      text: delivery.attachedCount === 1
                        ? "Your file didn't finish uploading, so this message wasn't sent. Please re-attach it and try again."
                        : `None of your ${delivery.attachedCount} files finished uploading, so this message wasn't sent. Please re-attach them and try again — sending fewer at a time helps with large files.`,
                      sourceTag: 'prest0n_attachment_delivery_guard',
                    });
                    removeThinkingIndicator();
                    return { handled: true, error: undefined };
                  }
                  if (delivery.kind === 'some-dropped') {
                    // Some DID arrive, so the turn is still worth sending —
                    // but say which ones did not, rather than letting the
                    // answer quietly cover a smaller set than was attached.
                    console.warn(
                      `[GlobalTools] Attachment-delivery guard: ${delivery.droppedNames.length} of ${attachedNames.length} attachment(s) did not upload`,
                      delivery.droppedNames
                    );
                    helpers.addSystemMessage({
                      text: `${delivery.droppedNames.length} of your ${attachedNames.length} files didn't finish uploading and aren't included: ${delivery.droppedNames.join(', ')}. Everything else was sent.`,
                      sourceTag: 'prest0n_attachment_delivery_guard',
                    });
                  }

                  const inlineDataUrlChars = sumInlineDataUrlChars(serializedAttachments);
                  if (inlineDataUrlChars > MAX_INLINE_DATAURL_TURN_CHARS) {
                    console.warn(
                      `[GlobalTools] Inline-base64 send guard: refusing turn — ${inlineDataUrlChars} inline dataUrl chars exceeds ${MAX_INLINE_DATAURL_TURN_CHARS} (uploads did not finish)`
                    );
                    helpers.addSystemMessage({
                      text: "Your file(s) didn't finish uploading, so this message wasn't sent. Please re-attach the file(s) and try again.",
                      sourceTag: 'prest0n_inline_attachment_guard',
                    });
                    removeThinkingIndicator();
                    return { handled: true, error: undefined };
                  }

                  // Attachment-only turn (file dropped with no caption): send
                  // something rather than an empty turn. The backend
                  // (turnValidation.buildAttachmentOnlyPrompt) synthesizes the
                  // same text as a safety net for non-webchat ingestion.
                  //
                  // INCIDENT-20260827: this used to ASSERT the task —
                  // "review… summarize… reply in the language of the
                  // document(s)" — replacing what the user had just asked for.
                  // Attaching a file right after being asked to send one is the
                  // most natural thing a user does, and it silently discarded
                  // their request: a translation ask became a Spanish summary,
                  // and Prest0n was blamed for following an instruction the app
                  // had written on the user's behalf.
                  //
                  // It now states the FACT and defers to the conversation, and
                  // the wording is kept identical to the backend's copy —
                  // attachmentOnlyPromptParity.test.ts fails if they drift,
                  // because two copies of a decision is how this one hid.
                  const attachmentNames = serializedAttachments.map((a: any) => a?.name).filter(Boolean).join(', ')
                    || `${serializedAttachments.length} attached document(s)`;
                  const oneFile = serializedAttachments.length === 1;
                  const turnText = message.text?.trim()
                    ? message.text
                    : (serializedAttachments.length
                        ? `I have attached ${attachmentNames} without a message. `
                          + `If I have already said in this conversation what I want done with a file — `
                          + `translating it, for instance — do that with ${oneFile ? 'it' : 'them'} now, in full. `
                          + `Only if nothing in our conversation says what I want: review ${oneFile ? 'this document' : 'these documents'}, `
                          + `tell me what ${oneFile ? 'it is' : 'they are'}, and summarize the key facts, parties, dates and any `
                          + `deadlines, and which matter/case ${oneFile ? 'it' : 'each one'} relates to. `
                          + `Reply in the language I have been writing in, not the document's.`
                        : message.text);
                  const dispatchOptions = buildPrest0nTurnDispatchOptions({
                    serverOwnedAttachmentTurn,
                    preAnalyzedDocumentContent,
                  });
                  // Routine Threads routing hint (2026-08-01 — FIXED backend
                  // contract): resolve the ORIGINATING thread's routineId via
                  // the host lookup; a turn from a routine thread ALWAYS
                  // carries requestedWorkflow:'routines' (regardless of text —
                  // the "resume"-typed-in-thread production RCA). Outside
                  // routine threads the 2026-07-31 text rungs apply unchanged.
                  const hintThreadId = resolveOriginatingThreadId(helpers);
                  let originatingThreadRoutineId: string | null = null;
                  if (hintThreadId && threadRoutineLookupRef.current) {
                    try {
                      originatingThreadRoutineId = threadRoutineLookupRef.current(hintThreadId) || null;
                    } catch {
                      originatingThreadRoutineId = null; // lookup is best-effort — never block the send
                    }
                  }
                  const requestedWorkflow: 'deep-research' | 'routines' | undefined =
                    resolveRequestedWorkflowHint({
                      threadRoutineId: originatingThreadRoutineId,
                      topToolId: turnTopRegexMatch?.tool.id ?? null,
                      messageText: message.text || '',
                    });

                  // ============================================================
                  // Ingest-on-upload progress (2026-06 reliability overhaul)
                  // ============================================================
                  // handleSend stamped message.ingestJobId after the GCS
                  // signed-URL uploads succeeded and prest0nStartIngest answered.
                  // While this turn is pending, mirror the job doc's live
                  // progress onto the visible pending widget through the SAME
                  // surface the task-polling stage labels use
                  // (streamCallbacks.onThinking) — no new UI. Subscription is
                  // torn down by stopIngestProgress from onDone/onError, the
                  // failure branches below, user abort, and hook unmount.
                  const ingestJobId = message.ingestJobId;
                  const ingestAccountId =
                    prest0nSession.session?.accountId || migrationGate.accountId || accountId;
                  if (ingestJobId && ingestAccountId) {
                    try {
                      const db = getFirestore();
                      const jobRef = doc(db, `accounts/${ingestAccountId}/ingestJobs/${ingestJobId}`);
                      unsubscribeIngestProgress = onSnapshot(
                        jobRef,
                        (snap) => {
                          if (!snap.exists()) return;
                          const job = snap.data() as {
                            status?: string;
                            totalFiles?: number;
                            completedFiles?: number;
                            failedFiles?: Array<{ name?: string; reason?: string }>;
                          };
                          if (job.status === 'pending' || job.status === 'processing') {
                            const total = job.totalFiles ?? 0;
                            // Clamp: never paint "3 of 2" if the backend races
                            // counter updates across snapshot batches.
                            const completed = Math.min(Math.max(job.completedFiles ?? 0, 0), total);
                            if (total > 0) {
                              streamCallbacks.onThinking?.(`Processing documents: ${completed} of ${total}…`);
                            }
                            return;
                          }
                          // Terminal (completed/failed): surface partial failures
                          // once, then stop listening — backend stage labels own
                          // the widget from here.
                          const failedCount = Array.isArray(job.failedFiles) ? job.failedFiles.length : 0;
                          if (failedCount > 0) {
                            streamCallbacks.onThinking?.(`${failedCount} document(s) could not be read`);
                          }
                          stopIngestProgress();
                        },
                        (err) => {
                          // Progress is cosmetic — never let a rules/network
                          // error on the listener affect the turn itself.
                          console.warn('[GlobalTools] ingest progress subscription error (non-fatal):', err);
                          stopIngestProgress();
                        },
                      );
                      ingestProgressUnsubsRef.current.add(unsubscribeIngestProgress);
                      // User abort skips onDone/onError entirely — tear down here.
                      abortController.signal.addEventListener('abort', stopIngestProgress, { once: true });
                    } catch (err) {
                      console.warn('[GlobalTools] ingest progress subscription setup failed (non-fatal):', err);
                    }
                  }

                  startServerBackedProgressTicker('still working');

                  // PRE-FLIGHT STAMP (stamp-race fix, 2026-07-10): mint the
                  // durable task id NOW and stamp it onto the visible pending
                  // widget BEFORE the network call. Previously the stamp only
                  // landed when the 202 arrived (onTaskQueued) — a refresh in
                  // that window left the widget without a serverTaskId, so
                  // hydrate finalized it as Interrupted even though the server
                  // task lived. The backend enqueues under this exact id
                  // (validated + idempotent); if the route resolves inline or
                  // an older backend mints its own id, the onTaskQueued
                  // re-stamp below keeps the widget correct.
                  // Delivery ladder (PR-2): the send path minted the id BEFORE the
                  // bubble and hands it down; minting here is now the fallback
                  // for callers that predate the ladder (programmatic sends).
                  const preMintedClientTaskId = (helpers as { clientTaskId?: unknown } | undefined)?.clientTaskId;
                  const clientTaskId = typeof preMintedClientTaskId === 'string' && preMintedClientTaskId.trim()
                    ? preMintedClientTaskId.trim()
                    : mintClientTaskId();
                  deliveryKeyForTurn = deliveryKey(resolveOriginatingThreadId(helpers), userTurnAnchorId(clientTaskId));
                  // STRUCTURAL race guard (session-hardening S1): the stamp
                  // below re-runs the resume effect via state.messages, and
                  // sendTurn's isStreaming flag only flips AFTER its session
                  // pre-flight (seconds-wide in a fresh thread). Register the
                  // id BEFORE stamping so no resume path can claim this task
                  // while the live send is driving it.
                  // Declared here, in the same scope as the failure bubble
                  // below: a refusal decided inside the retry loop has to be
                  // readable where the bubble is built.
                  let turnRefusal: { message: string } | null = null;
                  registerLiveSend(clientTaskId);
                  try {
                    streamCallbacks.onTaskQueued?.(clientTaskId, 'pending');
                  } catch { /* stamp is best-effort */ }

                  let success = false;
                  try {
                    // R8 (2026-08-04, trial-onboarding screenshot): a failed
                    // START is retried automatically before anything is shown
                    // to the user. A fresh account's first turn can lose the
                    // session-creation race (404) or hit a transient 5xx —
                    // both heal in seconds, and the SAME clientTaskId makes
                    // the resend idempotent server-side. Backoff 1.5s → 3s;
                    // an abort (user pressed stop / navigated) stops the loop.
                    for (let attempt = 0; attempt <= SEND_START_RETRY_DELAYS_MS.length; attempt += 1) {
                      success = await prest0nSession.sendTurn(
                        turnText,
                        streamCallbacks,
                        {
                          attachments: serializedAttachments,
                          // ORCHESTRATOR_AUTHORITATIVE_ROUTING: regex hint is
                          // already woven into turnTopAugmentedPageCtx above.
                          pageContext: turnTopAugmentedPageCtx,
                          signal: abortController.signal,
                          documentContent: dispatchOptions.documentContent,
                          responseMode: dispatchOptions.responseMode,
                          threadId: resolveOriginatingThreadId(helpers) || undefined,
                          // Ingest-on-upload: lets the backend reuse the
                          // upload-time extraction results for this turn.
                          ingestJobId,
                          requestedWorkflow,
                          clientTaskId,
                          // Reply to a specific message (2026-09-15): the send
                          // path stamps the bubble's `replyTo` onto the helper
                          // bundle exactly as it hands down the task id.
                          replyTo: (helpers as { replyTo?: import('../../../types/messageReply').MessageReplyRef | null } | undefined)?.replyTo ?? undefined,
                        }
                      );
                      if (success || abortController.signal.aborted) break;
                      // A typed REFUSAL is the server's answer, not a dead
                      // start (2026-09-12). Retrying it buys nothing — the
                      // next attempt is declined identically — and each retry
                      // delays the sentence that tells the reader what to do.
                      turnRefusal = prest0nSession.consumeTurnRefusal?.() ?? null;
                      if (turnRefusal) break;
                      const delayMs = SEND_START_RETRY_DELAYS_MS[attempt];
                      if (delayMs === undefined) break;
                      console.warn(`[GlobalTools] sendTurn failed to start (attempt ${attempt + 1}) — retrying in ${delayMs}ms`);
                      await new Promise((r) => setTimeout(r, delayMs));
                      if (abortController.signal.aborted) break;
                    }
                  } finally {
                    // Send settled (stream done, 202 accepted, or threw) —
                    // from here the task doc exists server-side (or never
                    // will), so the normal resume/poll authorities take over.
                    clearLiveSend(clientTaskId);
                    // Last-resort stop for the paced reveal: onDone/onError
                    // cover the normal terminals, this covers a user abort or
                    // a throw that bypasses both. Idempotent — a no-op when
                    // the callbacks already flushed it.
                    stopStreamReveal();
                  }

                  if (!success) {
                    stopServerBackedProgressTicker();
                    stopIngestProgress();
                    // Delivery ladder (PR-2): the start never happened — the bubble
                    // says "not sent · retry" instead of looking sent.
                    deliveryLedger.markFailed(deliveryKeyForTurn, 'send-start-exhausted');
                    // Stop fix (2026-07-31): the send failed — the turn is
                    // terminal with no poll running; drop the controller +
                    // live-task registration here (the post-await cleanup
                    // skips turns whose pending widget is still on screen).
                    releaseTurnAbortController();
                    // Terminal — release the originating thread's processing flag so
                    // the elapsed/"watch" clock stops (see onError note above).
                    try { releaseProcessingHeartbeatRef.current?.(originatingThreadId); }
                    catch (relErr) { console.warn('[useGlobalTools] terminal-error releaseProcessingHeartbeat threw:', relErr); }
                    console.error('[GlobalTools] Prest0n session sendTurn returned false for migrated account — NOT falling back to legacy orchestrator');
                    // Retries exhausted (B3, 2026-09-02). sendTurn logs each
                    // individual throw at its own catch; this records that the
                    // whole start sequence gave up, which is the moment the
                    // user actually sees "couldn't start this reply" — so a
                    // report of that card can be matched to a server row
                    // instead of to nothing at all.
                    logOrchestratorTurnError(
                      new Error(`sendTurn returned false after ${SEND_START_RETRY_DELAYS_MS.length + 1} start attempts`),
                      'send-start-exhausted',
                    );
                    // 2026-08-08: also retire the pending widget — this branch
                    // used to leave it animating next to the retry card (the
                    // handled-path return never removes the spinner).
                    removeThinkingIndicator();
                    // R8: retries exhausted — surface the RETRYABLE card
                    // (one-click resend, no re-appended user bubble), never
                    // the old dead-end "try again in a moment" text.
                    // A refusal speaks for itself and offers NO Retry: the
                    // button would resend into the same refusal, and this file
                    // already holds the rule that an affordance which cannot
                    // work is never advertised.
                    helpers.addMessage({
                      text: sendFailureBubbleText(turnText, turnRefusal),
                      isUser: false,
                      timestamp: new Date(),
                      sourceTag: SEND_FAILURE_RETRY_SOURCE_TAG,
                      status: 'interrupted',
                      interrupted: true,
                      regeneratePrompt: turnRefusal
                        ? undefined
                        : ((turnText || '').trim() || undefined),
                    });
                    return { handled: true, error: undefined };
                  }

                  // Round J empty-turn guard (RECOVERABLE stream error lane):
                  // onError(recoverable=true) — e.g. the new VM_STREAM_EMPTY
                  // event — only records orchestratorError, which is consulted
                  // by the legacy pattern-match fallback that this migrated
                  // lane never reaches. If that error was the turn's terminal
                  // and nothing visible was produced, surface the retryable
                  // card here instead of returning a silent handled=true.
                  if (
                    orchestratorError
                    && !turnHandled
                    && !emptyTurnCardRendered
                    && streamingMessageId === null
                    && !accumulatedText
                    && !clarifyingCardFinalized
                    // Finding 4: a painted tool widget means the turn was NOT
                    // empty — never stack a retry card under it.
                    && !paintedThisTurn
                  ) {
                    console.error('[GlobalTools] Recoverable stream error with empty turn on migrated lane — surfacing retryable card:', orchestratorError.message);
                    emptyTurnCardRendered = true;
                    // Finding 8d: attachment-only turns have no regenerate
                    // prompt, so no Retry button renders — the copy must not
                    // advertise one.
                    helpers.addMessage({
                      text: sendFailureBubbleText(message.text),
                      isUser: false,
                      timestamp: new Date(),
                      sourceTag: SEND_FAILURE_RETRY_SOURCE_TAG,
                      status: 'interrupted',
                      interrupted: true,
                      regeneratePrompt: (message.text || '').trim() || undefined,
                    });
                    try { releaseProcessingHeartbeatRef.current?.(originatingThreadId); }
                    catch (relErr) { console.warn('[useGlobalTools] empty-turn releaseProcessingHeartbeat threw:', relErr); }
                    removeThinkingIndicator();
                  }

                  return { handled: true };
                } catch (sessionErr) {
                  stopServerBackedProgressTicker();
                  stopIngestProgress();
                  // Stop fix (2026-07-31): terminal — see the !success branch.
                  releaseTurnAbortController();
                  // Terminal — release the originating thread's processing flag so
                  // the elapsed/"watch" clock stops (see onError note above).
                  try { releaseProcessingHeartbeatRef.current?.(originatingThreadId); }
                  catch (relErr) { console.warn('[useGlobalTools] terminal-error releaseProcessingHeartbeat threw:', relErr); }
                  console.error('[GlobalTools] Prest0n session threw for migrated account — NOT falling back to legacy orchestrator:', sessionErr);
                  // 2026-08-08: retire the pending widget on this terminal
                  // error lane too (idempotent when a stream callback already
                  // removed it) — never an animating spinner next to an error.
                  removeThinkingIndicator();
                  // No on-call notice here (follow-up to #1233/#1275): this
                  // catch fires on a CLIENT-side throw while starting the
                  // reply, so no server failure record exists and there is no
                  // traceId to show. A genuine server-side failure arrives
                  // through onError with structured errorInfo instead — that
                  // lane keeps the notice, and its ref.
                  helpers.addSystemMessage({
                    text: "Prest0n hit a runtime error starting this reply. Please try again in a moment.",
                    sourceTag: 'prest0n_session_send_error',
                  });
                  return { handled: true, error: undefined };
                }
              })()
            : await (async () => {
                // ══════════════════════════════════════════════════════════
                // D13-6a / D15 TOMBSTONE (owner rulings 2026-08-24): the
                // in-browser orchestrator brain is SEVERED — do NOT
                // reintroduce a client-side model call or decision here.
                // Signed-out turns never reach this handler (the G1 gate in
                // useGlobalChat routes them to guestRunChatTurn, whose brain
                // is the server-side zero-permission primary —
                // guestPrimaryTurn.ts); the Hidalgo tenant embed rides
                // maestroWidgetChat server-side. This branch is therefore
                // reachable ONLY in a sub-second auth teardown race
                // (isAuthorized held at dispatch, the auth uid vanished
                // before send). Per the 2026-08-21 owner directive — fail
                // LOUDLY, never degrade to a browser brain — it surfaces the
                // retryable send-failure card with the turn unsent. The
                // orchestrator library files are deleted separately (D13-6b)
                // once their shared types/utilities are re-homed.
                // ══════════════════════════════════════════════════════════
                console.error('[GlobalTools] auth teardown race: no account id at send time — surfacing retryable card (browser orchestrator severed, D13-6a)');
                helpers.addMessage({
                  text: sendFailureBubbleText(message.text),
                  isUser: false,
                  timestamp: new Date(),
                  sourceTag: SEND_FAILURE_RETRY_SOURCE_TAG,
                  status: 'interrupted',
                  interrupted: true,
                  regeneratePrompt: (message.text || '').trim() || undefined,
                });
                try { releaseProcessingHeartbeatRef.current?.(originatingThreadId); }
                catch (relErr) { console.warn('[useGlobalTools] teardown-race releaseProcessingHeartbeat threw:', relErr); }
                removeThinkingIndicator();
                // Widened exactly as the removed orchestrator's return type
                // was, so the shared error plumbing below keeps compiling
                // (it is belt-and-suspenders now — this arm never errors).
                return { handled: true, error: undefined as Error | undefined };
              })();

          // Clear timeout on success
          clearTimeout(thinkingTimeoutId);
          // Frozen-clock + stop fix (2026-07-31): on the async 202 lane,
          // sendTurn resolves ~2-3s after dispatch while the background poll
          // (usePrest0nSession.pollTaskUntilTerminal, a floating promise)
          // keeps driving the pending widget. Stopping the elapsed ticker /
          // clearing the AbortController here froze the pane clock at ~0:03
          // and left Stop with nothing to abort. When the turn is still LIVE
          // async (task id stamped + pending widget on screen), keep both —
          // every terminal path (finalize closure, removeActivePending,
          // onDone / non-recoverable onError, Stop) stops the ticker and
          // releases the controller itself.
          const asyncTurnStillLive = turnServerTaskId !== null && !activePendingRemoved;
          if (!asyncTurnStillLive) {
            stopServerBackedProgressTicker();
            // Clear orchestrator abort controller ref (orchestrator complete)
            // — thread-aware + identity-scoped: only this turn's entry.
            releaseTurnAbortController();
          }
          // Cancel any still-pending progress-hint timers. Belt-and-
          // suspenders: removeThinkingIndicator() also clears these, but the
          // orchestrator success path may return without calling it.
          clearProgressHintTimers();
          // Mark orchestrator as inactive (re-enables dormancy detection)
          isOrchestratorActiveRef.current = false;

          // If abort was triggered by user clicking stop (not by thinking timeout),
          // don't remove thinking indicator - it was already finalized as "stopped" by cancelRunningTool
          if (abortController.signal.aborted && !thinkingTimedOut) {
            console.log('[GlobalTools] User cancelled - keeping stopped message');
            return true;
          }

          if (thinkingTimedOut) {
            console.log('[GlobalTools] Orchestrator timed out but returned result - falling through to pattern matching');
          }

          // If orchestrator handled the message WITHOUT errors, return. Do not remove
          // the spinner here: some backend/tool paths report handled before React has
          // painted the final visible output, which creates a blank gap. Visible
          // output/progress/error handlers remove the spinner themselves.
          if (orchestratorResult.handled && !orchestratorResult.error) {
            return true;
          }

          // If orchestrator didn't handle (fall through), keep thinking visible until
          // pattern matching either starts a tool or we show a visible error/no-match.

          // If orchestrator returned an error, log it and fall through to pattern matching.
          // Pattern matching can often recover transparently (e.g., "deep research" trigger).
          if (orchestratorResult.error) {
            console.error('[GlobalTools] ========== ORCHESTRATOR RETURNED ERROR ==========');
            console.error('[GlobalTools] Error:', orchestratorResult.error);
            console.error('[GlobalTools] Error type:', orchestratorResult.error?.constructor?.name);
            console.error('[GlobalTools] Error name:', (orchestratorResult.error as any)?.name);
            const errObj = orchestratorResult.error instanceof Error ? orchestratorResult.error : new Error(String(orchestratorResult.error));
            console.error('[GlobalTools] Error message:', errObj.message);
            console.error('[GlobalTools] Error stack:', errObj.stack);
            console.error('[GlobalTools] Full error object:', JSON.stringify(errObj, Object.getOwnPropertyNames(errObj)));
            console.log('[GlobalTools] Falling through to pattern matching for recovery...');

            orchestratorError = errObj;
          }

          // Fall through to pattern matching (whether orchestrator succeeded with no match, or errored)
        } catch (error: any) {
          // Clear timeout on error
          clearTimeout(thinkingTimeoutId);
          stopServerBackedProgressTicker();
          // Cancel any still-pending progress-hint timers so they don't fire
          // into the failed turn (webchat-streaming-keepalive, 2026-05-14).
          clearProgressHintTimers();
          // Clear orchestrator abort controller ref (turn dead) — thread-aware
          // + identity-scoped: only this turn's entry (and its live-task
          // registration) leaves the maps. The migrated lane catches its own
          // errors and returns, so this outer catch is a genuinely dead turn —
          // no floating poll to preserve.
          releaseTurnAbortController();
          // Mark orchestrator as inactive (re-enables dormancy detection)
          isOrchestratorActiveRef.current = false;

          // If abort was triggered by user clicking stop (not by thinking timeout),
          // don't remove thinking indicator - it was already finalized as "stopped" by cancelRunningTool
          if (abortController.signal.aborted && !thinkingTimedOut) {
            console.log('[GlobalTools] User cancelled - keeping stopped message');
            return true;
          }

          if (thinkingTimedOut) {
            console.log('[GlobalTools] Orchestrator timed out - falling through to pattern matching');
          }

          // Keep thinking visible during pattern-matching recovery. If no recovery
          // happens, the visible error/no-match branch below will remove it.

          // Log the error and fall through to pattern matching for recovery
          console.error('[GlobalTools] ========== ORCHESTRATOR EXCEPTION ==========');
          console.error('[GlobalTools] Exception:', error);
          console.error('[GlobalTools] Exception type:', error?.constructor?.name);
          console.error('[GlobalTools] Exception name:', error?.name);
          console.error('[GlobalTools] Exception message:', error?.message);
          console.error('[GlobalTools] Exception stack:', error?.stack);
          console.error('[GlobalTools] Full exception object:', JSON.stringify(error, Object.getOwnPropertyNames(error || {})));
          console.log('[GlobalTools] Falling through to pattern matching for recovery...');

          orchestratorError = error;
        }
      }

      // ========================================================================
      // Pattern Matching (existing logic)
      // ========================================================================

      // Track C's legacy mirror (loose translate + PDF/DOCX → browser
      // translate tool) ran here as a recovery-path pre-empt until
      // 2026-08-24. Removed under the master rule (Primacy Audit D2): the
      // observation already rode into the turn as TRANSLATE_INTENT_HINT via
      // turnTopAugmentedPageCtx (both lanes), so a turn that lands here
      // failed AFTER the primary saw the evidence — the honest recovery is
      // the retryable send-failure card below (R30.1), never a client-side
      // re-decision. Do NOT reintroduce a pre-empt here.

      // ============================================================
      // ORCHESTRATOR-AUTHORITATIVE ROUTING (2026-05-10)
      // ============================================================
      // The deciding model was given the regex hint as a contextHint
      // BEFORE it ran (threaded via pageContext by the prest0n-session
      // branch; the legacy browser call site is severed — D13-6a). When the flag is ON, we therefore
      // do NOT regex-pre-empt here as a fallback — the LLM was the sole
      // routing authority for this turn. If the orchestrator still failed
      // to handle, surface a recoverable error instead of silently routing
      // to a tool whose hardcoded text would override the LLM's intent.
      // ============================================================
      const match = ORCHESTRATOR_AUTHORITATIVE_ROUTING ? null : matchToolTrigger(message.text);
      if (!match) {
        // R30.1 (2026-08-18 interactive-cat dead-turn incident): an orchestrator
        // failure with no recovery used to post a TERMINAL system error and end
        // the turn. That dead end poisoned the thread — the user's natural "can
        // you try again?" follow-ups don't re-classify like the original ask,
        // and the planner then routes AROUND the "broken" path it sees in
        // history. Surface the RETRYABLE send-failure card instead (same
        // machinery as the Round J empty-turn guard): one click re-dispatches
        // the ORIGINAL text, which re-runs the full pipeline — routing,
        // classification, and lane selection included.
        if (orchestratorError) {
          const errorStr = String(orchestratorError?.message || orchestratorError);
          // 2026-08-18 RCA (parallel session): this branch used to DISCARD the
          // error — nothing reached the server, so production failures here
          // were undiagnosable. Ship it to the decision log before the card.
          // (The debug-in-bubble suffix that landed with that RCA is gone with
          // the terminal bubble itself — the decision log now carries the
          // error, and the card text stays standardized.)
          logOrchestratorTurnError(orchestratorError, 'retryable-card');
          console.error('[GlobalTools] Both orchestrator and pattern matching failed — surfacing retryable card:', errorStr);

          helpers.addMessage({
            text: sendFailureBubbleText(message.text),
            isUser: false,
            timestamp: new Date(),
            sourceTag: SEND_FAILURE_RETRY_SOURCE_TAG,
            status: 'interrupted',
            interrupted: true,
            regeneratePrompt: (message.text || '').trim() || undefined,
          });
          // Justin incident 2026-08-17 (parallel session) + R30.1: a handled
          // turn that ends in a message MUST release the originating thread's
          // processing flag itself — holding it wedged the composer under a
          // permanent "still working" pill.
          try { releaseProcessingHeartbeatRef.current?.(turnOriginatingThreadId); }
          catch (relErr) { console.warn('[useGlobalTools] orchestrator-error releaseProcessingHeartbeat threw:', relErr); }
          removeThinkingIndicator();
          return true; // Handled with a retryable card
        }
        removeThinkingIndicator();
        return false; // No tool matched
      }

      // If orchestrator failed but pattern matching recovered, log the transparent recovery
      if (orchestratorError) {
        console.log(`[GlobalTools] Orchestrator failed but pattern matching recovered → triggering "${match.tool.id}"`);
      }

      const { tool, match: triggerMatch } = match;

      // Enforce client-side tool permission
      const permissionKey = TOOL_PERMISSION_MAPPING[tool.id];
      if (permissionKey && !hasPermission(permissionKey)) {
        console.warn(`[GlobalTools] Permission check failed: User lacks ${permissionKey} to use tool ${tool.id}`);
        helpers.addSystemMessage({
          // ONE refusal (R7; PR-4B, 2026-09-16): the server's own wording from the
          // byte-for-byte mirror — role-neutral (this hook holds no role name), in
          // the asker's own language. Never a second hand-authored sentence.
          text: composePermissionRefusal({
            activityLabel: permissionDisplayName(permissionKey),
            roleNames: [],
            language: pickRefusalLanguage({ lastUserMessage: message.text }),
          }),
          sourceTag: 'permission_denied',
        });
        removeThinkingIndicator();
        // Same heartbeat-ownership contract as the orchestrator-error branch
        // above: a handled turn that ends in a message MUST release the
        // originating thread's processing flag itself.
        try { releaseProcessingHeartbeatRef.current?.(turnOriginatingThreadId); }
        catch (relErr) { console.warn('[useGlobalTools] permission-denied releaseProcessingHeartbeat threw:', relErr); }
        return true; // We handled the turn by displaying a permission message
      }

      // Get or create runtime for the matched tool
      const runtime = getOrCreateRuntime(tool.id, turnOriginatingThreadId);
      if (!runtime) {
        return false;
      }

      // Start the tool with all extracted fields from trigger match
      // This preserves targetLanguage, inlineContent, and other custom fields
      const { matches: _matches, ...triggerInput } = triggerMatch;
      await runtime.start(message, triggerInput);
      // Do not remove the spinner merely because the tool start promise returned;
      // visible tool progress/final/error handlers are responsible for replacing it.
      return true; // Message handled
    };
  }, [isAuthorized, getOrCreateRuntime, sharedContext, recordTransaction]);

  // ========================================================================
  // Button Click Handler
  // ========================================================================

  /**
   * Handle button click events from chat messages.
   * Supports context passing via ButtonClickContext for workflow chaining.
   * Returns true if the button was handled.
   */
  const handleButtonClick = useCallback(
    async (buttonId: string, context?: ButtonClickContext): Promise<boolean> => {
      // OUTWARD-DECISION buttons must answer from the FIRST click of a page
      // session (2026-08-27, batch-tile refresh work): chatHelpersRef is
      // populated by the first TURN, so after a refresh a held-send decision
      // used to fall through unhandled — a silently dead "send it" — until
      // the user happened to send a message. Those branches carry their own
      // messaging fallback (base outward messaging, wired at mount by
      // useGlobalChat); everything else still requires the turn-time
      // helpers bundle exactly as before.
      const isOutwardDecisionButton =
        buttonId.startsWith('authorize-action-') ||
        buttonId.startsWith('batch-send-all-') ||
        // The jump rail is navigation inside an already-rendered card: it
        // must answer from the first click of a page session for the same
        // reason the decisions do, and it needs no turn helpers at all.
        buttonId.startsWith(`${STAGED_SEND_JUMP_BUTTON_ID}-`);
      if (!isAuthorized || (!chatHelpersRef.current && !isOutwardDecisionButton)) {
        return false;
      }

      // Finding 5 (2026-07-15): resolve the thread this click belongs to so
      // button-created runtimes bind to it. Preference order:
      //   1. context.threadId — the ACTIVE thread at click time, stamped by
      //      GlobalChatSystem's delegated click handler (buttons live in a
      //      thread's transcript, so this is the button's home thread);
      //   2. the chat helpers bundle's originatingThreadId (the most recent
      //      send's thread) as a fallback for hosts that don't stamp it.
      // Without an explicit thread, getOrCreateRuntime fell back to the
      // shared last-writer-wins currentTurnThreadIdRef — unbound on fresh
      // sessions, stale-bound after another thread's turn — and
      // canRuntimeCaptureSend then rejected the user's SAME-THREAD typed
      // reply to the awaiting-input tool (wedged workflow).
      const buttonOriginatingThreadId: string | null =
        context?.threadId !== undefined
          ? context.threadId
          : ((chatHelpersRef.current as unknown as { originatingThreadId?: string | null })
              ?.originatingThreadId ?? null);

      // Prevent double-click: if this button is already being processed, swallow the click
      if (processingButtonsRef.current.has(buttonId)) {
        console.warn(`[useGlobalTools] Button "${buttonId}" already processing — ignoring duplicate click`);
        return true;
      }
      processingButtonsRef.current.add(buttonId);

      try {

      // ===================================================================
      // PHASE 2 BROWSER TILE: Inline pause-UI button handling.
      //
      // The browser tile renders three button-id'd elements that don't
      // map cleanly onto the runtime.start()/runtime.resume() lifecycle:
      //
      //   - SUBMIT_ANSWER : POST {BASE_API}/api/sessions/:id/answer with
      //                     { fieldId, value } from the inline input.
      //   - LANG_TOGGLE   : flip the tile's data-locale between en / es
      //                     and re-render the tile in place via the
      //                     existing renderBrowserTileHtml output. The
      //                     frame-stream subscription will overwrite our
      //                     local DOM patch on the next snapshot.
      //   - ANSWER_INPUT  : the text input itself; clicks on the input
      //                     should never count as a workflow trigger.
      //
      // We handle them here, BEFORE findToolByButtonId() lands us in the
      // generic runtime.start() path — that path would re-trigger the
      // browser-use tool's launch step.
      // ===================================================================
      if (buttonId === BROWSER_USE_BUTTON_IDS.ANSWER_INPUT) {
        // Plain input — never a button-click intent.
        return true;
      }
      if (buttonId === BROWSER_USE_BUTTON_IDS.SUBMIT_ANSWER) {
        const buttonEl = context?.buttonElement;
        const sessionId =
          (context?.dataAttributes?.sessionId as string | undefined) ?? '';
        const fieldId =
          (context?.dataAttributes?.fieldId as string | undefined) ?? '';

        // Find the matching input in the same paused-tile container so
        // multiple tiles on the page don't cross-feed values.
        let value = '';
        const container =
          (buttonEl?.closest('[data-browser-tile="true"]') as HTMLElement | null) ||
          buttonEl?.closest('div')?.parentElement ||
          document;
        const inputEl = (container as HTMLElement | Document).querySelector(
          `input[data-button-id="${BROWSER_USE_BUTTON_IDS.ANSWER_INPUT}"][data-session-id="${sessionId}"][data-field-id="${fieldId}"]`
        ) as HTMLInputElement | null;
        if (inputEl) value = inputEl.value;

        if (!sessionId || !fieldId) {
          console.warn('[useGlobalTools] SUBMIT_ANSWER missing session/field id', {
            sessionId, fieldId,
          });
          return true;
        }
        if (!value.trim()) {
          // Surface inline error — empty answer
          if (buttonEl) {
            const parent = buttonEl.parentElement;
            if (parent && !parent.querySelector('.bu-submit-error')) {
              const err = document.createElement('div');
              err.className = 'bu-submit-error';
              err.style.cssText =
                'color:#dc2626;font-size:0.72rem;margin-top:6px;';
              err.textContent = 'Please enter an answer.';
              parent.appendChild(err);
            }
          }
          return true;
        }

        // Loading state on the button while POST is in flight
        if (buttonEl) {
          (buttonEl as HTMLButtonElement).disabled = true;
          buttonEl.dataset.originalLabel = buttonEl.textContent || '';
          buttonEl.textContent = 'sending…';
        }
        // Clear any prior error
        const priorErr = buttonEl?.parentElement?.parentElement?.querySelector(
          '.bu-submit-error'
        );
        if (priorErr) priorErr.remove();

        const result = await postSubmitAnswer({
          sessionId,
          fieldId,
          value,
        });

        if (buttonEl) {
          // Lifecycle-gated restore (2026-08-19): a retired card's submit
          // button stays dead even after the POST settles.
          restoreWorkflowCtaButton(buttonEl);
          buttonEl.textContent = buttonEl.dataset.originalLabel || 'send';
        }
        if (!result.ok) {
          // Surface inline error
          if (buttonEl) {
            const parent = buttonEl.parentElement?.parentElement;
            if (parent) {
              let errEl = parent.querySelector('.bu-submit-error') as HTMLDivElement | null;
              if (!errEl) {
                errEl = document.createElement('div');
                errEl.className = 'bu-submit-error';
                errEl.style.cssText =
                  'color:#dc2626;font-size:0.72rem;margin-top:6px;';
                parent.appendChild(errEl);
              }
              errEl.textContent = `Error: ${result.error ?? 'unknown'}`;
            }
          }
          return true;
        }
        // Success — clear input. Frame-stream subscription will re-render.
        if (inputEl) inputEl.value = '';
        return true;
      }
      if (buttonId === BROWSER_USE_BUTTON_IDS.LANG_TOGGLE) {
        const buttonEl = context?.buttonElement;
        const targetLocale =
          (context?.dataAttributes?.locale as 'en' | 'es' | undefined) ?? 'en';
        // The clarify block sits in the parent tile; flip data-locale on
        // the tile root and let the existing rendered HTML be patched.
        // We do a minimal inline swap: update the visible question text
        // and the EN/ES toggle styles. The frame-stream subscription
        // will re-render the full tile on the next snapshot.
        const tileRoot =
          (buttonEl?.closest('[data-browser-tile="true"]') as HTMLElement | null) ||
          buttonEl?.closest('div')?.parentElement?.parentElement;
        if (!tileRoot) return true;
        tileRoot.setAttribute('data-locale', targetLocale);

        // Update active-locale styling on both toggle buttons.
        const toggleButtons = tileRoot.querySelectorAll(
          `button[data-button-id="${BROWSER_USE_BUTTON_IDS.LANG_TOGGLE}"]`
        );
        toggleButtons.forEach((btn) => {
          const b = btn as HTMLButtonElement;
          const isActive = b.dataset.locale === targetLocale;
          b.style.color = isActive ? 'var(--brand-text)' : '#999';
          b.style.fontWeight = isActive ? '600' : '400';
        });

        // Update the visible clarifying-question copy + input placeholder
        // using the EN/ES copy embedded as data-en / data-es attributes,
        // if the renderer attached them. Falls back gracefully when
        // attributes are absent — the next frame-stream re-render will
        // resolve.
        const clarifyEl = tileRoot.querySelector(
          '[data-i18n="clarify"]'
        ) as HTMLElement | null;
        if (clarifyEl) {
          const enText = clarifyEl.dataset.en;
          const esText = clarifyEl.dataset.es;
          if (enText && esText) {
            clarifyEl.textContent = targetLocale === 'es' ? esText : enText;
          }
        }
        const inputEl = tileRoot.querySelector(
          `input[data-button-id="${BROWSER_USE_BUTTON_IDS.ANSWER_INPUT}"]`
        ) as HTMLInputElement | null;
        if (inputEl) {
          inputEl.placeholder =
            targetLocale === 'es' ? 'Su respuesta…' : 'Your answer…';
        }
        const submitEl = tileRoot.querySelector(
          `button[data-button-id="${BROWSER_USE_BUTTON_IDS.SUBMIT_ANSWER}"]`
        ) as HTMLButtonElement | null;
        if (submitEl) {
          submitEl.textContent = targetLocale === 'es' ? 'enviar' : 'send';
        }
        return true;
      }
      if (buttonId === BROWSER_USE_BUTTON_IDS.OPEN_DEEP_LINK) {
        // The OPEN_DEEP_LINK anchor is rendered as <a target="_blank">.
        // Default browser navigation handles it — we just acknowledge
        // the click so the generic runtime.start() path doesn't fire.
        return true;
      }

      // ===================================================================
      // BATCH SEND TILE (2026-08-27, Justin's report): the batch card's own
      // two button families. The per-row send/skip buttons reuse the
      // authorize-action-* ids and ride the branch below unchanged.
      // ===================================================================
      // "send all remaining" — loops every still-pending row through the
      // SAME per-item transactional decide (the claim stays the only
      // double-send guard), stamps each row as it settles, then posts ONE
      // durable summary so the thread record (and the orchestrator's next
      // turn) knows what became of the batch.
      // THE JUMP RAIL (2026-09-17, owner: "it should be able to easily
      // quickly view all emails suggested within the element"). Pure DOM:
      // scroll the staged-send stack to one row and flash it. It decides
      // nothing, calls nothing, and writes nothing — the whole point is that
      // every staged email stays expanded and readable, and this is how you
      // get to the one you want without hunting.
      if (buttonId.startsWith(`${STAGED_SEND_JUMP_BUTTON_ID}-`)) {
        const actionId = ((context?.dataAttributes?.actionId as string | undefined) || '').trim();
        const target = actionId ? document.getElementById(`p0ac-row-${actionId}`) : null;
        if (target) {
          target.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          target.classList.add('p0ac-row--flash');
          window.setTimeout(() => target.classList.remove('p0ac-row--flash'), 1200);
        }
        return true;
      }

      if (buttonId.startsWith('batch-send-all-')) {
        const batchId = ((context?.dataAttributes?.batchId as string | undefined) || '').trim();
        const allButtonEl = context?.buttonElement;
        if (!batchId) {
          console.warn('[useGlobalTools] batch-send-all button missing batch id', { buttonId });
          return true;
        }
        if (allButtonEl) {
          // The first stamp's re-render replaces this whole tile (the master
          // button renders disabled while any row is in flight) — this
          // direct patch only covers the beat before that.
          allButtonEl.disabled = true;
          allButtonEl.textContent = 'sending…';
          allButtonEl.style.opacity = '0.6';
        }
        const batchHelpers = chatHelpersRef.current;
        const baseForBatch = baseOutwardMessagingRef.current;
        const { sendAllRemaining, hasBatchEntry, rehydrateBatchTileFromDom: rehydrateForSendAll } =
          await import('../tools/batchSendTile');
        const batchMessaging = batchHelpers
          ? {
              addSystemMessage: createMessagingHelpers(batchHelpers, sharedContext).addSystemMessage,
              updateMessageById: batchHelpers.streaming.updateStreamingMessageById
                ? (id: string, update: { workflowActionButtonsHtml?: string }) =>
                    batchHelpers.streaming.updateStreamingMessageById!(id, update)
                : undefined,
            }
          : baseForBatch
            ? {
                addSystemMessage:
                  baseForBatch.addSystemMessage as MessagingHelpers['addSystemMessage'],
                updateMessageById: baseForBatch.updateMessageById || undefined,
              }
            : null;
        if (!batchMessaging) return true;
        // REFRESH SELF-HEALING (2026-08-27): the registry died with the page
        // — rebuild the tile from its own DOM self-description so send-all
        // still knows every row. A miss after this leaves an honest no-op.
        if (!hasBatchEntry(batchId)) {
          try {
            await rehydrateForSendAll(allButtonEl, buttonOriginatingThreadId, batchMessaging);
          } catch { /* the summary === null branch below answers */ }
        }
        const summary = await sendAllRemaining(batchId, batchMessaging);
        if (!summary) return true;
        const trouble = summary.failed + summary.unconfirmed + summary.unreachable;
        batchMessaging.addSystemMessage({
          // Clean sweep → data-only record (the tile's rows and counter ARE
          // the receipt); anything short of one → the renderer says so
          // honestly in one or two sentences, same channel as a single
          // card's failure receipt.
          ...(trouble > 0 ? {} : { renderPolicy: 'never' as const }),
          payload: {
            kind: 'prest0n.batch_send_summary',
            toolId: 'connect-app',
            severity: trouble > 0 ? 'error' : 'success',
            data: { batchId, ...summary },
            summary: trouble > 0
              ? `prest0n: the user pressed "send all remaining" on a batch of held emails. Verified result: ` +
                `${summary.sent} sent and confirmed, ${summary.skipped} skipped, ${summary.failed} failed, ` +
                `${summary.unconfirmed} not confirmed by the provider, ${summary.unreachable} never reached ` +
                `the server (those rows re-armed). The batch card above shows each row's honest state. In ONE ` +
                `or TWO short sentences: say plainly how many went out and how many did not, and give the ` +
                `single next step for the ones that did not (their rows say why). NEVER claim a failed or ` +
                `unconfirmed one was sent.`
              : `prest0n: all ${summary.sent} remaining held emails in the batch were sent and confirmed ` +
                `after the user pressed "send all remaining". The batch card is the receipt. Emit EMPTY ` +
                `content — say nothing.`,
          } as any,
          sourceTag: 'prest0n_batch_send_summary',
        } as any);
        return true;
      }

      // ===================================================================
      // OUTWARD-ACTION AUTHORIZE CARD (2026-08-18): "send it" / "don't
      // send" on the held-email card. The endpoint's transactional claim is
      // the double-send guard (a second click 409s as already-decided). The
      // user's feedback is the RECEIPT this posts (2026-08-20) — the button
      // label is only a glance-state beside it. Handled BEFORE
      // findToolByButtonId() like the connect-card buttons below.
      // ===================================================================
      if (
        buttonId.startsWith('authorize-action-approve') ||
        buttonId.startsWith('authorize-action-retry') ||
        buttonId.startsWith('authorize-action-cancel')
      ) {
        // RETRY (2026-08-24) is an approve of the SAME held draft. It only
        // ever renders on a receipt the server marked `retryable`, i.e. one
        // whose failure proved nothing left the building and which left the
        // hold armed — so it runs the identical path, and the claim
        // transaction remains the double-send guard exactly as before.
        const isRetry = buttonId.startsWith('authorize-action-retry');
        const isApprove = isRetry || buttonId.startsWith('authorize-action-approve');
        const buttonEl = context?.buttonElement;
        // P4 (2026-09-16): the SAME card machinery carries held TEAM AND ROLE
        // changes. Only the words differ — "sending…" under a role change is a
        // false statement about what is happening, and the whole receipt family
        // exists because a button that guesses is worse than no button. The
        // card declares its own kind; the label it already wears distinguishes
        // an approver's "decline" from the asker's own "withdraw".
        const isGovernanceCard = ((context?.dataAttributes?.kind as string | undefined) || '').trim() === 'rbac';
        const priorLabel = (buttonEl?.textContent || '').trim().toLowerCase();
        const isWithdraw = isGovernanceCard && !isApprove && priorLabel.includes('withdraw');
        const actionId = ((context?.dataAttributes?.actionId as string | undefined) || '').trim();
        if (!actionId) {
          console.warn('[useGlobalTools] authorize-action button missing action id', { buttonId });
          return true;
        }
        // BOTH buttons die the moment one is clicked (owner directive
        // 2026-08-25, ask 1): a live sibling on a card being decided is an
        // invitation to a second, contradictory decision. The transactional
        // claim would refuse it anyway — greying makes the refusal visible
        // before the click instead of after.
        const siblingButtons: HTMLButtonElement[] = [];
        if (buttonEl?.parentElement) {
          buttonEl.parentElement
            .querySelectorAll<HTMLButtonElement>(`button[data-action-id="${actionId}"]`)
            .forEach((b) => { if (b !== buttonEl) siblingButtons.push(b); });
        }
        const greySibling = () => {
          for (const b of siblingButtons) {
            b.disabled = true;
            b.style.opacity = '0.5';
            b.style.cursor = 'default';
          }
        };
        if (buttonEl) {
          buttonEl.disabled = true;
          buttonEl.textContent = isGovernanceCard
            ? isApprove ? 'making the change…' : isWithdraw ? 'withdrawing…' : 'declining…'
            : isApprove ? 'sending…' : 'cancelling…';
          buttonEl.style.opacity = '0.7';
          greySibling();
        }
        // CONFIRMATION CLARITY (owner directive 2026-08-20, Miles's repro):
        // this used to label the button "sent ✓" whenever the decide call
        // came back without success:false — the shape of the round trip, not
        // the fate of the email — and every refused decision threw, silently
        // restoring "send it" with nothing said. decidePendingAction now
        // returns a VERIFIED outcome (the provider's own verdict) and never
        // throws, so the button states the truth and ONE receipt lands in the
        // thread for every decision — sent, cancelled, refused, unconfirmed,
        // unreachable alike. That receipt is both the user's durable record
        // (the DOM label dies on rehydrate and the model never sees it) and,
        // through its structured payload, the prompt that makes Prest0n
        // explain a failure instead of going quiet behind a dead button.
        const cardTo = ((context?.dataAttributes?.to as string | undefined) || '').trim();
        const cardSubject = ((context?.dataAttributes?.subject as string | undefined) || '').trim();
        const decideHelpers = chatHelpersRef.current;
        const { decidePendingAction } = await import('../../prest0nSession/client');
        const { renderSendOutcome, stampSendOutcomeInPlace } = await import('../tools/authorizeAction');
        // BATCH ROW (2026-08-27): when this actionId lives on a batch tile,
        // the ROW is the receipt — flip it to "sending…" now, stamp the
        // verified outcome into it after, and skip the separate receipt
        // message. A registry miss (non-batch card, unparseable html) leaves
        // every step below exactly as it was.
        const { markBatchRowSending, stampBatchDecision, hasBatchRow, rehydrateBatchTileFromDom } =
          await import('../tools/batchSendTile');
        // Pre-first-turn fallback (refresh work, same date): before any turn
        // has run this page session, the mount-time messaging carries the
        // receipt and the tile re-render.
        const baseMessaging = baseOutwardMessagingRef.current;
        const rowMessaging = decideHelpers
          ? {
              addSystemMessage: createMessagingHelpers(decideHelpers, sharedContext).addSystemMessage,
              updateMessageById: decideHelpers.streaming.updateStreamingMessageById
                ? (id: string, update: { workflowActionButtonsHtml?: string }) =>
                    decideHelpers.streaming.updateStreamingMessageById!(id, update)
                : undefined,
            }
          : baseMessaging
            ? {
                addSystemMessage:
                  baseMessaging.addSystemMessage as MessagingHelpers['addSystemMessage'],
                updateMessageById: baseMessaging.updateMessageById || undefined,
              }
            : null;
        // REFRESH SELF-HEALING: the registry died with the page — rebuild
        // this tile from its own DOM self-description before deciding, so
        // the row (not a separate message) carries the receipt. A non-batch
        // card has no batch root and returns immediately.
        if (rowMessaging && !hasBatchRow(actionId)) {
          try {
            await rehydrateBatchTileFromDom(buttonEl, buttonOriginatingThreadId, rowMessaging);
          } catch { /* the documented v1 fallback (separate receipt) stands */ }
        }
        if (rowMessaging && isApprove) markBatchRowSending(actionId, rowMessaging);
        const decided = await decidePendingAction(actionId, isApprove ? 'authorize' : 'cancel');
        // The card's own attributes fill in what a refused or unreachable
        // decision could not echo back, so the receipt always names the email.
        const outcome = {
          ...decided,
          to: (decided.to || cardTo || '').trim() || undefined,
          subject: (decided.subject || cardSubject || '').trim() || undefined,
        };
        if (buttonEl) {
          buttonEl.style.opacity = '1';
          if (isGovernanceCard && outcome.kind !== 'unreachable') {
            // `delivered` is the SERVER's verdict that the change was actually
            // made (governanceExecute), never the shape of the round trip —
            // the same rule the send path learned on 2026-08-20.
            buttonEl.textContent = outcome.delivered
              ? 'done'
              : outcome.decision === 'cancel'
                ? isWithdraw ? 'withdrawn' : 'declined'
                : 'not changed';
            if (outcome.delivered) buttonEl.style.background = PREST0_PRIMARY_CTA_BG;
          } else if (outcome.delivered) {
            // 'sent' (owner directive 2026-08-25, ask 1) — the receipt below
            // is the full record; the button is only the glance-state.
            buttonEl.textContent = 'sent';
            buttonEl.style.background = PREST0_PRIMARY_CTA_BG;
          } else if (outcome.kind === 'unreachable') {
            // Nothing was decided server-side, and the claim is transactional
            // (a repeat can never double-send) — so this ONE case re-arms,
            // and the sibling comes back with it.
            buttonEl.textContent = isGovernanceCard
              ? isApprove ? 'make the change' : isWithdraw ? 'withdraw' : "don't"
              : isApprove ? 'send it' : "don't send";
            restoreWorkflowCtaButton(buttonEl);
            for (const b of siblingButtons) {
              // Through the gate, never raw: restoreWorkflowCtaButton is the
              // ONE sanctioned re-enable (it refuses retired cards), and
              // workflowCtaLifecycle.test.ts rejects any bare `.disabled =
              // false` in this hook — which CI caught when this re-arm
              // shipped raw.
              restoreWorkflowCtaButton(b);
              b.style.opacity = '1';
              b.style.cursor = 'pointer';
            }
          } else if (outcome.retryable) {
            // Still armed: the receipt below carries a fresh retry, so this
            // button must not read as a closed matter.
            buttonEl.textContent = 'not sent — try again below';
          } else if (outcome.kind === 'superseded') {
            buttonEl.textContent = 'replaced by a newer draft';
          } else {
            buttonEl.textContent = outcome.decision === 'cancel' ? 'cancelled' : 'not sent';
          }
        }
        // The batch row absorbs the receipt; every other card keeps its
        // separate receipt message exactly as before. The action-decided
        // event below fires either way — the failure-explain turn must not
        // care which surface carried the receipt.
        const stampedIntoBatchRow = rowMessaging
          ? stampBatchDecision(actionId, outcome, rowMessaging)
          : false;
        if (rowMessaging && !stampedIntoBatchRow) {
          // ONE TILE (owner directive 2026-08-29): the receipt stamps ONTO
          // the card that was clicked — the same message re-renders from
          // offer to record, so a decided send never becomes two messages.
          // GUARDS (adversarial review, same day): (a) a click that lives on
          // a BATCH tile never stamps in place — when the registry stamp
          // above missed (post-refresh rehydrate failure), overwriting the
          // whole tile with a single-send receipt would destroy every other
          // row's buttons and record; the documented v1 separate receipt
          // stands there. (b) a button whose DOM is GONE at outcome time
          // (thread switched away or the pane re-rendered mid-round-trip)
          // never stamps: the by-id updater targets the active pane and
          // would silently no-op, leaving NO receipt anywhere — the classic
          // receipt routes to the card's own thread through addMessage.
          // Turn-time and pre-first-turn paths post the SAME receipt —
          // rowMessaging wraps whichever is live.
          const inBatchTile = Boolean(buttonEl?.closest('[data-batch-root]'));
          const cardMessageId = !inBatchTile && buttonEl?.isConnected
            ? (buttonEl.closest('[data-message-id]') as HTMLElement | null)
                ?.getAttribute('data-message-id') || ''
            : '';
          const stampedInPlace = stampSendOutcomeInPlace(cardMessageId, actionId, outcome, rowMessaging);
          if (!stampedInPlace) renderSendOutcome(actionId, outcome, rowMessaging);
        }
        // `success` here means THE EMAIL WENT OUT — not "the request
        // returned". The failure listener (GlobalChatSystem) keys off it to
        // run the Prest0n turn that explains and fixes, and a provider
        // refusal now arrives as a perfectly successful HTTP round trip, so
        // reporting `ok` would silently retire that whole channel.
        // ONE emitter for both lanes (2026-09-17): the React staged-send tile
        // decides the same holds, and a second hand-rolled CustomEvent is how
        // the two would start carrying different details. `success` is derived
        // in one place — a provider refusal arrives as a successful HTTP round
        // trip, so a send counts only when DELIVERED — and `decidedAt` keeps
        // each decision its own event for the listener's per-action dedupe.
        emitActionDecided(actionDecidedDetail(actionId, outcome));
        return true;
      }

      // ===================================================================
      // CONNECT-APP BROWSER LANE (connection ladder lane 3; single-session
      // fix 2026-08-19): "open <app> sign-in" on the catalog-miss card opens
      // the LARGE in-page sign-in surface (DesktopSignInModalHost) — the
      // MODAL is the sole desktop-session starter. The old handler also
      // launched browser-use against the platform-SHARED service here, so
      // every click produced two sessions on two hosts and the login could
      // land in a profile the org's Prest0n never reads. The researched
      // sign-in URL rides the card's data attributes so the modal opens the
      // real page (no session-memory map, no search-results fallback).
      // Handled BEFORE findToolByButtonId() because the prefix belongs to
      // connect-app's buttonIds and the generic path would restart
      // connect-app instead.
      // ===================================================================
      // Popup-blocked reopen (2026-08-19): open the minted OAuth URL from
      // INSIDE the user's click with the shared window name — a direct click
      // is never popup-blocked. The original connect poll is still running,
      // so completion lands in the same path; nothing else to wire here.
      if (buttonId.startsWith(CONNECT_APP_BUTTON_IDS.reopen)) {
        const buttonEl = context?.buttonElement;
        const url = ((context?.dataAttributes?.connectUrl as string | undefined) || '').trim();
        if (!/^https:\/\//i.test(url)) {
          console.warn('[useGlobalTools] connect-app reopen button missing/invalid url', { buttonId });
          return true;
        }
        try {
          window.open(url, 'prest0n-connect');
          if (buttonEl) {
            buttonEl.disabled = true;
            buttonEl.textContent = 'sign-in opened — finish there';
            buttonEl.style.opacity = '0.7';
          }
        } catch (openErr) {
          console.warn('[useGlobalTools] connect-app reopen failed', openErr);
        }
        return true;
      }

      if (buttonId.startsWith(CONNECT_APP_BUTTON_IDS.browser)) {
        const buttonEl = context?.buttonElement;
        const appName = ((context?.dataAttributes?.appName as string | undefined) || '').trim();
        if (!appName) {
          console.warn('[useGlobalTools] connect-app browser-lane button missing app name', {
            buttonId,
          });
          return true;
        }
        const signInUrl = ((context?.dataAttributes?.signInUrl as string | undefined) || '').trim();
        // "sign in again" (2026-09-02 review): the reconnect pill carries the
        // roster's own slug (data-custom-slug); the flow opens the surface ON
        // it and registers nothing — a re-register rewrote the stored
        // scope/access with the chat lane's defaults. Only a reconnect pill
        // may hand a slug across; the flow re-validates its shape.
        const existingSlug = context?.dataAttributes?.reconnect === 'true'
          ? ((context?.dataAttributes?.customSlug as string | undefined) || '').trim()
          : '';
        if (buttonEl) {
          // The modal takes over from here — keep the button settled.
          buttonEl.disabled = true;
          buttonEl.textContent = 'opened ✓';
          buttonEl.style.background = PREST0_PRIMARY_CTA_BG;
        }
        // The DOM-only "opened ✓" above dies with the page. Its DURABLE twin —
        // payload.data.lifecycle.opened (contract C2) — is written by the
        // TRANSCRIPT's delegated click handler (GlobalChatSystem.tsx), the one
        // surface holding both the clicked tile's message and the update
        // dispatch.
        //
        // It used to be attempted HERE, resolving the card's payload through
        // chatHelpersRef's per-thread message reader. That reader is carried
        // only by the PER-TURN helper bundle (useGlobalChat.ts:7007/7061) —
        // the mount-time bundle (6035) has no such field — and it reads the
        // per-thread localStorage copy, which is wired only when the thread
        // switcher is on. So on a freshly reloaded page with no turn yet — a
        // reload, then a click on yesterday's standing card, the exact
        // "day-old card" case the stamp exists for — it silently did nothing
        // (2026-09-02 review). The transcript handler has the live message.
        // One registration + hand-off path (2026-08-19): scope policy and
        // the modal event live in connectCustomFlow — shared with the
        // Integrations panel's custom row.
        void import('../../composio/connectCustomFlow').then(({ beginCustomConnectFlow }) =>
          beginCustomConnectFlow({
            appName,
            ...(signInUrl ? { signInUrl } : {}),
            ...(existingSlug ? { existingSlug } : {}),
            // This click happened INSIDE a conversation (2026-09-03): the
            // chat stamps the thread on, so finishing the sign-in returns
            // here and answers what was asked instead of going quiet.
            fromChat: true,
          })
        ).catch((err) => {
          console.warn('[useGlobalTools] custom connect flow failed to start', err);
        });
        return true;
      }

      // ===================================================================
      // KEY LANE (2026-09-03): the "other ways" api-key row, made actionable.
      //
      // The card offers this from a PREDICTION — the catalog's `auth_schemes`,
      // which is all a client-side catalog copy carries (a live walk found
      // `auth_config_details`, the field the server's rule actually reads, on
      // 0 of 1,501 list items). The prediction agreed with the retrieved truth
      // 24 times out of 24 on a spread sample, but a sample is not a proof for
      // the 1,249 toolkits it would speak for, so the SERVER decides here:
      // `mode: 'key'` returns exactly what the mint needs, and an empty answer
      // is the honest "this app has no key sign-in after all". That case falls
      // back to the browser sign-in the card was already offering — the whole
      // point of attaching this to a row rather than replacing the primary.
      // ===================================================================
      if (buttonId.startsWith(CONNECT_APP_BUTTON_IDS.key)) {
        const buttonEl = context?.buttonElement;
        const appName = ((context?.dataAttributes?.appName as string | undefined) || '').trim();
        const toolkitSlug = ((context?.dataAttributes?.toolkitSlug as string | undefined) || '')
          .trim()
          .toLowerCase();
        const lang = context?.dataAttributes?.lang === 'es' ? 'es' : 'en';
        if (!toolkitSlug || !appName) {
          console.warn('[useGlobalTools] connect-app key button missing app or toolkit', { buttonId });
          return true;
        }
        const restore = buttonEl ? buttonEl.textContent : null;
        if (buttonEl) {
          buttonEl.disabled = true;
          buttonEl.textContent = lang === 'es' ? 'un momento…' : 'one moment…';
        }
        void (async () => {
          let hasKeyLane = false;
          try {
            const { getConnectRequiredFields } = await import('../../composio/ComposioClient');
            const meta = await getConnectRequiredFields(toolkitSlug, { mode: 'key' });
            hasKeyLane = (meta?.required?.length || 0) > 0;
          } catch (err) {
            // Unknown is not "no": we simply could not ask. The browser lane
            // below still connects the app, which is what the user came for.
            console.warn('[useGlobalTools] key-lane field lookup failed', err);
          }
          if (buttonEl) {
            // Through the gate, never raw: a card retired while this lookup
            // was in flight must stay dead, and restoreWorkflowCtaButton is
            // the only thing that knows that.
            restoreWorkflowCtaButton(buttonEl);
            if (restore !== null) buttonEl.textContent = restore;
          }
          if (hasKeyLane) {
            const { CONNECT_SETUP_EVENT } = await import('../../../components/ConnectSetupHost');
            document.dispatchEvent(
              new CustomEvent(CONNECT_SETUP_EVENT, {
                detail: { toolkitSlug, displayName: appName, mode: 'key', lang },
              }),
            );
            return;
          }
          const { routeToBrowserLane } = await import('../../composio/connectLane');
          await routeToBrowserLane({ appName, toolkitSlug, surface: 'chat', chatFirst: true });
        })().catch((err) => {
          console.warn('[useGlobalTools] key lane failed to start', err);
        });
        return true;
      }

      // ===================================================================
      // CONNECT-APP CARD (PLAN-storage-folder-permissions §7.3, 2026-08-11):
      // "connect <App>" buttons on the in-chat connect card. Handled here,
      // BEFORE findToolByButtonId(), because the generic registry path
      // would runtime.start() the connect-app tool and re-post the offer
      // card instead of connecting; the click needs (a) the toolkit slug
      // from the button's data attributes, (b) click-time OAuth minting +
      // popup + poll (Onboarding.connectAppInFlow pattern), and (c)
      // loading-state patching on the clicked button — same reasoning as
      // the Phase 2 browser-tile buttons above. Per-app button ids
      // (`${prefix}-${slug}`) mean the double-click guard is per app, so
      // two different apps can connect concurrently.
      // ===================================================================
      if (buttonId.startsWith(CONNECT_APP_BUTTON_IDS.connect)) {
        const buttonEl = context?.buttonElement;
        const toolkitSlug = (
          (context?.dataAttributes?.toolkitSlug as string | undefined) ||
          buttonId.slice(CONNECT_APP_BUTTON_IDS.connect.length).replace(/^-/, '')
        ).trim();
        const toolkitName = (
          (context?.dataAttributes?.toolkitName as string | undefined) || toolkitSlug
        ).trim();
        const connectHelpers = chatHelpersRef.current;
        if (!toolkitSlug || !connectHelpers) {
          console.warn('[useGlobalTools] connect-app button missing toolkit slug or helpers', {
            buttonId,
            toolkitSlug,
          });
          // No connect was attempted — never let the delegated handler
          // paint the done state over a click that did nothing.
          if (context?.buttonElement) context.buttonElement.dataset.prest0nKeepLive = 'true';
          return true;
        }

        // Loading state while the OAuth mint + popup + poll are in flight.
        const originalLabel = buttonEl?.textContent || '';
        if (buttonEl) {
          buttonEl.disabled = true;
          buttonEl.dataset.originalLabel = originalLabel;
          buttonEl.textContent = 'connecting…';
          buttonEl.style.opacity = '0.7';
        }

        // C3b email journey: ask WHICH inbox before the OAuth mint — the same
        // modal the Integrations panel shows (EmailIntentPromptHost renders
        // it; the channel degrades to no-intent when no host is mounted, so
        // the click never dead-ends). Cancel restores the button untouched.
        let connectIntent: { intendedAddress?: string | null } | undefined;
        if (isEmailJourneySlug(toolkitSlug)) {
          const intentAnswer = await requestEmailIntent(toolkitSlug, toolkitName);
          if (intentAnswer.cancelled) {
            if (buttonEl) {
              restoreWorkflowCtaButton(buttonEl);
              buttonEl.textContent = buttonEl.dataset.originalLabel || originalLabel;
              buttonEl.style.opacity = '1';
              // TRUTHFUL BUTTON STATE (2026-08-25 adversarial review): no
              // OAuth was ever attempted — without this marker the delegated
              // handler painted the persistent done state over the cancel.
              buttonEl.dataset.prest0nKeepLive = 'true';
            }
            return true;
          }
          if (intentAnswer.intendedAddress) {
            connectIntent = { intendedAddress: intentAnswer.intendedAddress };
          }
        }

        try {
          const outcome = await launchConnectFlow({
            toolkitSlug,
            displayName: toolkitName,
            ...(connectIntent ? { intent: connectIntent } : {}),
            // WRAPPED helpers (2026-08-24 WhatsApp repro): the raw sink
            // stored the structured events but never narrated them — the
            // needs-setup card landed as an EMPTY tile with just a pill.
            // The wrapper routes payloads through the structured-event
            // renderer exactly like the auto-offer lanes.
            messaging: createMessagingHelpers(connectHelpers, sharedContext),
          });
          if (buttonEl) {
            if (outcome === 'connected') {
              // Keep the button disabled and mark it done — the structured
              // composio.connection_complete event carries the confirmation.
              // Brand-consistent done state (2026-08-12): ink + gold ring,
              // matching the tile's selected-button treatment.
              buttonEl.textContent = '✓ connected';
              buttonEl.style.opacity = '1';
              buttonEl.style.background = '#1C1917';
              buttonEl.style.boxShadow = '0 0 0 2px var(--brand-gold)';
            } else if (outcome === 'unsupported') {
              // RESOLVED, not failed (2026-09-02): this app has no one-click
              // sign-in, and the flow has already posted the browser-lane
              // card below. Re-inviting the same click would only re-post
              // that card, so the button retires here and points at it.
              restoreWorkflowCtaButton(buttonEl);
              buttonEl.textContent = 'see the sign-in option below';
              buttonEl.style.opacity = '0.7';
              (buttonEl as HTMLButtonElement).disabled = true;
              buttonEl.style.cursor = 'default';
            } else {
              // timeout / denied / error / needs-setup — re-enable so the
              // user can retry (a fresh click mints a fresh OAuth URL; never
              // re-use one). Lifecycle-gated: a retired card stays dead.
              // TRUTHFUL BUTTON STATE (2026-08-24 WhatsApp repro): mark the
              // element so the delegated click handler does NOT paint the
              // "✓ + disabled" selection state or persist a selected index —
              // a failed connect used to end checked-off anyway.
              restoreWorkflowCtaButton(buttonEl);
              buttonEl.textContent = buttonEl.dataset.originalLabel || originalLabel;
              buttonEl.style.opacity = '1';
              buttonEl.dataset.prest0nKeepLive = 'true';
            }
          }
        } catch (connectErr) {
          console.warn('[useGlobalTools] connect-app flow failed', connectErr);
          if (buttonEl) {
            restoreWorkflowCtaButton(buttonEl);
            buttonEl.textContent = buttonEl.dataset.originalLabel || originalLabel;
            buttonEl.style.opacity = '1';
            buttonEl.dataset.prest0nKeepLive = 'true';
          }
        }
        return true;
      }

      // ===================================================================
      // RETRY-AS (C3b, 2026-08-21): [connect <address> instead] on a
      // mismatch / no-mailbox verification card — remove the wrong sign-in,
      // then relaunch with the SAME intended address (no re-prompt; the
      // intent rides the button's data attributes so it survives reloads).
      // ===================================================================
      if (buttonId.startsWith(CONNECT_APP_BUTTON_IDS.retryAs)) {
        const buttonEl = context?.buttonElement;
        const toolkitSlug = ((context?.dataAttributes?.toolkitSlug as string | undefined) || '').trim();
        const toolkitName = ((context?.dataAttributes?.toolkitName as string | undefined) || toolkitSlug).trim();
        const intendedAddress = ((context?.dataAttributes?.intendedAddress as string | undefined) || '').trim();
        const wrongConnectionId = ((context?.dataAttributes?.connectionId as string | undefined) || '').trim();
        const retryHelpers = chatHelpersRef.current;
        if (!toolkitSlug || !intendedAddress || !retryHelpers) {
          console.warn('[useGlobalTools] retry-as button missing slug/address/helpers', { buttonId });
          return true;
        }
        const originalLabel = buttonEl?.textContent || '';
        if (buttonEl) {
          buttonEl.disabled = true;
          buttonEl.dataset.originalLabel = originalLabel;
          buttonEl.textContent = 'reconnecting…';
          buttonEl.style.opacity = '0.7';
        }
        try {
          // Best-effort removal of the wrong sign-in FIRST — a failed
          // disconnect must not block the relaunch (the fresh connection
          // becomes primary-by-verification either way).
          if (wrongConnectionId) {
            try { await disconnectAccount(wrongConnectionId); } catch (discErr) {
              console.warn('[useGlobalTools] retry-as disconnect failed (relaunching anyway)', discErr);
            }
          }
          const outcome = await launchConnectFlow({
            toolkitSlug,
            displayName: toolkitName,
            intent: { intendedAddress },
            messaging: {
              addSystemMessage: (input) => retryHelpers.addSystemMessage(input),
            },
          });
          if (buttonEl) {
            if (outcome === 'connected') {
              buttonEl.textContent = '✓ reconnected';
              buttonEl.style.opacity = '1';
              buttonEl.style.background = '#1C1917';
              buttonEl.style.boxShadow = '0 0 0 2px var(--brand-gold)';
            } else {
              restoreWorkflowCtaButton(buttonEl);
              buttonEl.textContent = buttonEl.dataset.originalLabel || originalLabel;
              buttonEl.style.opacity = '1';
            }
          }
        } catch (retryErr) {
          console.warn('[useGlobalTools] retry-as flow failed', retryErr);
          if (buttonEl) {
            restoreWorkflowCtaButton(buttonEl);
            buttonEl.textContent = buttonEl.dataset.originalLabel || originalLabel;
            buttonEl.style.opacity = '1';
          }
        }
        return true;
      }

      // Find tool by button ID
      const buttonMatch = findToolByButtonId(buttonId);
      if (!buttonMatch) {
        return false;
      }

      const { tool, buttonKey } = buttonMatch;

      // SPECIAL CASE: "skip" button for deep research
      // This button is meant to skip to Stage 3, not restart the workflow
      // Check if deep research is awaiting input (stage1_confirm)
      // Use getOrCreateRuntime (not .get) so persisted state is restored after page reload.
      if (buttonKey === 'skip' && tool.id === 'deep-research') {
        // Finding 5: bind to the clicking thread (see buttonOriginatingThreadId).
        let drRuntime = getOrCreateRuntime('deep-research', buttonOriginatingThreadId);

        // CROSS-DEVICE FALLBACK: If runtime doesn't have awaiting-input state,
        // the user may be on a different device where localStorage is empty.
        // Try fetching from Firestore directly and restoring before resuming.
        if (drRuntime && !drRuntime.isAwaitingInput()) {
          const drState = drRuntime.getState() as Record<string, unknown> | undefined;
          if (drState?.stage !== 'stage1_confirm' && drState?.stage !== 'stage1_retry') {
            try {
              const fbUser = getAuth().currentUser;
              if (fbUser) {
                const db = getFirestore();
                const snap = await getDoc(doc(db, 'users', fbUser.uid, 'workflow_state', 'deep_research'));
                if (snap.exists()) {
                  const data = snap.data();
                  if (data?.stage === 'stage1_confirm' && data.originalQuery) {
                    const payload = { ...data, savedAt: data.savedAt?.toMillis?.() || Date.now() };
                    localStorage.setItem('prest0_dr_workflow_state', JSON.stringify(payload));
                    // Merge with initial state defaults to ensure all required fields exist
                    const initialDefaults = tool.createInitialState() as Record<string, unknown>;
                    const fullState = { ...initialDefaults, ...payload };
                    drRuntime.restoreState(fullState as any, 'qualify');
                    console.log('[useGlobalTools] Skip button: restored state from Firestore (cross-device)');
                  }
                }
              }
            } catch (e) {
              console.warn('[useGlobalTools] Skip button Firestore fallback failed:', e);
            }
          }
        }

        if (drRuntime?.isAwaitingInput()) {
          console.log('[useGlobalTools] Skip button: resuming deep research with "skip"');
          await drRuntime.resume('skip', undefined, undefined);
          return true;
        }
        // FALLBACK: check tool state directly when isAwaitingInput flag was lost
        const drState = drRuntime?.getState() as Record<string, unknown> | undefined;
        if (drRuntime && (drState?.stage === 'stage1_confirm' || drState?.stage === 'stage1_retry')) {
          console.warn(
            `[useGlobalTools] Skip button RECOVERY: isAwaitingInput=false but stage=${drState.stage} — force-resuming`
          );
          await drRuntime.resume('skip', undefined, undefined);
          return true;
        }

        // If the persisted checkpoint is gone, do not fall through to the
        // generic runtime.start() path. That starts Deep Research with an empty
        // query and produces a misleading "topic required" message from stale
        // cross-device buttons.
        chatHelpersRef.current?.addSystemMessage({
          text: 'This deep research checkpoint is no longer resumable. Refresh to load the latest history, or start a new request with `deep research [topic]`.',
          sourceTag: 'deep_research_resume_expired',
        });
        return true;
      }

      // SAFETY NET: If deep research is actively running (e.g., stage3 in progress),
      // swallow the click instead of falling through to runtime.start() which would restart the workflow
      if (tool.id === 'deep-research') {
        const drRuntime = getOrCreateRuntime('deep-research', buttonOriginatingThreadId);
        if (drRuntime?.isRunning() && !drRuntime.isAwaitingInput()) {
          console.warn('[useGlobalTools] Deep research is actively running — swallowing button click');
          return true;
        }
      }

      // Get or create runtime for the target tool.
      // Finding 5: explicit thread binding so the runtime's awaiting-input
      // capture accepts the user's same-thread typed reply.
      const runtime = getOrCreateRuntime(tool.id, buttonOriginatingThreadId);
      if (!runtime) {
        return false;
      }

      if (tool.id === 'document-analyzer' && buttonKey === 'retry') {
        const retryFiles = sharedContext.lastDocumentAnalyzerFilesRef?.current || [];
        if (!retryFiles.length) {
          chatHelpersRef.current?.addSystemMessage({
            text: 'No previous attachment is available for retry. Please re-attach the document and try again.',
            sourceTag: 'document_analyzer_retry_missing_files',
          });
          return true;
        }

        const fallbackQuery =
          sharedContext.lastTranslatableContentRef.current?.source === 'document_analysis_failed'
            ? sharedContext.lastTranslatableContentRef.current.content
            : 'Retry document analysis';

        const triggerMessage: ChatMessage = {
          text: `[Button: ${buttonKey}]`,
          isUser: true,
          timestamp: new Date(),
          attachments: retryFiles,
        };

        console.log('[useGlobalTools] Retrying document analyzer with forceRefresh=true', {
          files: retryFiles.map((f) => f.name),
        });

        await runtime.start(triggerMessage, {
          files: retryFiles,
          query: fallbackQuery,
          forceRefresh: true,
        });
        return true;
      }

      // KEY FIX: If tool is awaiting input, resume instead of restart
      // This handles "proceed" buttons in multi-stage workflows (e.g., video script)
      // Without this check, clicking proceed would reset state and show questions again
      if (runtime.isAwaitingInput()) {
        // Resume the paused workflow - button acts as "proceed" with empty input
        await runtime.resume('', undefined, undefined);
        return true;
      }

      // CONTEXT INJECTION: If context has workflow output, inject into shared context
      // This ensures downstream tools (like video script) can find the content
      if (context?.workflowOutput) {
        console.log('[useGlobalTools] Injecting workflow output into shared context:', context.workflowOutput.id);
        const translatableContent = toTranslatableContent(context.workflowOutput);
        setLastTranslatableContent(translatableContent);
        lastTranslatableContentRef.current = translatableContent;
      }

      // DOCUMENT CONTEXT BRIDGING: Ensure document analysis is available for downstream workflows
      // This handles cases where document was analyzed earlier and button click needs the context
      if (context?.linkedDocumentId) {
        const docAnalysis = getDocumentAnalysis(context.linkedDocumentId);
        if (docAnalysis && !hasDocumentCache(sharedContext)) {
          console.log('[useGlobalTools] Bridging document analysis from stored context:', context.linkedDocumentId);
          sharedContext.setDocumentContent(docAnalysis.analysis);
        }
      } else if (!hasDocumentCache(sharedContext)) {
        // Try to get latest document analysis if no specific ID provided
        const latestDoc = getLatestDocumentAnalysis();
        if (latestDoc) {
          console.log('[useGlobalTools] Bridging latest document analysis:', latestDoc.id);
          sharedContext.setDocumentContent(latestDoc.analysis);
        }
      }

      // DEEP RESEARCH FROM BUTTON: Extract topic from workflow output title
      if (tool.id === 'deep-research' && buttonKey === 'start') {
        // Get topic from workflow output context
        const topic = context?.workflowOutput?.title ||
                      lastTranslatableContentRef.current?.title ||
                      'immigration law topic';

        console.log('[useGlobalTools] Deep research button: using topic:', topic);

        const triggerMessage: ChatMessage = {
          text: `[Button: ${buttonKey}]`,
          isUser: true,
          timestamp: new Date(),
        };

        // Start with topic as query
        await runtime.start(triggerMessage, { query: `deep research ${topic}` });
        return true;
      }

      // UNIFIED VIDEO-SCRIPT PATH: Pre-collect content before starting
      // This ensures consistent content detection regardless of initiation method
      if (tool.id === 'video-script') {
        // Dynamic import to avoid circular dependencies
        const { collectVideoScriptContent } = await import('../tools/videoScript');
        const sourceContent = collectVideoScriptContent(sharedContext, {
          query: undefined,
          documentContent: sharedContext.documentContent,
        });

        console.log('[useGlobalTools] Video script button: pre-collected sourceContent:', !!sourceContent);

        const triggerMessage: ChatMessage = {
          text: `[Button: ${buttonKey}]`,
          isUser: true,
          timestamp: new Date(),
        };

        // Start with pre-collected content
        await runtime.start(triggerMessage, { sourceContent });
        return true;
      }

      // Otherwise, start fresh workflow (generic path for other tools)
      const triggerMessage: ChatMessage = {
        text: `[Button: ${buttonKey}]`,
        isUser: true,
        timestamp: new Date(),
      };

      await runtime.start(triggerMessage);

      return true;

      } finally {
        processingButtonsRef.current.delete(buttonId);
      }
    },
    [isAuthorized, getOrCreateRuntime, setLastTranslatableContent, lastTranslatableContentRef, sharedContext]
  );

  // ========================================================================
  // Running Tool Helpers
  // ========================================================================

  /**
   * Get the currently running tool ID, if any.
   */
  const getRunningTool = useCallback((): GlobalToolId | null => {
    for (const [toolId, runtime] of toolRuntimesRef.current.entries()) {
      if (runtime.isRunning()) {
        return toolId;
      }
    }
    return null;
  }, []);

  /**
   * Check if a specific tool is running.
   */
  const isToolRunning = useCallback((toolId: GlobalToolId): boolean => {
    const runtime = toolRuntimesRef.current.get(toolId);
    return runtime?.isRunning() ?? false;
  }, []);

  /**
   * Cancel running operations (orchestrator + workflows).
   *
   * Thread-isolation: when `threadId` is supplied, cancels only that
   * thread's in-flight orchestrator request — a per-chip cancel UI must
   * pass its threadId so it never aborts a different thread's request.
   * Without an id, falls back to legacy "cancel everything" behaviour
   * for the global stop button and dormancy paths.
   *
   * Converts any active pending widget to a permanent "stopped" message.
   */
  const cancelRunningTool = useCallback((threadId?: string | null, reason?: 'user_stop' | 'thread_archived'): void => {
    // 1. Finalize the CANCELLED THREAD's active stream with "stopped" BEFORE
    //    aborting — converts its spinning widget to a permanent "stopped"
    //    bubble in that thread's history.
    //
    //    Round O (false-"stopped" root fix): this step used to finalize ONE
    //    global slot regardless of the passed threadId, so archiving thread B
    //    stamped "stopped" onto thread A's live turn. It now finalizes ONLY
    //    the entry registered for the cancelled thread; with no threadId
    //    (legacy global stop / single-thread surfaces, where the Stop path
    //    resolves the active thread to null) it keeps the historical
    //    stop-everything semantics.
    //
    //    F1 (2026-08-03, owner screenshot triage): the word "stopped" is a
    //    claim the USER stopped the turn. Archive/delete cancels (reason
    //    'thread_archived') skip the visible stamp entirely — the thread is
    //    going away, and a "stopped" bubble in a thread the user merely
    //    archived read as an unexplained failure.
    const stoppedContent = {
      text: 'stopped',
      html: getStoppedWidgetHtml(),
      sourceTag: 'cancelled',
    };
    const finalizedCount = reason === 'thread_archived'
      ? 0
      : activeStreamRegistryRef.current.finalizeForCancel(
        threadId ?? null,
        stoppedContent,
      );

    // Round O review F2: a turn dispatched before the thread store hydrated
    // (or an auth-deferred resend pinned to null) registers under the null
    // key yet renders in the visible pane. If a thread-scoped stop finds
    // NOTHING for that thread — no stream entry and no abort controller —
    // fall back to the legacy global stop so Stop can never silently no-op.
    //
    // F1 (2026-08-03): the fallback is now EXPLICIT-STOP ONLY. For archive/
    // delete cancels it could stamp "stopped" onto a DIFFERENT thread's live
    // turn (the null-key slot renders in the visible pane) — the exact
    // cross-thread mislabel class Round O fixed for the scoped path.
    let effectiveThreadId: string | null = threadId ?? null;
    if (
      reason !== 'thread_archived' &&
      effectiveThreadId !== null &&
      finalizedCount === 0 &&
      !orchestratorAbortByThreadRef.current.has(effectiveThreadId) &&
      // Stop fix (2026-07-31): a thread whose controller is gone but whose
      // SERVER task is still registered (e.g. the live poll exhausted and
      // detached) is NOT "nothing to stop" — never widen its stop to a
      // global cancel of every thread's task.
      !liveServerTaskByThreadRef.current.has(effectiveThreadId)
    ) {
      activeStreamRegistryRef.current.finalizeForCancel(null, stoppedContent);
      effectiveThreadId = null;
    }

    // 2. Abort orchestrator(s)
    if (effectiveThreadId) {
      const c = orchestratorAbortByThreadRef.current.get(effectiveThreadId);
      if (c) {
        try { c.abort('user_cancelled'); } catch { /* ignore */ }
        orchestratorAbortByThreadRef.current.delete(effectiveThreadId);
      }
    } else {
      // Global cancel. 2026-08-14 narrowing (owner rule: Stop interrupts
      // ONLY the turn it was pressed on): a widened thread-scoped Stop may
      // abort at most ONE controller — the null/legacy slot if present,
      // else an unambiguous single registration. With two or more live
      // controllers the widened stop no-ops with a warning instead of
      // killing every other thread's in-flight turn. A genuine global
      // cancel (threadId passed as null by archive-all/logout paths) keeps
      // full-clear semantics via reason.
      const entries = Array.from(orchestratorAbortByThreadRef.current.entries());
      const widened = threadId != null; // we got here via the widening fallback
      if (widened && entries.length > 1) {
        console.warn(
          `[GlobalTools] widened stop for thread ${threadId} found ${entries.length} live controllers — refusing cross-thread abort`
        );
      } else {
        for (const [tid, c] of entries) {
          try { c.abort('user_cancelled'); } catch { /* ignore */ }
          void tid;
        }
        orchestratorAbortByThreadRef.current.clear();
      }
    }

    // 2b. Stop fix (2026-07-31): ALSO cancel the live SERVER task for the
    //     stopped thread(s). The local abort above only stops the client-side
    //     background poll — without a server cancel the task keeps running on
    //     the backend and its durable completion write still lands minutes
    //     later. Fire-and-forget by design: failure is swallowed (log-only)
    //     because the local abort already protects the UI and the server
    //     sweeper remains the backstop. Registrations are stamped pre-flight
    //     (onTaskQueued) and cleared at each turn's terminal, so a Stop after
    //     completion finds nothing to cancel.
    const fireServerTaskCancel = (taskId: string): void => {
      try {
        void cancelPrest0nTask(taskId)
          .then((resp) => {
            console.log('[GlobalTools] server task cancel acknowledged:', taskId, resp?.status);
          })
          .catch((err) => {
            console.warn('[GlobalTools] server task cancel failed (non-fatal):', taskId, err);
          });
      } catch (err) {
        console.warn('[GlobalTools] server task cancel dispatch failed (non-fatal):', taskId, err);
      }
    };
    if (effectiveThreadId) {
      const liveTaskId = liveServerTaskByThreadRef.current.get(effectiveThreadId);
      if (liveTaskId) {
        liveServerTaskByThreadRef.current.delete(effectiveThreadId);
        fireServerTaskCancel(liveTaskId);
      }
    } else {
      // Global cancel — cancel every thread's live task (incl. the null key
      // used by thread-less/legacy surfaces).
      for (const liveTaskId of liveServerTaskByThreadRef.current.values()) {
        fireServerTaskCancel(liveTaskId);
      }
      liveServerTaskByThreadRef.current.clear();
    }

    // 3. Cancel running workflow runtimes (cleanup only, message already
    //    finalized). Round O: thread-scoped via the Round J runtime→thread
    //    binding (`runtimeThreadIdByToolRef`, stamped by getOrCreateRuntime) —
    //    a thread-scoped cancel only cancels runtimes BOUND to that thread;
    //    a global cancel (no threadId) cancels every running runtime.
    for (const [toolId, runtime] of toolRuntimesRef.current.entries()) {
      if (!runtime.isRunning()) continue;
      if (!shouldCancelRuntimeForThread(
        runtimeThreadIdByToolRef.current.get(toolId),
        effectiveThreadId,
      )) {
        continue;
      }
      runtime.cancel();
    }
  }, []);

  /**
   * Check if any tool is currently awaiting user input.
   * When a tool is awaiting input, the send button should remain enabled
   * so the user can respond to the tool's questions.
   */
  const isAnyToolAwaitingInput = useCallback((): boolean => {
    for (const runtime of toolRuntimesRef.current.values()) {
      if (runtime.isAwaitingInput()) {
        return true;
      }
    }
    return false;
  }, []);

  // ========================================================================
  // Return Value
  // ========================================================================

  return {
    // Authorization
    isAuthorized,
    userEmail: user?.email,

    // Shared Context
    lastResearchReport,
    setLastResearchReport,
    lastResearchReportRef,
    lastTranslatableContent,
    setLastTranslatableContent,
    lastTranslatableContentRef,
    drSession,
    setDrSession,
    drSessionRef,
    resetDrSession,
    videoScriptSession,
    setVideoScriptSession,
    videoScriptSessionRef,
    resetVideoScriptSession,

    // Runtime Management
    createMessageHandler,
    handleButtonClick,
    getRunningTool,
    isToolRunning,
    cancelRunningTool,
    isAnyToolAwaitingInput,
    bridgeLegacyContent,
    hydrateFromHistory,
    setActiveStream,

    // Refs
    chatHelpersRef,
    pageContextRef,
    setPageContext,

    // Transaction Recording
    recordTransaction,

    // Context Message Appending
    setAppendContextMessage,

    // Processing-state heartbeat (for chain-handoff spinner gap)
    setProcessingHeartbeat,
    setChatInstanceId,
    setThreadRoutineLookup,
    setBaseOutwardMessaging,
    setGuestServerResearch,

    // Prest0n Session
    setPrest0nSession,
    setRequestSessionRewire,

    // Shared Context
    sharedContext,
  };
}

// ============================================================================
// Export Type
// ============================================================================

export type GlobalToolsHook = ReturnType<typeof useGlobalTools>;
